import L from "leaflet";
import {
  buildHostedQimeraFileUrl,
  createEnvironmentStatus,
  createProjectId,
  defaultProjectExportFolder,
  downloadText,
  esc,
  formatBytes,
  formatDistanceNm,
  formatSoundSpeed,
  formatTimestamp,
  inferProjectNameFromFolder,
  isLikelyAbsoluteWindowsPath,
  joinPathParts,
  lineDistanceNm,
  normalizeSourceScanInterval,
  quoteArg,
  resolveProjectExportFolder,
  sanitizeExportStem,
  stateGlyph,
  statusBadge,
  statusToneStyle,
} from "./utils.js";
import {
  fetchJsonWithTimeout,
  normalizeMachineStatus,
  postJson,
} from "./api.js";
import {
  getActiveGridName,
  getActiveProject,
  getActiveProjectFolder,
  getSelectedSourceExportNames,
  normalizeGridItem,
  normalizeProjectRecord,
  normalizeSourceItem,
} from "./state-model.js";

const STATE_KEY = "hydrotools:qimera-command:v1";
const SOURCES_BOTTOM_PANEL_ID = "qimera-command-sources";

const WORKFLOW_PRESETS = {
  create_import_process_grid: {
    label: "New Project + Import + Process + Grid",
    importMode: "folder",
    importFormat: "kmall",
    includeCreateProject: true,
    includeImport: true,
    includeAutoProcess: true,
    includeCreateDynamicGrid: true,
    includeExportGeotiff: false,
    includeExportFloatingPointGeotiff: false,
    includeExportGridAscii: false,
    includeExportBag: false,
    includeGenerateThuTvuLayers: false,
    includeExportGsf: false,
    includeExportNoaaCsv: false,
    includeInventory: false,
  },
  append_export: {
    label: "Append + Export Deliverables",
    importMode: "folder",
    importFormat: "kmall",
    includeCreateProject: false,
    includeImport: true,
    includeAutoProcess: true,
    includeCreateDynamicGrid: false,
    includeAppendToGrid: true,
    includeExportGeotiff: true,
    includeExportBag: true,
    includeExportNoaaCsv: false,
    includeInventory: false,
  },
  inventory: {
    label: "Project Inventory / Diagnostics",
    importMode: "folder",
    importFormat: "kmall",
    includeCreateProject: false,
    includeImport: false,
    includeAutoProcess: false,
    includeCreateDynamicGrid: false,
    includeAppendToGrid: false,
    includeExportGeotiff: false,
    includeExportBag: false,
    includeExportNoaaCsv: false,
    includeInventory: true,
  },
};


function sourceStateLabel(item) {
  if (item.svpNeedsReprocess === true && item.importStatus === "imported") return "Needs Reprocess (SVP)";
  if (item.processStatus === "processed") return "Processed";
  if (item.processStatus === "processing") return "Processing";
  if (item.filterStatus === "filtering") return "Filtering";
  if (item.importStatus === "imported") return "Imported";
  if (item.importStatus === "importing") return "Importing";
  return item.readyState === "ready" ? "Ready" : "Building";
}

function sourceStateTone(item) {
  if (item.svpNeedsReprocess === true && item.importStatus === "imported") return "color:#fbbf24;";
  if (item.processStatus === "processed") return "color:#93c5fd;";
  if (item.processStatus === "processing") return "color:#60a5fa;";
  if (item.filterStatus === "filtering") return "color:#f9a8d4;";
  if (item.importStatus === "imported") return "color:#fde68a;";
  if (item.importStatus === "importing") return "color:#facc15;";
  return item.readyState === "ready" ? "color:#cbd5e1;" : "color:#fbbf24;";
}

const SVP_PROFILE_PALETTE = ["#38bdf8", "#34d399", "#f472b6", "#facc15", "#fb7185", "#a78bfa"];

function getSvpProfileColor(profileName) {
  const key = String(profileName || "").trim();
  if (!key) return SVP_PROFILE_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i);
    hash |= 0;
  }
  const index = Math.abs(hash) % SVP_PROFILE_PALETTE.length;
  return SVP_PROFILE_PALETTE[index];
}

function parseSvpCastTimeMs(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    const dateMs = value.getTime();
    return Number.isFinite(dateMs) ? dateMs : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    if (Math.abs(value) > 1e12) return value;
    return value * 1000;
  }
  const text = String(value || "").trim();
  if (!text) return null;
  const numeric = Number(text);
  if (Number.isFinite(numeric)) {
    if (Math.abs(numeric) > 1e12) return numeric;
    return numeric * 1000;
  }
  const parsedMs = Date.parse(text);
  return Number.isFinite(parsedMs) ? parsedMs : null;
}

function deriveSvpCastRows(profiles, schedule) {
  const profileRows = Array.isArray(profiles) ? profiles : [];
  const scheduleRows = Array.isArray(schedule) ? schedule : [];
  const rows = [];
  const pushFrom = (entry, fallbackProfileName = "") => {
    if (!entry || typeof entry !== "object") return;
    const profileName = String(
      entry.profileName
      || entry.profile
      || entry.svpProfile
      || entry.svp_profile
      || entry.profileFile
      || entry.profile_file
      || fallbackProfileName
      || "",
    ).trim();
    const timeMs = parseSvpCastTimeMs(
      entry.time
      ?? entry.timeUtc
      ?? entry.time_utc
      ?? entry.timestamp
      ?? entry.dateTime
      ?? entry.datetime
      ?? entry.castTime
      ?? entry.cast_time
      ?? entry.startTime
      ?? entry.start_time,
    );
    const lat = Number(
      entry.latitude
      ?? entry.lat
      ?? entry.y
      ?? entry.location?.latitude
      ?? entry.location?.lat
      ?? entry.position?.latitude
      ?? entry.position?.lat,
    );
    const lon = Number(
      entry.longitude
      ?? entry.lon
      ?? entry.lng
      ?? entry.long
      ?? entry.x
      ?? entry.location?.longitude
      ?? entry.location?.lon
      ?? entry.location?.lng
      ?? entry.position?.longitude
      ?? entry.position?.lon
      ?? entry.position?.lng,
    );
    const sourceName = String(entry.sourceName || entry.source || entry.lineName || entry.line || "").trim();
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lon);
    const hasTime = Number.isFinite(timeMs);
    if (!(profileName || hasCoords || hasTime)) return;
    rows.push({
      profileName,
      sourceName,
      timeMs: hasTime ? timeMs : null,
      lat: hasCoords ? lat : null,
      lon: hasCoords ? lon : null,
    });
  };
  scheduleRows.forEach((entry) => pushFrom(entry));
  profileRows.forEach((profile) => pushFrom(profile, String(profile?.name || "").trim()));
  const deduped = [];
  const seen = new Set();
  rows.forEach((row) => {
    const key = `${row.profileName}|${Number.isFinite(row.timeMs) ? row.timeMs : ""}|${Number.isFinite(row.lat) ? row.lat.toFixed(6) : ""}|${Number.isFinite(row.lon) ? row.lon.toFixed(6) : ""}|${row.sourceName}`;
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(row);
  });
  deduped.sort((a, b) => {
    const aHasTime = Number.isFinite(a.timeMs);
    const bHasTime = Number.isFinite(b.timeMs);
    if (aHasTime && bHasTime && a.timeMs !== b.timeMs) return a.timeMs - b.timeMs;
    if (aHasTime !== bHasTime) return aHasTime ? -1 : 1;
    return String(a.profileName || "").localeCompare(String(b.profileName || ""), undefined, { sensitivity: "base" });
  });
  return deduped;
}

function buildSvpCastMapSvg(castRows, selectedProfileNames = []) {
  const points = (Array.isArray(castRows) ? castRows : [])
    .filter((row) => Number.isFinite(row?.lat) && Number.isFinite(row?.lon));
  if (!points.length) return "";
  const width = 420;
  const height = 220;
  const padLeft = 44;
  const padRight = 16;
  const padTop = 16;
  const padBottom = 28;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const lons = points.map((row) => Number(row.lon));
  const lats = points.map((row) => Number(row.lat));
  let minLon = Math.min(...lons);
  let maxLon = Math.max(...lons);
  let minLat = Math.min(...lats);
  let maxLat = Math.max(...lats);
  if (maxLon - minLon < 1e-9) {
    minLon -= 0.005;
    maxLon += 0.005;
  }
  if (maxLat - minLat < 1e-9) {
    minLat -= 0.005;
    maxLat += 0.005;
  }
  const lonSpan = Math.max(1e-9, maxLon - minLon);
  const latSpan = Math.max(1e-9, maxLat - minLat);
  const xFor = (lon) => padLeft + ((Number(lon) - minLon) / lonSpan) * innerWidth;
  const yFor = (lat) => padTop + ((maxLat - Number(lat)) / latSpan) * innerHeight;
  const selected = new Set((Array.isArray(selectedProfileNames) ? selectedProfileNames : []).map((name) => String(name || "").trim()));
  const orderedPath = points.filter((row) => Number.isFinite(row.timeMs));
  return `
    <svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" aria-label="SVP cast location plot">
      <rect x="0" y="0" width="${width}" height="${height}" rx="12" ry="12" fill="rgba(15,23,42,.2)" stroke="rgba(148,163,184,.2)" />
      <line x1="${padLeft}" y1="${height - padBottom}" x2="${width - padRight}" y2="${height - padBottom}" stroke="rgba(148,163,184,.24)" />
      <line x1="${padLeft}" y1="${padTop}" x2="${padLeft}" y2="${height - padBottom}" stroke="rgba(148,163,184,.24)" />
      <text x="${padLeft}" y="${height - 8}" fill="#94a3b8" font-size="10">${esc(minLon.toFixed(5))}</text>
      <text x="${width - padRight}" y="${height - 8}" text-anchor="end" fill="#94a3b8" font-size="10">${esc(maxLon.toFixed(5))}</text>
      <text x="${padLeft - 6}" y="${height - padBottom}" text-anchor="end" fill="#94a3b8" font-size="10">${esc(minLat.toFixed(5))}</text>
      <text x="${padLeft - 6}" y="${padTop + 10}" text-anchor="end" fill="#94a3b8" font-size="10">${esc(maxLat.toFixed(5))}</text>
      ${orderedPath.length > 1 ? `<polyline points="${orderedPath.map((row) => `${xFor(row.lon).toFixed(2)},${yFor(row.lat).toFixed(2)}`).join(" ")}" fill="none" stroke="rgba(148,163,184,.35)" stroke-width="1.2" stroke-dasharray="3 3" />` : ""}
      ${points.map((row) => {
    const isSelected = selected.has(String(row.profileName || "").trim());
    const color = isSelected ? "#22d3ee" : "#cbd5e1";
    const radius = isSelected ? 4 : 3;
    const timeLabel = Number.isFinite(row.timeMs) ? formatTimestamp(new Date(row.timeMs).toISOString()) : "No time";
    const label = `${row.profileName || "Cast"} | ${timeLabel} | ${Number(row.lat).toFixed(5)}, ${Number(row.lon).toFixed(5)}`;
    return `
        <circle cx="${xFor(row.lon).toFixed(2)}" cy="${yFor(row.lat).toFixed(2)}" r="${radius}" fill="${color}" stroke="rgba(15,23,42,.9)" stroke-width="1">
          <title>${esc(label)}</title>
        </circle>
      `;
  }).join("")}
      <text x="${width / 2}" y="${height - 8}" text-anchor="middle" fill="#94a3b8" font-size="11">Longitude</text>
      <text x="14" y="${height / 2}" text-anchor="middle" fill="#94a3b8" font-size="11" transform="rotate(-90 14 ${height / 2})">Latitude</text>
    </svg>
  `;
}

function normalizeSourceNameKey(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "";
  const base = text.split(/[\\/]/).pop() || text;
  return base.trim();
}

function buildSvpAssignmentBySource(assignments) {
  const map = new Map();
  (Array.isArray(assignments) ? assignments : []).forEach((row) => {
    const sourceKey = normalizeSourceNameKey(row?.sourceName || row?.source || row?.lineName || row?.line);
    if (!sourceKey) return;
    const profileName = String(row?.profileName || row?.profile || "").trim();
    map.set(sourceKey, profileName);
  });
  return map;
}

function buildSvpProfileSvg(profiles) {
  const selectedProfiles = (Array.isArray(profiles) ? profiles : [profiles])
    .filter((profile) => profile && Array.isArray(profile.points) && profile.points.length);
  if (!selectedProfiles.length) return "";
  const width = 420;
  const height = 240;
  const padLeft = 52;
  const padRight = 18;
  const padTop = 18;
  const padBottom = 30;
  const innerWidth = width - padLeft - padRight;
  const innerHeight = height - padTop - padBottom;
  const allPoints = selectedProfiles.flatMap((profile) => profile.points || []);
  const speeds = allPoints.map((point) => Number(point.soundSpeed)).filter(Number.isFinite);
  const depths = allPoints.map((point) => Number(point.depth)).filter(Number.isFinite);
  if (!speeds.length || !depths.length) return "";
  const minSpeed = Math.min(...speeds);
  const maxSpeed = Math.max(...speeds);
  const minDepth = Math.min(...depths);
  const maxDepth = Math.max(...depths);
  const speedSpan = Math.max(1e-9, maxSpeed - minSpeed);
  const depthSpan = Math.max(1e-9, maxDepth - minDepth);
  const xFor = (value) => padLeft + ((Number(value) - minSpeed) / speedSpan) * innerWidth;
  const yFor = (value) => padTop + ((Number(value) - minDepth) / depthSpan) * innerHeight;
  const speedTicks = [0, 0.5, 1].map((t) => minSpeed + (speedSpan * t));
  const depthTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => minDepth + (depthSpan * t));
  return `
    <svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" aria-label="SVP profile chart">
      <rect x="0" y="0" width="${width}" height="${height}" rx="12" ry="12" fill="rgba(15,23,42,.2)" stroke="rgba(148,163,184,.2)" />
      ${speedTicks.map((tick) => `
        <g>
          <line x1="${xFor(tick).toFixed(2)}" y1="${padTop}" x2="${xFor(tick).toFixed(2)}" y2="${(height - padBottom).toFixed(2)}" stroke="rgba(148,163,184,.14)" />
          <text x="${xFor(tick).toFixed(2)}" y="${height - 8}" text-anchor="middle" fill="#cbd5e1" font-size="11">${esc(formatSoundSpeed(tick))}</text>
        </g>
      `).join("")}
      ${depthTicks.map((tick) => `
        <g>
          <line x1="${padLeft}" y1="${yFor(tick).toFixed(2)}" x2="${(width - padRight).toFixed(2)}" y2="${yFor(tick).toFixed(2)}" stroke="rgba(148,163,184,.12)" />
          <text x="${padLeft - 8}" y="${(yFor(tick) + 4).toFixed(2)}" text-anchor="end" fill="#cbd5e1" font-size="11">${esc(Number(tick).toFixed(0))}</text>
        </g>
      `).join("")}
      ${selectedProfiles.map((profile, index) => {
        const polyline = (profile.points || [])
          .map((point) => `${xFor(point.soundSpeed).toFixed(2)},${yFor(point.depth).toFixed(2)}`)
          .join(" ");
        const color = getSvpProfileColor(profile?.name || `profile-${index}`);
        return `<polyline points="${polyline}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />`;
      }).join("")}
      <text x="${width / 2}" y="${height - 8}" text-anchor="middle" fill="#94a3b8" font-size="11">Sound speed</text>
      <text x="16" y="${height / 2}" text-anchor="middle" fill="#94a3b8" font-size="11" transform="rotate(-90 16 ${height / 2})">Depth</text>
    </svg>
  `;
}

const FILTER_PROFILE_OPTIONS = [
  "Very Weak Spline",
  "Weak Spline",
  "Medium Spline",
  "Strong Spline",
  "Very Strong Spline",
  "Reject All Soundings",
  "Accept All Soundings",
  "Reject Additional Soundings",
  "Accept Additional Soundings",
  "Reject User Soundings",
  "Accept User Soundings",
  "Revert All Manual Edits",
  "Revert All Filtering",
];

const SVP_ASSIGNMENT_STRATEGY_OPTIONS = [
  { value: "nearest-time", label: "Nearest in Time" },
  { value: "nearest-distance", label: "Nearest in Distance" },
  { value: "time-then-distance", label: "Time then Distance" },
  { value: "distance-then-time", label: "Distance then Time" },
  { value: "weighted-hybrid", label: "Weighted Hybrid" },
];

const GRID_PROFILE_OPTIONS = [
  "Default",
  "Deep",
  "Shallow",
  "Custom",
  "NOAA_0.5m",
  "NOAA_1m",
  "NOAA_2m",
  "NOAA_4m",
  "NOAA_8m",
  "NOAA_16m",
  "NOAA_32m",
];

const DEPTH_LAYER_OPTIONS = [
  "Average",
  "Shallow",
  "Deep",
  "CUBE",
];

const BAG_OUTPUT_OPTIONS = [
  "stddev",
  "attached-scalar",
  "cube-uncertainty",
  "compute-tvu",
  "compute-slope-corrected-uncertainty",
  "loaded-scalar",
];


function defaultState() {
  return {
    workflowPreset: "create_import_process_grid",
    qimeraExecutable: "C:\\Program Files\\QPS\\Qimera\\2.8.2\\bin\\qimera-command.exe",
    sentinelAdminUrl: "http://localhost:1947/",
    qimeraStatusUrl: "/api/qimera/status",
    projectFolder: "",
    projects: [],
    selectedProjectId: "",
    crsMode: "epsg",
    epsgCode: "26910",
    wktFile: "",
    surveyType: "",
    description: "",
    importFormat: "kmall",
    sourceFlowMode: "batch",
    svpAssignmentStrategy: "nearest-time",
    svpMaxTimeHours: "",
    svpMaxDistanceNm: "",
    importMode: "folder",
    inputPath: "",
    sbetFile: "",
    sbetRmsFile: "",
    sbetGpsWeek: "",
    sbetCoordinateSystem: "",
    sbetPriority: true,
    additionalFiles: "",
    vesselName: "",
    delayConvertToQpd: false,
    includeCreateProject: true,
    includeImport: true,
    includeAutoProcess: true,
    includeCreateDynamicGrid: true,
    includeAppendToGrid: false,
    includeExportGeotiff: false,
    includeExportBag: false,
    includeExportNoaaCsv: false,
    includeInventory: false,
    gridName: "Dynamic Grid",
    cellSize: "0.5",
    gridMode: "cube",
    gridProfile: "Default",
    excludeGridCoverage: false,
    selectedGridName: "",
    autoAppendProcessedToGrid: false,
    autoExportGeotiffOnGridAppend: true,
    exportFolder: "",
    geotiffFilename: "grid.tif",
    geotiffWorldFile: false,
    geotiffOrigin: "lower-left",
    floatingGeotiffFilename: "grid-float.tif",
    floatingGeotiffNoDataValue: "NaN",
    asciiBounds: "",
    asciiZPrecision: "",
    asciiColumnSeparator: ",",
    bagDate: "",
    bagUseXmlDate: false,
    bagCompression: "0",
    bagScalarPath: "",
    bagXmlMetadata: "",
    bagVersion: "15",
    bagOutputMode: "compute-tvu",
    geotiffDepthLayer: "Average",
    autoLoadExportedTiffs: true,
    gsfFilenamePattern: "%v\\%f",
    gsfReplaceWhitespace: false,
    gsfExportSystemsIndividually: false,
    gsfSpecificSystem: "",
    gsfExcludeUserAdditionalSoundings: false,
    gsfExcludeRawBackscatterImagery: false,
    gsfDoNotFlagFilteredAsManualEdits: false,
    gsfFlagAllInterpolatedAsAccepted: false,
    noaaCsvFilename: "noaa-export.csv",
    lastAutoExportBase: "grid",
    printProgress: true,
    monitorSourceFolder: false,
    autoImportNewSources: false,
    autoProcessNewSources: false,
    autoFilterSources: false,
    selectedFilterProfile: "",
    sourceScanIntervalSec: 30,
    sourceWorkspaceDock: "bottom",
    sectionOpen: {
      environment: true,
      project: true,
      workflow: true,
      import: true,
      sbet: true,
      svp: true,
      svpProfileView: true,
      svpProfileList: true,
      svpLineAssignments: true,
      metrics: true,
      sourceAutomation: false,
      sourceFilters: false,
      sourceWorkspace: true,
      gridAutomation: false,
      filters: true,
      grid: true,
      export: true,
      exportGrid: true,
      exportSource: true,
      output: true,
    },
  };
}

function readState() {
  const fallback = defaultState();
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const merged = { ...fallback, ...(parsed && typeof parsed === "object" ? parsed : {}) };
    if (!String(merged.qimeraStatusUrl || "").trim()) {
      merged.qimeraStatusUrl = fallback.qimeraStatusUrl;
    }
    if (!String(merged.sentinelAdminUrl || "").trim()) {
      merged.sentinelAdminUrl = fallback.sentinelAdminUrl;
    }
    const projects = Array.isArray(merged.projects)
      ? merged.projects.map(normalizeProjectRecord).filter(Boolean)
      : [];
    const hadProjectsArray = Array.isArray(parsed?.projects);
    if (!projects.length && !hadProjectsArray && String(merged.projectFolder || "").trim()) {
      const migrated = normalizeProjectRecord({
        id: createProjectId(),
        name: inferProjectNameFromFolder(merged.projectFolder),
        host: "localhost",
        folder: merged.projectFolder,
        mode: "existing",
      });
      if (migrated) projects.push(migrated);
    }
    const selectedProjectId = projects.some((project) => project.id === merged.selectedProjectId)
      ? merged.selectedProjectId
      : (projects[0]?.id || "");
    const mergedState = { ...merged, projects, selectedProjectId, projectFolder: getActiveProjectFolder({ ...merged, projects, selectedProjectId }) };
    mergedState.sectionOpen = {
      ...fallback.sectionOpen,
      ...(mergedState.sectionOpen && typeof mergedState.sectionOpen === "object" ? mergedState.sectionOpen : {}),
    };
    return mergedState;
  } catch {
    return fallback;
  }
}

function writeState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

function applyPreset(state, presetId) {
  const preset = WORKFLOW_PRESETS[presetId];
  if (!preset) return state;
  return {
    ...state,
    workflowPreset: presetId,
    importMode: preset.importMode,
    importFormat: preset.importFormat,
    includeCreateProject: !!preset.includeCreateProject,
    includeImport: !!preset.includeImport,
    includeAutoProcess: !!preset.includeAutoProcess,
    includeCreateDynamicGrid: !!preset.includeCreateDynamicGrid,
    includeAppendToGrid: !!preset.includeAppendToGrid,
    includeExportGeotiff: !!preset.includeExportGeotiff,
    includeExportFloatingPointGeotiff: false,
    includeExportGridAscii: false,
    includeExportBag: !!preset.includeExportBag,
    includeGenerateThuTvuLayers: false,
    includeExportGsf: false,
    includeExportNoaaCsv: !!preset.includeExportNoaaCsv,
    includeInventory: !!preset.includeInventory,
  };
}

function getImportJobName(format) {
  const normalized = String(format || "").trim().toLowerCase();
  if (normalized === "all") return "add-raw-sonar-files-all";
  if (normalized === "db") return "add-raw-sonar-files-db";
  if (normalized === "s7k") return "add-raw-sonar-files-s7k";
  return "add-raw-sonar-files-kmall";
}

function buildSingleCommands(state) {
  const exe = quoteArg(state.qimeraExecutable);
  const projectFolder = quoteArg(getActiveProjectFolder(state));
  const gridName = getActiveGridName(state) || "Dynamic Grid";
  const selectedSourceNames = getSelectedSourceExportNames(state);
  const commands = [];

  if (state.includeCreateProject) {
    const cmd = [
      exe, "--job", "create-project",
      "--project-folder", projectFolder,
    ];
    if (String(state.crsMode || "").trim() === "wkt-file") {
      cmd.push("--wkt-file", quoteArg(state.wktFile));
    } else {
      cmd.push("--epsg-code", String(state.epsgCode || "").trim());
    }
    if (String(state.surveyType || "").trim()) cmd.push("--survey-type", quoteArg(state.surveyType));
    if (String(state.description || "").trim()) cmd.push("--description", quoteArg(state.description));
    commands.push(cmd.join(" "));
  }

  if (state.includeImport) {
    const cmd = [exe, "--job", getImportJobName(state.importFormat), "--project-folder", projectFolder];
    if (String(state.epsgCode || "").trim()) cmd.push("--epsg-code", String(state.epsgCode).trim());
    if (state.delayConvertToQpd) cmd.push("--delay-convert-to-qpd");
    if (state.printProgress) cmd.push("--print-progress");
    if (state.importMode === "folder") {
      cmd.push("--folder-path", quoteArg(state.inputPath));
    } else {
      String(state.additionalFiles || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean).forEach((file) => cmd.push(quoteArg(file)));
    }
    if (String(state.vesselName || "").trim()) cmd.push("--vessel-name", quoteArg(state.vesselName));
    commands.push(cmd.join(" "));
  }

  if (state.includeAutoProcess) {
    const cmd = [exe, "--job", "auto-process", "--project-folder", projectFolder];
    if (state.printProgress) cmd.push("--print-progress");
    commands.push(cmd.join(" "));
  }

  if (state.includeCreateDynamicGrid) {
    const cmd = [exe, "--job", "create-dynamic-grid", "--project-folder", projectFolder, "--name", quoteArg(state.gridName || "Dynamic Grid")];
    if (String(state.cellSize || "").trim()) cmd.push("--cell-size", String(state.cellSize).trim());
    if (String(state.gridMode || "cube").trim() === "cube" && String(state.gridProfile || "").trim()) cmd.push("--cube-config", quoteArg(state.gridProfile));
    if (state.excludeGridCoverage) cmd.push("--exclude-coverage");
    if (state.printProgress) cmd.push("--print-progress");
    commands.push(cmd.join(" "));
  }

  if (state.includeAppendToGrid) {
    const cmd = [exe, "--job", "append-to-grid", "--project-folder", projectFolder];
    commands.push(cmd.join(" "));
  }

  if (state.includeExportGeotiff) {
    const cmd = [
      exe, "--job", "export-geotiff", "--project-folder", projectFolder,
      "--grid-name", quoteArg(gridName),
      "--depth-layer", quoteArg(state.geotiffDepthLayer || "Average"),
    ];
    const filename = String(state.geotiffFilename || "").trim() || "grid.tif";
    if (String(state.exportFolder || "").trim()) {
      cmd.push("--export-filepath", quoteArg(joinPathParts(state.exportFolder, filename)));
    } else {
      cmd.push("--export-filename", quoteArg(filename));
    }
    if (state.geotiffWorldFile) cmd.push("--export-world-file");
    if (String(state.geotiffOrigin || "").trim()) cmd.push("--origin", quoteArg(state.geotiffOrigin));
    commands.push(cmd.join(" "));
  }

  if (state.includeExportFloatingPointGeotiff) {
    const cmd = [
      exe, "--job", "export-grid-floating-point-geotiff", "--project-folder", projectFolder,
      "--grid-name", quoteArg(gridName),
      "--depth-layer", quoteArg(state.geotiffDepthLayer || "Average"),
    ];
    const filename = String(state.floatingGeotiffFilename || "").trim() || "grid-float.tif";
    if (String(state.exportFolder || "").trim()) {
      cmd.push("--export-filepath", quoteArg(joinPathParts(state.exportFolder, filename)));
    } else {
      cmd.push("--export-filename", quoteArg(filename));
    }
    cmd.push("--no-data-value", quoteArg(String(state.floatingGeotiffNoDataValue || "").trim() || "NaN"));
    commands.push(cmd.join(" "));
  }

  if (state.includeExportGridAscii) {
    const cmd = [
      exe, "--job", "export-grid-ascii", "--project-folder", projectFolder,
      "--depth-layer", quoteArg(state.geotiffDepthLayer || "Average"),
    ];
    if (String(state.exportFolder || "").trim()) cmd.push("--export-folder", quoteArg(state.exportFolder));
    String(state.asciiBounds || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean).forEach((bound) => {
      cmd.push("--bounds", quoteArg(bound));
    });
    if (String(state.asciiZPrecision || "").trim()) cmd.push("--z-precision", quoteArg(state.asciiZPrecision));
    if (String(state.asciiColumnSeparator || "").trim()) cmd.push("--column-separator", quoteArg(state.asciiColumnSeparator));
    cmd.push(quoteArg(gridName));
    commands.push(cmd.join(" "));
  }

  if (state.includeExportBag) {
    const cmd = [
      exe, "--job", "export-bag", "--project-folder", projectFolder,
      "--grid-name", quoteArg(gridName),
      "--version", String(state.bagVersion || "15").trim(),
      "--output", quoteArg(state.bagOutputMode || "compute-tvu"),
      "--depth-layer", quoteArg(state.geotiffDepthLayer || "Average"),
    ];
    if (String(state.bagScalarPath || "").trim()) cmd.push("--scalar-path", quoteArg(state.bagScalarPath));
    if (String(state.bagDate || "").trim()) cmd.push("--date", quoteArg(state.bagDate));
    if (state.bagUseXmlDate) cmd.push("--xml-date");
    if (String(state.bagCompression || "").trim()) cmd.push("--compression", String(state.bagCompression).trim());
    if (String(state.bagXmlMetadata || "").trim()) cmd.push("--bag-xml-metadata", quoteArg(state.bagXmlMetadata));
    commands.push(cmd.join(" "));
  }

  if (state.includeGenerateThuTvuLayers) {
    const cmd = [
      exe, "--job", "generate-thu-tvu-layers", "--project-folder", projectFolder,
      "--grid-name", quoteArg(gridName),
    ];
    commands.push(cmd.join(" "));
  }

  if (state.includeExportGsf) {
    const cmd = [exe, "--job", "export-gsf", "--project-folder", projectFolder];
    if (String(state.exportFolder || "").trim()) cmd.push("--export-folder", quoteArg(state.exportFolder));
    if (String(state.gsfFilenamePattern || "").trim()) cmd.push("--filename-pattern", quoteArg(state.gsfFilenamePattern));
    if (state.gsfReplaceWhitespace) cmd.push("--replace-whitespace");
    if (state.gsfExportSystemsIndividually) cmd.push("--export-systems-individually");
    if (String(state.gsfSpecificSystem || "").trim()) cmd.push("--specific-system", quoteArg(state.gsfSpecificSystem));
    if (state.gsfExcludeUserAdditionalSoundings) cmd.push("--exclude-user-additional-soundings");
    if (state.gsfExcludeRawBackscatterImagery) cmd.push("--exclude-raw-backscatter-imagery");
    if (state.gsfDoNotFlagFilteredAsManualEdits) cmd.push("--do-not-flag-filtered-as-manual-edits");
    if (state.gsfFlagAllInterpolatedAsAccepted) cmd.push("--flag-all-interpolated-soundings-as-accepted");
    selectedSourceNames.forEach((name) => cmd.push(quoteArg(name)));
    commands.push(cmd.join(" "));
  }

  if (state.includeExportNoaaCsv) {
    const cmd = [exe, "--job", "export-noaa-csv", "--project-folder", projectFolder];
    if (String(state.noaaCsvFilename || "").trim()) cmd.push("--output-filename", quoteArg(state.noaaCsvFilename));
    commands.push(cmd.join(" "));
  }

  if (state.includeInventory) {
    ["list-source-files", "list-grids", "list-vessels", "list-systems"].forEach((job) => {
      commands.push([exe, "--job", job, "--project-folder", projectFolder].join(" "));
    });
  }

  return commands;
}

function buildConfigObject(state) {
  const activeProjectFolder = getActiveProjectFolder(state);
  const gridName = getActiveGridName(state) || "Dynamic Grid";
  const selectedSourceNames = getSelectedSourceExportNames(state);
  const jobs = [];
  
  if (state.includeCreateProject) {
    const job = { job: "create-project" };
    if (String(state.crsMode || "").trim() === "wkt-file") {
      job["wkt-file"] = String(state.wktFile || "").trim();
    } else {
      job["epsg-code"] = Number(state.epsgCode || 0);
    }
    if (String(state.surveyType || "").trim()) job["survey-type"] = String(state.surveyType).trim();
    if (String(state.description || "").trim()) job.description = String(state.description).trim();
    jobs.push(job);
  }

  if (state.includeImport) {
    const job = { job: getImportJobName(state.importFormat) };
    if (String(state.epsgCode || "").trim()) job["epsg-code"] = Number(state.epsgCode);
    if (state.delayConvertToQpd) job["delay-convert-to-qpd"] = true;
    if (state.printProgress) job["print-progress"] = true;
    if (state.importMode === "folder") {
      job["folder-path"] = String(state.inputPath || "").trim();
    } else {
      const files = String(state.additionalFiles || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
      if (files.length) job.files = files;
    }
    if (String(state.vesselName || "").trim()) job["vessel-name"] = String(state.vesselName).trim();
    jobs.push(job);
  }

  if (state.includeAutoProcess) jobs.push(state.printProgress ? { job: "auto-process", "print-progress": true } : "auto-process");

  if (state.includeCreateDynamicGrid) {
    const job = { job: "create-dynamic-grid", name: String(state.gridName || "Dynamic Grid").trim() };
    if (String(state.cellSize || "").trim()) job["cell-size"] = Number(state.cellSize);
    if (String(state.gridMode || "cube").trim() === "cube" && String(state.gridProfile || "").trim()) job["cube-config"] = String(state.gridProfile).trim();
    if (state.excludeGridCoverage) job["exclude-coverage"] = true;
    if (state.printProgress) job["print-progress"] = true;
    jobs.push(job);
  }

  if (state.includeAppendToGrid) jobs.push("append-to-grid");

  if (state.includeExportGeotiff) {
    const job = {
      job: "export-geotiff",
      "grid-name": String(gridName).trim(),
      "depth-layer": String(state.geotiffDepthLayer || "Average").trim(),
    };
    const filename = String(state.geotiffFilename || "").trim() || "grid.tif";
    if (String(state.exportFolder || "").trim()) {
      job["export-filepath"] = joinPathParts(String(state.exportFolder || "").trim(), filename);
    } else {
      job["export-filename"] = filename;
    }
    if (state.geotiffWorldFile) job["export-world-file"] = true;
    if (String(state.geotiffOrigin || "").trim()) job.origin = String(state.geotiffOrigin).trim();
    jobs.push(job);
  }

  if (state.includeExportFloatingPointGeotiff) {
    const job = {
      job: "export-grid-floating-point-geotiff",
      "grid-name": String(gridName).trim(),
      "depth-layer": String(state.geotiffDepthLayer || "Average").trim(),
    };
    const filename = String(state.floatingGeotiffFilename || "").trim() || "grid-float.tif";
    if (String(state.exportFolder || "").trim()) {
      job["export-filepath"] = joinPathParts(String(state.exportFolder || "").trim(), filename);
    } else {
      job["export-filename"] = filename;
    }
    job["no-data-value"] = String(state.floatingGeotiffNoDataValue || "").trim() || "NaN";
    jobs.push(job);
  }

  if (state.includeExportGridAscii) {
    const job = {
      job: "export-grid-ascii",
      "grid-names": [String(gridName).trim()],
      "depth-layer": String(state.geotiffDepthLayer || "Average").trim(),
    };
    if (String(state.exportFolder || "").trim()) job["export-folder"] = String(state.exportFolder).trim();
    const bounds = String(state.asciiBounds || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (bounds.length) job.bounds = bounds;
    if (String(state.asciiZPrecision || "").trim()) job["z-precision"] = String(state.asciiZPrecision).trim();
    if (String(state.asciiColumnSeparator || "").trim()) job["column-separator"] = String(state.asciiColumnSeparator).trim();
    jobs.push(job);
  }

  if (state.includeExportBag) {
    const job = {
      job: "export-bag",
      "grid-name": String(gridName).trim(),
      version: Number(state.bagVersion || 15),
      output: String(state.bagOutputMode || "compute-tvu").trim(),
      "depth-layer": String(state.geotiffDepthLayer || "Average").trim(),
    };
    if (String(state.bagScalarPath || "").trim()) job["scalar-path"] = String(state.bagScalarPath).trim();
    if (String(state.bagDate || "").trim()) job.date = String(state.bagDate).trim();
    if (state.bagUseXmlDate) job["xml-date"] = true;
    if (String(state.bagCompression || "").trim()) job.compression = Number(state.bagCompression);
    if (String(state.bagXmlMetadata || "").trim()) job["bag-xml-metadata"] = String(state.bagXmlMetadata).trim();
    jobs.push(job);
  }

  if (state.includeGenerateThuTvuLayers) {
    jobs.push({
      job: "generate-thu-tvu-layers",
      "grid-name": String(gridName).trim(),
    });
  }

  if (state.includeExportGsf) {
    const job = {
      job: "export-gsf",
      "source-file-names": selectedSourceNames,
    };
    if (String(state.exportFolder || "").trim()) job["export-folder"] = String(state.exportFolder).trim();
    if (String(state.gsfFilenamePattern || "").trim()) job["filename-pattern"] = String(state.gsfFilenamePattern).trim();
    if (state.gsfReplaceWhitespace) job["replace-whitespace"] = true;
    if (state.gsfExportSystemsIndividually) job["export-systems-individually"] = true;
    if (String(state.gsfSpecificSystem || "").trim()) job["specific-system"] = String(state.gsfSpecificSystem).trim();
    if (state.gsfExcludeUserAdditionalSoundings) job["exclude-user-additional-soundings"] = true;
    if (state.gsfExcludeRawBackscatterImagery) job["exclude-raw-backscatter-imagery"] = true;
    if (state.gsfDoNotFlagFilteredAsManualEdits) job["do-not-flag-filtered-as-manual-edits"] = true;
    if (state.gsfFlagAllInterpolatedAsAccepted) job["flag-all-interpolated-soundings-as-accepted"] = true;
    jobs.push(job);
  }

  if (state.includeExportNoaaCsv) {
    const job = { job: "export-noaa-csv" };
    if (String(state.noaaCsvFilename || "").trim()) job["output-filename"] = String(state.noaaCsvFilename).trim();
    jobs.push(job);
  }

  if (state.includeInventory) jobs.push("list-source-files", "list-grids", "list-vessels", "list-systems");

  return {
    "project-folder": String(activeProjectFolder || "").trim(),
    jobs,
  };
}

function buildConfigJson(state) {
  return JSON.stringify(buildConfigObject(state), null, 2);
}

function buildLauncherCommand(state) {
  return `${quoteArg(state.qimeraExecutable)} --config=${quoteArg("qimera-command-config.json")}`;
}

function validateForm(state) {
  if (!String(state.qimeraExecutable || "").trim()) return "Qimera command executable path is required.";
  if (!String(getActiveProjectFolder(state) || "").trim()) return "Select or add a project before generating commands.";
  if (state.includeCreateProject && String(state.crsMode || "").trim() === "wkt-file" && !String(state.wktFile || "").trim()) {
    return "DB/WKT file path is required for create-project when deriving the project CRS.";
  }
  if (state.includeCreateProject && String(state.crsMode || "").trim() !== "wkt-file" && !String(state.epsgCode || "").trim()) {
    return "EPSG code is required for create-project.";
  }
  if (state.includeImport && state.importMode === "folder" && !String(state.inputPath || "").trim()) return "Import folder path is required.";
  if (state.includeImport && state.importMode === "files") {
    const files = String(state.additionalFiles || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (!files.length) return "Add at least one raw sonar file path.";
  }
  if (state.includeCreateDynamicGrid && !String(state.gridName || "").trim()) {
    return "Grid name is required for grid operations.";
  }
  const gridName = getActiveGridName(state);
  if ((state.includeExportGeotiff || state.includeExportFloatingPointGeotiff || state.includeExportGridAscii || state.includeExportBag || state.includeGenerateThuTvuLayers) && !String(gridName || "").trim()) {
    return "Select an active grid before generating grid export commands.";
  }
  if (state.includeExportGeotiff && !String(state.geotiffFilename || "").trim()) return "GeoTIFF export filename is required.";
  if (state.includeExportFloatingPointGeotiff && !String(state.floatingGeotiffFilename || "").trim()) return "Floating-point GeoTIFF filename is required.";
  if (state.includeExportBag && !String(state.bagVersion || "").trim()) return "BAG version is required.";
  if (state.includeExportBag && !String(state.bagXmlMetadata || "").trim()) return "BAG XML metadata file is required.";
  if (state.includeExportBag && state.bagUseXmlDate && String(state.bagDate || "").trim()) return "Choose either a BAG date or XML date, not both.";
  if (state.includeExportBag && String(state.bagOutputMode || "").trim() === "loaded-scalar" && !String(state.bagScalarPath || "").trim()) return "Scalar path is required when BAG output is loaded-scalar.";
  if (state.includeExportGsf && !getSelectedSourceExportNames(state).length) return "Select at least one source line in Sources before generating GSF export commands.";
  if (state.includeExportNoaaCsv && !String(state.noaaCsvFilename || "").trim()) return "NOAA CSV filename is required.";
  return "";
}

function validateExportPlan(state) {
  if (!String(getActiveProjectFolder(state) || "").trim()) return "Select or add a project before running exports.";
  const hasExport =
    state.includeGenerateThuTvuLayers ||
    state.includeExportGeotiff ||
    state.includeExportFloatingPointGeotiff ||
    state.includeExportGridAscii ||
    state.includeExportBag ||
    state.includeExportGsf ||
    state.includeExportNoaaCsv;
  if (!hasExport) return "Select at least one export option first.";
  const gridName = getActiveGridName(state);
  if ((state.includeGenerateThuTvuLayers || state.includeExportGeotiff || state.includeExportFloatingPointGeotiff || state.includeExportGridAscii || state.includeExportBag) && !String(gridName || "").trim()) {
    return "Select an active grid before running grid exports.";
  }
  if (state.includeExportGeotiff && !String(state.geotiffFilename || "").trim()) return "GeoTIFF export filename is required.";
  if (state.includeExportFloatingPointGeotiff && !String(state.floatingGeotiffFilename || "").trim()) return "Floating-point GeoTIFF filename is required.";
  if (state.includeExportBag && !String(state.bagVersion || "").trim()) return "BAG version is required.";
  if (state.includeExportBag && !String(state.bagXmlMetadata || "").trim()) return "BAG XML metadata file is required.";
  if (state.includeExportBag && state.bagUseXmlDate && String(state.bagDate || "").trim()) return "Choose either a BAG date or XML date, not both.";
  if (state.includeExportBag && String(state.bagOutputMode || "").trim() === "loaded-scalar" && !String(state.bagScalarPath || "").trim()) return "Scalar path is required when BAG output is loaded-scalar.";
  if (state.includeExportGsf && !getSelectedSourceExportNames(state).length) return "Select at least one source line in Sources before running GSF export.";
  if (state.includeExportNoaaCsv && !String(state.noaaCsvFilename || "").trim()) return "NOAA CSV filename is required.";
  return "";
}

export const qimeraCommandTool = {
  id: "qimera-command",
  name: "Qimera Command",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

function createRuntime(app) {
  let containerEl = null;
  let state = readState();
  let status = "";
  let environment = createEnvironmentStatus();
  let environmentCheckNonce = 0;
  let projectDialog = null;
  let sourceBusy = {
    loading: false,
    importing: false,
    processing: false,
    filtering: false,
    removing: "",
  };
  let sourceActivityTimer = null;
  let sourceAutomationTimer = null;
  let sourceAutomation = {
    running: false,
    lastScanAt: "",
    newCount: 0,
    message: "",
    error: "",
  };
  let gridBusy = {
    loading: false,
    creating: false,
    appending: false,
    removing: false,
  };
  let gridAutomationTimer = null;
  let gridAutomation = {
    running: false,
    lastRunAt: "",
    message: "",
    error: "",
  };
  let sbetBusy = {
    validating: false,
    launching: false,
  };
  let sbetStatus = {
    ok: false,
    message: "",
    details: null,
    checkedAt: "",
  };
  let svpState = {
    loading: false,
    error: "",
    lastLoadedAt: "",
    profiles: [],
    schedule: [],
    assignments: [],
    selectedProfileNames: [],
    lastSelectedProfileName: "",
  };
  let metricsState = {
    loading: false,
    error: "",
    lastCalculatedAt: "",
    summary: null,
    rows: [],
  };
  let exportBusy = {
    running: false,
  };
  let exportExecution = {
    lastRunAt: "",
    message: "",
    error: "",
    results: [],
  };
  let tracklineState = {
    loading: false,
    lastLoadedAt: "",
    error: "",
    featureCount: 0,
  };
  let sourceDiagnostics = {
    lastCheckedAt: "",
    mode: "",
    sourceStatusCount: 0,
    projectXmlCount: 0,
    importedCount: 0,
    processedCount: 0,
    lastError: "",
  };
  let lastSelectedSourcePath = "";
  let sourceWorkspaceScrollSnapshot = null;

  function getSourcesBottomPanelEl() {
    return document.querySelector(`[data-qimera-bottom-panel="${SOURCES_BOTTOM_PANEL_ID}"]`);
  }

  function bindSourcesBottomPanel() {
    const panelEl = getSourcesBottomPanelEl();
    if (!panelEl || panelEl.dataset.bound === "true") return;
    panelEl.addEventListener("input", onInput);
    panelEl.addEventListener("change", onChange);
    panelEl.addEventListener("click", onClick);
    panelEl.addEventListener("scroll", onSourceWorkspaceScroll, true);
    panelEl.dataset.bound = "true";
  }

  function unbindSourcesBottomPanel() {
    const panelEl = getSourcesBottomPanelEl();
    if (!panelEl || panelEl.dataset.bound !== "true") return;
    panelEl.removeEventListener("input", onInput);
    panelEl.removeEventListener("change", onChange);
    panelEl.removeEventListener("click", onClick);
    panelEl.removeEventListener("scroll", onSourceWorkspaceScroll, true);
    delete panelEl.dataset.bound;
  }

  function queryToolElement(selector) {
    return containerEl?.querySelector(selector) || getSourcesBottomPanelEl()?.querySelector(selector) || null;
  }

  function setSourceWorkspaceDock(nextDock) {
    state.sourceWorkspaceDock = String(nextDock || "").trim().toLowerCase() === "side" ? "side" : "bottom";
    persist();
    render();
    if (state.sourceWorkspaceDock === "bottom") {
      app.ui.showBottomPanel(SOURCES_BOTTOM_PANEL_ID);
    }
  }

  function getSourceWorkspaceScrollState() {
    if (sourceWorkspaceScrollSnapshot) return { ...sourceWorkspaceScrollSnapshot };
    const sideEl = containerEl?.querySelector?.("[data-qimera-source-scroll]");
    const bottomEl = getSourcesBottomPanelEl()?.querySelector?.("[data-qimera-source-scroll]");
    const target = bottomEl || sideEl || null;
    if (!target) return null;
    return {
      dock: bottomEl ? "bottom" : "side",
      top: Number(target.scrollTop || 0),
      left: Number(target.scrollLeft || 0),
    };
  }

  function restoreSourceWorkspaceScrollState(snapshot) {
    if (!snapshot) return;
    const host = snapshot.dock === "bottom" ? getSourcesBottomPanelEl() : containerEl;
    const target = host?.querySelector?.("[data-qimera-source-scroll]");
    if (!target) return;
    const nextTop = Number(snapshot.top || 0);
    const nextLeft = Number(snapshot.left || 0);
    if (nextTop === 0 && Number(target.scrollTop || 0) > 0) return;
    if (nextLeft === 0 && Number(target.scrollLeft || 0) > 0) return;
    target.scrollTop = nextTop;
    target.scrollLeft = nextLeft;
  }

  function stabilizeSourceWorkspaceScroll(snapshot) {
    if (!snapshot) return;
    const apply = () => restoreSourceWorkspaceScrollState(snapshot);
    apply();
    requestAnimationFrame(apply);
    setTimeout(apply, 0);
    setTimeout(apply, 50);
    setTimeout(apply, 150);
    setTimeout(apply, 300);
  }

  function onSourceWorkspaceScroll(event) {
    const target = event?.target;
    if (!(target instanceof HTMLElement) || !target.hasAttribute("data-qimera-source-scroll")) return;
    const dock = getSourcesBottomPanelEl()?.contains(target) ? "bottom" : "side";
    sourceWorkspaceScrollSnapshot = {
      dock,
      top: Number(target.scrollTop || 0),
      left: Number(target.scrollLeft || 0),
    };
  }

  function updateSourceWorkspaceSelectionUi() {
    const hosts = [containerEl, getSourcesBottomPanelEl()].filter(Boolean);
    const selectedPaths = new Set(
      getActiveSourceItems()
        .filter((item) => item.selected)
        .map((item) => String(item.path || "").trim().toLowerCase())
        .filter(Boolean),
    );
    hosts.forEach((host) => {
      host.querySelectorAll?.("[data-source-row]").forEach((row) => {
        const rowPath = String(row?.dataset?.sourceRow || "").trim().toLowerCase();
        const isSelected = selectedPaths.has(rowPath);
        row.style.background = isSelected ? "rgba(96,165,250,.12)" : "";
        const nameEl = row.querySelector?.("[data-source-name]");
        if (nameEl) {
          nameEl.style.color = isSelected ? "#dbeafe" : "";
          nameEl.style.fontWeight = isSelected ? "600" : "";
        }
      });
    });
  }

  function renderSourceWorkspaceMarkup({ activeProject, sourceItems, dock = "bottom" } = {}) {
    const sourceWorkspaceOpen = isSectionOpen("sourceWorkspace");
    const svpReprocessCount = Array.isArray(sourceItems)
      ? sourceItems.filter((item) => item.svpNeedsReprocess === true && item.importStatus === "imported").length
      : 0;
    const headerCopy = activeProject
      ? `${activeProject.name} • ${sourceItems.length} line${sourceItems.length === 1 ? "" : "s"} loaded`
      : "Select a project and load a source folder to work with source lines.";
    const bodyHtml = `
      <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:10px; flex-wrap:wrap;">
        <div>
          <div class="small" style="margin-top:0; color:#cbd5e1;">${esc(headerCopy)}</div>
          <div class="small" style="margin-top:4px; opacity:.78;">Click rows to select lines for actions. Use the Track checkbox to show or hide tracklines.</div>
          ${svpReprocessCount ? `<div class="small" style="margin-top:4px; color:#fbbf24;">${esc(`${svpReprocessCount} line${svpReprocessCount === 1 ? "" : "s"} need SVP reprocess.`)}</div>` : ""}
        </div>
        <div class="row lm-add-row" style="margin-top:0;">
          <label class="small">Dock</label>
          <select id="qimera-source-workspace-dock" style="width:120px;">
            <option value="bottom"${dock === "bottom" ? " selected" : ""}>Bottom</option>
            <option value="side"${dock === "side" ? " selected" : ""}>Sidebar</option>
          </select>
          <button id="qimera-source-select-all" class="btn">Select All</button>
          <button id="qimera-source-select-none" class="btn">Select None</button>
          <button id="qimera-source-remove-selected" class="btn"${sourceBusy.removing === "__bulk__" ? " disabled" : ""}>${sourceBusy.removing === "__bulk__" ? "Removing..." : "Remove Selected"}</button>
          <label class="small" style="margin-left:8px;">Flow</label>
          <select id="qimera-source-flow-mode" style="width:120px;">
            <option value="single"${state.sourceFlowMode === "single" ? " selected" : ""}>Single</option>
            <option value="batch"${state.sourceFlowMode === "batch" ? " selected" : ""}>Batch</option>
          </select>
          <button id="qimera-import-selected" class="btn primary"${sourceBusy.importing || !activeProject ? " disabled" : ""}>${sourceBusy.importing ? "Importing..." : "Import Selected"}</button>
          <button id="qimera-process-imported" class="btn"${sourceBusy.processing || !activeProject ? " disabled" : ""}>${sourceBusy.processing ? "Processing..." : "Process Selected"}</button>
          <button id="qimera-refresh-sources" class="btn">Refresh Status</button>
        </div>
      </div>
      <details class="toolset-group" style="margin-top:8px;">
        <summary>Source Diagnostics</summary>
        <div class="toolset-content">
          <div class="small">
            ${
              sourceDiagnostics.lastCheckedAt
                ? esc(`Last check: ${formatTimestamp(sourceDiagnostics.lastCheckedAt)} | Mode: ${sourceDiagnostics.mode || "n/a"}`)
                : "No source diagnostics yet."
            }
          </div>
          <div class="small" style="margin-top:6px;">
            ${esc(`source-status rows: ${sourceDiagnostics.sourceStatusCount} | project-xml rows: ${sourceDiagnostics.projectXmlCount} | imported: ${sourceDiagnostics.importedCount} | processed: ${sourceDiagnostics.processedCount}`)}
          </div>
          ${sourceDiagnostics.lastError ? `<div class="small" style="margin-top:6px; color:#fca5a5;">${esc(`Last error: ${sourceDiagnostics.lastError}`)}</div>` : ""}
        </div>
      </details>
      <div data-qimera-source-scroll style="flex:1; min-height:0; border:1px solid rgba(148,163,184,.18); border-radius:10px; overflow:auto; margin-top:10px;">
        <table style="width:100%; border-collapse:collapse; font-size:12px; table-layout:fixed;">
          <colgroup>
            <col style="width:54px;" />
            <col />
            <col style="width:98px;" />
            <col style="width:92px;" />
            <col style="width:90px;" />
            <col style="width:90px;" />
            <col style="width:90px;" />
            <col style="width:170px;" />
            <col style="width:84px;" />
          </colgroup>
          <thead>
            <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18); position:sticky; top:0; background:rgba(30,41,59,.96); z-index:1;">
              <th style="padding:6px 8px;">Track</th>
              <th style="padding:6px 8px;">Line</th>
              <th style="padding:6px 8px;">State</th>
              <th style="padding:6px 8px; text-align:right;">Size</th>
              <th style="padding:6px 8px; text-align:center;">Imported</th>
              <th style="padding:6px 8px; text-align:center;">Processed</th>
              <th style="padding:6px 8px; text-align:center;">Appended</th>
              <th style="padding:6px 8px;">Filter</th>
              <th style="padding:6px 8px; text-align:center;">Remove</th>
            </tr>
          </thead>
          <tbody>
            ${sourceItems.length ? sourceItems.map((item) => `
              <tr data-source-row="${esc(item.path)}" style="border-bottom:1px solid rgba(148,163,184,.1); cursor:pointer; ${item.selected ? "background:rgba(96,165,250,.12);" : (item.svpNeedsReprocess ? "background:rgba(251,146,60,.16);" : "")} ${item.svpNeedsReprocess ? "box-shadow: inset 3px 0 0 rgba(251,146,60,.85);" : ""}">
                <td style="padding:6px 8px;"><input type="checkbox" data-source-check="${esc(item.path)}" ${item.checked ? "checked" : ""} /></td>
                <td style="padding:6px 8px;"><div data-source-name style="white-space:nowrap; overflow:hidden; text-overflow:ellipsis; ${item.selected ? "color:#dbeafe; font-weight:600;" : ""}">${esc(item.name)}</div></td>
                <td data-source-state style="padding:6px 8px; ${sourceStateTone(item)}">${esc(sourceStateLabel(item))}</td>
                <td data-source-size style="padding:6px 8px; text-align:right; color:#cbd5e1;">${esc(formatBytes(item.sizeBytes || 0))}</td>
                <td data-source-imported style="padding:6px 8px; text-align:center;">${stateGlyph(item.importStatus === "imported" || item.importStatus === "importing")}</td>
                <td data-source-processed style="padding:6px 8px; text-align:center;">${stateGlyph((item.processStatus === "processed" || item.processStatus === "processing") && item.svpNeedsReprocess !== true)}</td>
                <td data-source-appended style="padding:6px 8px; text-align:center;">${stateGlyph(item.gridAppendStatus === "appended" || item.gridAppendStatus === "appending")}</td>
                <td data-source-filter style="padding:6px 8px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(item.filterProfile || "None")}</td>
                <td style="padding:6px 8px; text-align:center;">
                  <button class="btn" data-source-zoom="${esc(item.path)}" title="Zoom to line" aria-label="Zoom to line">⌖</button>
                  ${item.importStatus === "imported"
                    ? `<button class="btn" data-source-remove="${esc(item.path)}" title="Remove from project" aria-label="Remove from project"${sourceBusy.removing === item.path ? " disabled" : ""}>${sourceBusy.removing === item.path ? "..." : "X"}</button>`
                    : ""}
                </td>
              </tr>
              <tr data-source-activity-path="${esc(item.path)}" style="${sourceRowActivity(item).active ? "" : "display:none;"}">
                <td colspan="9" style="padding:0;">
                  <div style="height:3px; background:rgba(148,163,184,.14); overflow:hidden;">
                    <div data-source-activity-fill style="height:100%; width:${sourceRowActivity(item).progress}%; background:${sourceRowActivity(item).color}; transition:width .3s ease;"></div>
                  </div>
                </td>
              </tr>
            `).join("") : `
              <tr>
                <td colspan="9" style="padding:10px 8px;" class="small">Choose a host source folder and load lines to build the import list.</td>
              </tr>
            `}
          </tbody>
        </table>
      </div>
    `;
    if (dock === "bottom") {
      return `
        <details class="card lt-collapsible" data-section-key="sourceWorkspace" ${sourceWorkspaceOpen ? "open" : ""} style="height:100%; display:flex; flex-direction:column;">
          <summary>Source Workspace</summary>
          <div style="display:flex; flex-direction:column; min-height:0; flex:1;">${bodyHtml}</div>
        </details>
      `;
    }
    return `
      <details class="toolset-group" data-section-key="sourceWorkspace" ${sourceWorkspaceOpen ? "open" : ""} style="margin-top:10px;">
        <summary>Source Workspace</summary>
        <div class="toolset-content" style="display:flex; flex-direction:column; min-height:0;">${bodyHtml}</div>
      </details>
    `;
  }

  function persist() {
    state.projectFolder = getActiveProjectFolder(state);
    writeState(state);
  }

  function isSectionOpen(key) {
    return state.sectionOpen?.[key] !== false;
  }

  function captureSectionState() {
    if (!containerEl && !getSourcesBottomPanelEl()) return;
    const detailsEls = [
      ...(containerEl ? Array.from(containerEl.querySelectorAll("details[data-section-key]")) : []),
      ...(getSourcesBottomPanelEl() ? Array.from(getSourcesBottomPanelEl().querySelectorAll("details[data-section-key]")) : []),
    ];
    detailsEls.forEach((el) => {
      const key = String(el.getAttribute("data-section-key") || "").trim();
      if (!key) return;
      if (!state.sectionOpen || typeof state.sectionOpen !== "object") {
        state.sectionOpen = { ...defaultState().sectionOpen };
      }
      state.sectionOpen[key] = !!el.open;
    });
  }

  function bindSectionToggleHandlers() {
    if (!containerEl && !getSourcesBottomPanelEl()) return;
    const detailsEls = [
      ...(containerEl ? Array.from(containerEl.querySelectorAll("details[data-section-key]")) : []),
      ...(getSourcesBottomPanelEl() ? Array.from(getSourcesBottomPanelEl().querySelectorAll("details[data-section-key]")) : []),
    ];
    detailsEls.forEach((el) => {
      el.addEventListener("toggle", () => {
        const key = String(el.getAttribute("data-section-key") || "").trim();
        if (!key) return;
        if (!state.sectionOpen || typeof state.sectionOpen !== "object") {
          state.sectionOpen = { ...defaultState().sectionOpen };
        }
        state.sectionOpen[key] = !!el.open;
        writeState(state);
      });
    });
  }

  function syncProjectSelection() {
    state.projects = Array.isArray(state.projects) ? state.projects.map(normalizeProjectRecord).filter(Boolean) : [];
    if (!state.projects.some((project) => project.id === state.selectedProjectId)) {
      state.selectedProjectId = state.projects[0]?.id || "";
    }
    const previousProjectFolder = String(state.projectFolder || "").trim();
    const activeProject = getActiveProject(state);
    state.projectFolder = activeProject ? getActiveProjectFolder(state) : "";
    if (activeProject) {
      const nextProjectFolder = String(activeProject.folder || "").trim();
      state.crsMode = String(activeProject.crsMode || "epsg").trim();
      state.epsgCode = String(activeProject.epsgCode || "").trim();
      state.wktFile = String(activeProject.wktFile || "").trim();
      state.surveyType = String(activeProject.surveyType || "").trim();
      state.description = String(activeProject.description || "").trim();
      state.inputPath = String(activeProject.sourceFolder || "").trim();
      state.sbetFile = String(activeProject.sbetFile || "").trim();
      state.sbetRmsFile = String(activeProject.sbetRmsFile || "").trim();
      state.sbetGpsWeek = String(activeProject.sbetGpsWeek || "").trim();
      state.sbetCoordinateSystem = String(activeProject.sbetCoordinateSystem || "").trim();
      state.sbetPriority = activeProject.sbetPriority !== false;
      state.monitorSourceFolder = activeProject.monitorSourceFolder === true;
      state.autoImportNewSources = activeProject.autoImportNewSources === true;
      state.autoProcessNewSources = activeProject.autoProcessNewSources === true;
      state.autoFilterSources = activeProject.autoFilterSources === true;
      state.selectedFilterProfile = String(activeProject.selectedFilterProfile || "").trim();
      state.sourceScanIntervalSec = normalizeSourceScanInterval(activeProject.sourceScanIntervalSec);
      state.svpAssignmentStrategy = String(activeProject.svpAssignmentStrategy || "nearest-time").trim() || "nearest-time";
      state.svpMaxTimeHours = String(activeProject.svpMaxTimeHours ?? "").trim();
      state.svpMaxDistanceNm = String(activeProject.svpMaxDistanceNm ?? "").trim();
      state.selectedGridName = String(activeProject.selectedGridName || "").trim();
      state.autoAppendProcessedToGrid = activeProject.autoAppendProcessedToGrid === true;
      state.autoExportGeotiffOnGridAppend = activeProject.autoExportGeotiffOnGridAppend !== false;
      const currentExportFolder = String(state.exportFolder || "").trim();
      const previousDefaultExport = defaultProjectExportFolder(previousProjectFolder);
      if (!currentExportFolder || (previousProjectFolder && currentExportFolder.toLowerCase() === previousDefaultExport.toLowerCase())) {
        state.exportFolder = defaultProjectExportFolder(nextProjectFolder);
      }
      state.additionalFiles = (activeProject.sourceItems || [])
        .filter((item) => item.selected)
        .map((item) => item.path)
        .join("\n");
    } else {
      state.additionalFiles = "";
    }
  }

  function pullStateFromForm() {
    if (!containerEl) return;
    const nextMonitorSourceFolder = !!queryToolElement("#qimera-monitor-source-folder")?.checked;
    const nextAutoImportNewSources = !!queryToolElement("#qimera-auto-import-sources")?.checked;
    const nextAutoProcessNewSources = !!queryToolElement("#qimera-auto-process-sources")?.checked;
    const nextAutoFilterSources = !!queryToolElement("#qimera-auto-filter-sources")?.checked;
    const nextSelectedFilterProfile = String(queryToolElement("#qimera-filter-profile")?.value || state.selectedFilterProfile || "").trim();
    const nextSourceScanIntervalSec = normalizeSourceScanInterval(queryToolElement("#qimera-source-scan-interval")?.value || state.sourceScanIntervalSec || 30);
    state.workflowPreset = String(queryToolElement("#qimera-workflow-preset")?.value || state.workflowPreset || "create_import_process_grid");
    state.qimeraExecutable = String(queryToolElement("#qimera-exe")?.value || "");
    state.crsMode = String(queryToolElement("#qimera-crs-mode")?.value || state.crsMode || "epsg");
    state.epsgCode = String(queryToolElement("#qimera-epsg")?.value || state.epsgCode || "");
    state.wktFile = String(queryToolElement("#qimera-wkt-file")?.value || state.wktFile || "");
    state.surveyType = String(queryToolElement("#qimera-survey-type")?.value || state.surveyType || "");
    state.description = String(queryToolElement("#qimera-description")?.value || state.description || "");
    state.importFormat = String(queryToolElement("#qimera-import-format")?.value || "kmall");
    state.sourceFlowMode = String(queryToolElement("#qimera-source-flow-mode")?.value || state.sourceFlowMode || "batch");
    state.svpAssignmentStrategy = String(queryToolElement("#qimera-svp-strategy")?.value || state.svpAssignmentStrategy || "nearest-time");
    state.svpMaxTimeHours = String(queryToolElement("#qimera-svp-max-time-hours")?.value || state.svpMaxTimeHours || "");
    state.svpMaxDistanceNm = String(queryToolElement("#qimera-svp-max-distance-nm")?.value || state.svpMaxDistanceNm || "");
    state.importMode = String(queryToolElement("#qimera-import-mode")?.value || "folder");
    state.inputPath = String(queryToolElement("#qimera-input-path")?.value || "");
    state.sbetFile = String(queryToolElement("#qimera-sbet-file")?.value || "");
    state.sbetRmsFile = String(queryToolElement("#qimera-sbet-rms-file")?.value || "");
    state.sbetGpsWeek = String(queryToolElement("#qimera-sbet-gps-week")?.value || "");
    state.sbetCoordinateSystem = String(queryToolElement("#qimera-sbet-coordinate-system")?.value || "");
    state.sbetPriority = queryToolElement("#qimera-sbet-priority")?.checked !== false;
    state.additionalFiles = String(queryToolElement("#qimera-files")?.value || "");
    state.vesselName = String(queryToolElement("#qimera-vessel-name")?.value || "");
    state.delayConvertToQpd = !!queryToolElement("#qimera-delay-qpd")?.checked;
    state.includeCreateProject = queryToolElement("#qimera-step-create") ? !!queryToolElement("#qimera-step-create")?.checked : state.includeCreateProject;
    state.includeImport = queryToolElement("#qimera-step-import") ? !!queryToolElement("#qimera-step-import")?.checked : state.includeImport;
    state.includeAutoProcess = queryToolElement("#qimera-step-process") ? !!queryToolElement("#qimera-step-process")?.checked : state.includeAutoProcess;
    state.includeCreateDynamicGrid = queryToolElement("#qimera-step-grid") ? !!queryToolElement("#qimera-step-grid")?.checked : state.includeCreateDynamicGrid;
    state.includeAppendToGrid = queryToolElement("#qimera-step-append") ? !!queryToolElement("#qimera-step-append")?.checked : state.includeAppendToGrid;
    state.includeExportGeotiff = queryToolElement("#qimera-step-geotiff") ? !!queryToolElement("#qimera-step-geotiff")?.checked : state.includeExportGeotiff;
    state.includeExportFloatingPointGeotiff = queryToolElement("#qimera-step-float-geotiff") ? !!queryToolElement("#qimera-step-float-geotiff")?.checked : state.includeExportFloatingPointGeotiff;
    state.includeExportGridAscii = queryToolElement("#qimera-step-grid-ascii") ? !!queryToolElement("#qimera-step-grid-ascii")?.checked : state.includeExportGridAscii;
    state.includeExportBag = queryToolElement("#qimera-step-bag") ? !!queryToolElement("#qimera-step-bag")?.checked : state.includeExportBag;
    state.includeGenerateThuTvuLayers = queryToolElement("#qimera-step-thu-tvu") ? !!queryToolElement("#qimera-step-thu-tvu")?.checked : state.includeGenerateThuTvuLayers;
    state.includeExportGsf = queryToolElement("#qimera-step-gsf") ? !!queryToolElement("#qimera-step-gsf")?.checked : state.includeExportGsf;
    state.includeExportNoaaCsv = queryToolElement("#qimera-step-noaa") ? !!queryToolElement("#qimera-step-noaa")?.checked : state.includeExportNoaaCsv;
    state.includeInventory = queryToolElement("#qimera-step-inventory") ? !!queryToolElement("#qimera-step-inventory")?.checked : state.includeInventory;
    state.gridName = String(queryToolElement("#qimera-grid-name")?.value || "");
    state.cellSize = String(queryToolElement("#qimera-cell-size")?.value || "");
    state.gridMode = String(queryToolElement("#qimera-grid-mode")?.value || state.gridMode || "cube");
    state.gridProfile = String(queryToolElement("#qimera-grid-profile")?.value || state.gridProfile || "Default");
    state.excludeGridCoverage = queryToolElement("#qimera-grid-exclude-coverage")?.checked === true;
    state.autoAppendProcessedToGrid = queryToolElement("#qimera-grid-auto-append")?.checked === true;
    state.exportFolder = String(queryToolElement("#qimera-export-folder")?.value || "");
    state.geotiffFilename = String(queryToolElement("#qimera-geotiff-filename")?.value || "");
    state.geotiffWorldFile = queryToolElement("#qimera-geotiff-world-file")?.checked === true;
    state.geotiffOrigin = String(containerEl.querySelector("#qimera-geotiff-origin")?.value || "lower-left");
    state.floatingGeotiffFilename = String(containerEl.querySelector("#qimera-float-geotiff-filename")?.value || "");
    state.floatingGeotiffNoDataValue = String(containerEl.querySelector("#qimera-float-geotiff-nodata")?.value || "");
    state.asciiBounds = String(containerEl.querySelector("#qimera-ascii-bounds")?.value || "");
    state.asciiZPrecision = String(containerEl.querySelector("#qimera-ascii-z-precision")?.value || "");
    state.asciiColumnSeparator = String(containerEl.querySelector("#qimera-ascii-separator")?.value || "");
    state.bagDate = String(containerEl.querySelector("#qimera-bag-date")?.value || "");
    state.bagUseXmlDate = containerEl.querySelector("#qimera-bag-xml-date")?.checked === true;
    state.bagCompression = String(containerEl.querySelector("#qimera-bag-compression")?.value || "0");
    state.bagScalarPath = String(containerEl.querySelector("#qimera-bag-scalar-path")?.value || "");
    state.bagXmlMetadata = String(containerEl.querySelector("#qimera-bag-xml")?.value || "");
    state.bagVersion = String(containerEl.querySelector("#qimera-bag-version")?.value || "15");
    state.bagOutputMode = String(containerEl.querySelector("#qimera-bag-output")?.value || "compute-tvu");
    state.geotiffDepthLayer = String(containerEl.querySelector("#qimera-depth-layer")?.value || "Average");
    state.autoLoadExportedTiffs = containerEl.querySelector("#qimera-auto-load-tiffs")?.checked !== false;
    state.gsfFilenamePattern = String(containerEl.querySelector("#qimera-gsf-pattern")?.value || "");
    state.gsfReplaceWhitespace = containerEl.querySelector("#qimera-gsf-replace-whitespace")?.checked === true;
    state.gsfExportSystemsIndividually = containerEl.querySelector("#qimera-gsf-export-systems")?.checked === true;
    state.gsfSpecificSystem = String(containerEl.querySelector("#qimera-gsf-specific-system")?.value || "");
    state.gsfExcludeUserAdditionalSoundings = containerEl.querySelector("#qimera-gsf-exclude-user-additional")?.checked === true;
    state.gsfExcludeRawBackscatterImagery = containerEl.querySelector("#qimera-gsf-exclude-backscatter")?.checked === true;
    state.gsfDoNotFlagFilteredAsManualEdits = containerEl.querySelector("#qimera-gsf-do-not-flag-filtered")?.checked === true;
    state.gsfFlagAllInterpolatedAsAccepted = containerEl.querySelector("#qimera-gsf-flag-interpolated")?.checked === true;
    state.noaaCsvFilename = String(containerEl.querySelector("#qimera-noaa-filename")?.value || "");
    state.printProgress = containerEl.querySelector("#qimera-print-progress") ? !!containerEl.querySelector("#qimera-print-progress")?.checked : state.printProgress;
    syncProjectSelection();
    state.monitorSourceFolder = nextMonitorSourceFolder;
    state.autoImportNewSources = nextAutoImportNewSources;
    state.autoProcessNewSources = nextAutoProcessNewSources;
    state.autoFilterSources = nextAutoFilterSources;
    state.selectedFilterProfile = nextSelectedFilterProfile;
    state.sourceScanIntervalSec = nextSourceScanIntervalSec;
    const activeProject = getActiveProject(state);
    if (activeProject) {
      activeProject.crsMode = String(state.crsMode || "epsg").trim();
      activeProject.epsgCode = String(state.epsgCode || "").trim();
      activeProject.wktFile = String(state.wktFile || "").trim();
      activeProject.surveyType = String(state.surveyType || "").trim();
      activeProject.description = String(state.description || "").trim();
      activeProject.sourceFolder = String(state.inputPath || "").trim();
      activeProject.sbetFile = String(state.sbetFile || "").trim();
      activeProject.sbetRmsFile = String(state.sbetRmsFile || "").trim();
      activeProject.sbetGpsWeek = String(state.sbetGpsWeek || "").trim();
      activeProject.sbetCoordinateSystem = String(state.sbetCoordinateSystem || "").trim();
      activeProject.sbetPriority = state.sbetPriority !== false;
      activeProject.monitorSourceFolder = state.monitorSourceFolder === true;
      activeProject.autoImportNewSources = state.autoImportNewSources === true;
      activeProject.autoProcessNewSources = state.autoProcessNewSources === true;
      activeProject.autoFilterSources = state.autoFilterSources === true;
      activeProject.selectedFilterProfile = String(state.selectedFilterProfile || "").trim();
      activeProject.sourceScanIntervalSec = normalizeSourceScanInterval(state.sourceScanIntervalSec);
      activeProject.svpAssignmentStrategy = String(state.svpAssignmentStrategy || "nearest-time").trim() || "nearest-time";
      activeProject.svpMaxTimeHours = String(state.svpMaxTimeHours || "").trim();
      activeProject.svpMaxDistanceNm = String(state.svpMaxDistanceNm || "").trim();
      activeProject.selectedGridName = String(state.selectedGridName || "").trim();
      activeProject.autoAppendProcessedToGrid = state.autoAppendProcessedToGrid === true;
      activeProject.autoExportGeotiffOnGridAppend = state.autoExportGeotiffOnGridAppend !== false;
    }
    persist();
  }

  function getActiveAutomationSettings() {
    const activeProject = getActiveProject(state);
    return {
      monitorSourceFolder: activeProject?.monitorSourceFolder === true,
      autoImportNewSources: activeProject?.autoImportNewSources === true,
      autoProcessNewSources: activeProject?.autoProcessNewSources === true,
      autoFilterSources: activeProject?.autoFilterSources === true,
      selectedFilterProfile: String(activeProject?.selectedFilterProfile || state.selectedFilterProfile || "").trim(),
      sourceScanIntervalSec: normalizeSourceScanInterval(activeProject?.sourceScanIntervalSec || state.sourceScanIntervalSec || 30),
    };
  }

  function getActiveSvpStrategySettings() {
    const activeProject = getActiveProject(state);
    return {
      strategy: String(activeProject?.svpAssignmentStrategy || state.svpAssignmentStrategy || "nearest-time").trim() || "nearest-time",
      maxTimeHours: String(activeProject?.svpMaxTimeHours ?? state.svpMaxTimeHours ?? "").trim(),
      maxDistanceNm: String(activeProject?.svpMaxDistanceNm ?? state.svpMaxDistanceNm ?? "").trim(),
    };
  }

  function buildSvpStrategyPayload() {
    const settings = getActiveSvpStrategySettings();
    const maxTimeHours = Number(settings.maxTimeHours);
    const maxDistanceNm = Number(settings.maxDistanceNm);
    return {
      strategy: settings.strategy,
      maxTimeHours: Number.isFinite(maxTimeHours) && maxTimeHours > 0 ? maxTimeHours : null,
      maxDistanceNm: Number.isFinite(maxDistanceNm) && maxDistanceNm > 0 ? maxDistanceNm : null,
    };
  }

  function markSourcesNeedingSvpReprocess(affectedSourceKeys, assignmentMap) {
    const affected = affectedSourceKeys instanceof Set ? affectedSourceKeys : new Set();
    if (!affected.size) return 0;
    const items = getActiveSourceItems();
    let changed = 0;
    const nextItems = items.map((item) => {
      const nameKey = normalizeSourceNameKey(item.name || item.path.split(/[\\/]/).pop() || "");
      if (!nameKey || !affected.has(nameKey)) return item;
      const nextProfile = String(assignmentMap?.get(nameKey) || item.svpAssignedProfile || "").trim();
      if (item.svpNeedsReprocess === true && item.svpAssignedProfile === nextProfile) return item;
      changed += 1;
      return normalizeSourceItem({
        ...item,
        svpNeedsReprocess: item.importStatus === "imported",
        svpAssignedProfile: nextProfile,
        processStatus: item.importStatus === "imported" ? "pending" : item.processStatus,
        checked: item.importStatus === "imported" ? true : item.checked,
      });
    }).filter(Boolean);
    if (changed > 0) {
      setActiveProjectSourceItems(nextItems);
    }
    return changed;
  }

  function stopSourceAutomationWatcher() {
    if (sourceAutomationTimer) {
      clearTimeout(sourceAutomationTimer);
      sourceAutomationTimer = null;
    }
  }

  async function persistActiveProjectMetadata(fields) {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) return null;
    const payload = { projectFolder: activeProject.folder, ...(fields && typeof fields === "object" ? fields : {}) };
    const result = await postJson("/api/qimera/project-metadata", payload, 30000);
    const metadata = result?.metadata && typeof result.metadata === "object" ? result.metadata : {};
    activeProject.sourceFolder = String(metadata.sourceFolder || activeProject.sourceFolder || "").trim();
    activeProject.sbetFile = String(metadata.sbetFile || activeProject.sbetFile || "").trim();
    activeProject.sbetRmsFile = String(metadata.sbetRmsFile || activeProject.sbetRmsFile || "").trim();
    activeProject.sbetGpsWeek = String(metadata.sbetGpsWeek || activeProject.sbetGpsWeek || "").trim();
    activeProject.sbetCoordinateSystem = String(metadata.sbetCoordinateSystem || activeProject.sbetCoordinateSystem || "").trim();
    activeProject.sbetPriority = metadata.sbetPriority !== false;
    activeProject.monitorSourceFolder = metadata.monitorSourceFolder === true;
    activeProject.autoImportNewSources = metadata.autoImportNewSources === true;
    activeProject.autoProcessNewSources = metadata.autoProcessNewSources === true;
    activeProject.autoFilterSources = metadata.autoFilterSources === true;
    activeProject.selectedFilterProfile = String(metadata.selectedFilterProfile || activeProject.selectedFilterProfile || "").trim();
    activeProject.sourceScanIntervalSec = normalizeSourceScanInterval(metadata.sourceScanIntervalSec || activeProject.sourceScanIntervalSec || 30);
    activeProject.svpAssignmentStrategy = String(metadata.svpAssignmentStrategy || activeProject.svpAssignmentStrategy || "nearest-time").trim() || "nearest-time";
    activeProject.svpMaxTimeHours = String(metadata.svpMaxTimeHours ?? activeProject.svpMaxTimeHours ?? "").trim();
    activeProject.svpMaxDistanceNm = String(metadata.svpMaxDistanceNm ?? activeProject.svpMaxDistanceNm ?? "").trim();
    activeProject.selectedGridName = String(metadata.selectedGridName || activeProject.selectedGridName || "").trim();
    activeProject.autoAppendProcessedToGrid = metadata.autoAppendProcessedToGrid === true;
    activeProject.autoExportGeotiffOnGridAppend = metadata.autoExportGeotiffOnGridAppend !== false;
    activeProject.lastGridAppendSignature = String(metadata.lastGridAppendSignature || activeProject.lastGridAppendSignature || "").trim();
    persist();
    return result;
  }

  function openProjectDialog(mode) {
    projectDialog = {
      mode,
      name: "",
      crsMode: "epsg",
      epsgCode: String(state.epsgCode || "").trim(),
      wktFile: String(state.wktFile || "").trim(),
      surveyType: "",
      description: "",
      host: String(window.location.hostname || "localhost"),
      folder: "",
      availableProjects: [],
      loading: mode === "open",
      submitting: false,
      error: "",
    };
    render();
    if (mode === "open") {
      loadManagedProjects();
    }
  }

  function closeProjectDialog() {
    projectDialog = null;
    render();
  }

  function updateProjectDialogField(field, value) {
    if (!projectDialog) return;
    projectDialog[field] = String(value || "");
    projectDialog.error = "";
  }

  async function loadManagedProjects() {
    if (!projectDialog || projectDialog.mode !== "open") return;
    projectDialog.loading = true;
    render();
    try {
      const payload = await fetchJsonWithTimeout("/api/qimera/projects", 15000);
      syncManagedProjectPresence(Array.isArray(payload?.projects) ? payload.projects : []);
      if (!projectDialog || projectDialog.mode !== "open") return;
      projectDialog.availableProjects = Array.isArray(payload?.projects) ? payload.projects : [];
      projectDialog.loading = false;
      render();
    } catch (error) {
      status = error?.message || "Could not load host-managed projects.";
      if (projectDialog && projectDialog.mode === "open") {
        projectDialog.loading = false;
        render();
      }
    }
  }

  function syncManagedProjectPresence(managedProjects) {
    const folders = new Set(
      (Array.isArray(managedProjects) ? managedProjects : [])
        .map((project) => String(project?.folder || "").trim().toLowerCase())
        .filter(Boolean),
    );
    state.projects = state.projects.map((project) => ({
      ...project,
      missingOnHost: !folders.has(String(project.folder || "").trim().toLowerCase()),
    }));
    persist();
  }

  async function refreshManagedProjectPresence() {
    try {
      const payload = await fetchJsonWithTimeout("/api/qimera/projects", 15000);
      syncManagedProjectPresence(Array.isArray(payload?.projects) ? payload.projects : []);
      render();
    } catch {
      // Leave existing state unchanged if the host list cannot be read.
    }
  }

  function addManagedProject(project) {
    const folder = String(project?.folder || "").trim();
    const name = String(project?.name || inferProjectNameFromFolder(folder)).trim();
    if (!folder || !name) return;
    const host = String(window.location.hostname || "localhost");
    const existing = state.projects.find((item) => item.folder.toLowerCase() === folder.toLowerCase());
    if (existing) {
      projectDialog = null;
      selectProject(existing.id);
      status = `Opened existing project ${existing.name}.`;
      render();
      return;
    }
    const next = normalizeProjectRecord({
      id: createProjectId(),
      name,
      host,
      folder,
      mode: "existing",
      crsMode: String(project?.crsMode || "").trim() || (String(project?.wktFile || "").trim() ? "wkt-file" : "epsg"),
      epsgCode: String(project?.epsgCode || "").trim(),
      wktFile: String(project?.wktFile || "").trim(),
      surveyType: String(project?.surveyType || "").trim(),
      description: String(project?.description || "").trim(),
      sourceFolder: String(project?.sourceFolder || "").trim(),
      monitorSourceFolder: project?.monitorSourceFolder === true,
      autoImportNewSources: project?.autoImportNewSources === true,
      autoProcessNewSources: project?.autoProcessNewSources === true,
      autoFilterSources: project?.autoFilterSources === true,
      selectedFilterProfile: String(project?.selectedFilterProfile || "").trim(),
      sourceScanIntervalSec: normalizeSourceScanInterval(project?.sourceScanIntervalSec || 30),
      sourceItems: [],
      gridItems: [],
      selectedGridName: String(project?.selectedGridName || "").trim(),
      autoAppendProcessedToGrid: project?.autoAppendProcessedToGrid === true,
      autoExportGeotiffOnGridAppend: project?.autoExportGeotiffOnGridAppend !== false,
      lastGridAppendSignature: String(project?.lastGridAppendSignature || "").trim(),
      addedAt: new Date().toISOString(),
    });
    if (!next) return;
    state.projects = [next, ...state.projects];
    projectDialog = null;
    selectProject(next.id);
    status = `Opened ${name} from the host project root.`;
    render();
    refreshManagedProjectPresence();
  }

  async function commitProjectDialog() {
    if (!projectDialog) return;
    const host = String(window.location.hostname || "localhost");
    const name = String(projectDialog.name || "").trim();
    const crsMode = String(projectDialog.crsMode || "epsg").trim();
    const epsgCode = String(projectDialog.epsgCode || "").trim();
    const wktFile = String(projectDialog.wktFile || "").trim();
    const surveyType = String(projectDialog.surveyType || "").trim();
    const description = String(projectDialog.description || "").trim();
    if (projectDialog.mode !== "create") return;
    if (!name) {
      const message = "Project name is required.";
      projectDialog.error = message;
      status = message;
      render();
      return;
    }
    if (crsMode === "wkt-file") {
      if (!wktFile) {
        const message = "DB/WKT file path is required when deriving the project CRS.";
        projectDialog.error = message;
        status = message;
        render();
        return;
      }
    } else if (!epsgCode) {
      const message = "EPSG is required when creating a Qimera project.";
      projectDialog.error = message;
      status = message;
      render();
      return;
    }
    if (!surveyType) {
      const message = "Survey type is required when creating a Qimera project.";
      projectDialog.error = message;
      status = message;
      render();
      return;
    }
    if (!description) {
      const message = "Description is required when creating a Qimera project.";
      projectDialog.error = message;
      status = message;
      render();
      return;
    }
    projectDialog.submitting = true;
    projectDialog.error = "";
    status = `Creating Qimera project "${name}" in the host project root...`;
    render();
    let created;
    try {
      created = await postJson("/api/qimera/create-project", {
        executablePath: String(state.qimeraExecutable || "").trim(),
        projectName: name,
        crsMode,
        epsgCode,
        wktFile,
        surveyType,
        description,
      });
      if (created?.executablePath && created.executablePath !== state.qimeraExecutable) {
        state.qimeraExecutable = String(created.executablePath);
      }
    } catch (error) {
      if (projectDialog) projectDialog.submitting = false;
      const message = error?.message || "Project creation failed.";
      if (projectDialog) projectDialog.error = message;
      status = message;
      render();
      return;
    }
    const folder = String(created?.projectFolder || "").trim();
    const existing = state.projects.find((project) => project.folder.toLowerCase() === folder.toLowerCase() && project.host.toLowerCase() === host.toLowerCase());
    if (existing) {
      existing.name = name;
      existing.mode = "new";
      state.selectedProjectId = existing.id;
      syncProjectSelection();
      persist();
      projectDialog = null;
      status = `Selected existing registry entry for ${name}.`;
      render();
      return;
    }
    const next = normalizeProjectRecord({
      id: createProjectId(),
      name: String(created?.projectName || name),
      host,
      folder,
      mode: "new",
      crsMode: String(created?.metadata?.crsMode || created?.crsMode || crsMode || "epsg").trim(),
      epsgCode: String(created?.metadata?.epsgCode || epsgCode || "").trim(),
      wktFile: String(created?.metadata?.wktFile || created?.wktFile || wktFile || "").trim(),
      surveyType: String(created?.metadata?.surveyType || surveyType || "").trim(),
      description: String(created?.metadata?.description || description || "").trim(),
      sourceFolder: String(created?.metadata?.sourceFolder || "").trim(),
      monitorSourceFolder: created?.metadata?.monitorSourceFolder === true,
      autoImportNewSources: created?.metadata?.autoImportNewSources === true,
      autoProcessNewSources: created?.metadata?.autoProcessNewSources === true,
      autoFilterSources: created?.metadata?.autoFilterSources === true,
      selectedFilterProfile: String(created?.metadata?.selectedFilterProfile || "").trim(),
      sourceScanIntervalSec: normalizeSourceScanInterval(created?.metadata?.sourceScanIntervalSec || 30),
      sourceItems: [],
      gridItems: [],
      selectedGridName: String(created?.metadata?.selectedGridName || "").trim(),
      autoAppendProcessedToGrid: created?.metadata?.autoAppendProcessedToGrid === true,
      autoExportGeotiffOnGridAppend: created?.metadata?.autoExportGeotiffOnGridAppend !== false,
      lastGridAppendSignature: String(created?.metadata?.lastGridAppendSignature || "").trim(),
      addedAt: new Date().toISOString(),
    });
    if (!next) {
      status = "Project entry is incomplete.";
      render();
      return;
    }
    state.projects = [next, ...state.projects];
    state.selectedProjectId = next.id;
    state.crsMode = next.crsMode;
    state.epsgCode = next.epsgCode;
    state.wktFile = next.wktFile;
    state.surveyType = next.surveyType;
    state.description = next.description;
    syncProjectSelection();
    persist();
    projectDialog = null;
    status = `Created and selected ${next.name}.`;
    render();
    refreshManagedProjectPresence();
  }

  function removeProject(projectId) {
    const target = state.projects.find((project) => project.id === projectId);
    state.projects = state.projects.filter((project) => project.id !== projectId);
    syncProjectSelection();
    persist();
    status = target ? `Removed ${target.name} from the project list.` : "Removed project from the list.";
    render();
  }

  async function updateProjectPath(projectId) {
    const target = state.projects.find((project) => project.id === projectId);
    if (!target) return;
    try {
      const picked = await postJson("/api/qimera/pick-folder", {
        description: "Select the existing Qimera project folder on the host",
        selectedPath: String(target.folder || "").trim(),
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "Project folder selection canceled.";
        render();
        return;
      }
      let managedProject = null;
      try {
        const payload = await fetchJsonWithTimeout("/api/qimera/projects", 15000);
        const managedProjects = Array.isArray(payload?.projects) ? payload.projects : [];
        managedProject = managedProjects.find((project) => String(project?.folder || "").trim().toLowerCase() === pickedPath.toLowerCase()) || null;
        syncManagedProjectPresence(managedProjects);
      } catch {
        managedProject = null;
      }
      target.folder = pickedPath;
      target.name = String(managedProject?.name || inferProjectNameFromFolder(pickedPath) || target.name).trim();
      target.crsMode = String(managedProject?.crsMode || "").trim() || (String(managedProject?.wktFile || "").trim() ? "wkt-file" : (target.crsMode || "epsg"));
      target.epsgCode = String(managedProject?.epsgCode || target.epsgCode || "").trim();
      target.wktFile = String(managedProject?.wktFile || target.wktFile || "").trim();
      target.surveyType = String(managedProject?.surveyType || target.surveyType || "").trim();
      target.description = String(managedProject?.description || target.description || "").trim();
      target.sourceFolder = String(managedProject?.sourceFolder || target.sourceFolder || "").trim();
      target.monitorSourceFolder = managedProject?.monitorSourceFolder === true;
      target.autoImportNewSources = managedProject?.autoImportNewSources === true;
      target.autoProcessNewSources = managedProject?.autoProcessNewSources === true;
      target.autoFilterSources = managedProject?.autoFilterSources === true;
      target.selectedFilterProfile = String(managedProject?.selectedFilterProfile || target.selectedFilterProfile || "").trim();
      target.sourceScanIntervalSec = normalizeSourceScanInterval(managedProject?.sourceScanIntervalSec || target.sourceScanIntervalSec || 30);
      target.selectedGridName = String(managedProject?.selectedGridName || target.selectedGridName || "").trim();
      target.autoAppendProcessedToGrid = managedProject?.autoAppendProcessedToGrid === true;
      target.lastGridAppendSignature = String(managedProject?.lastGridAppendSignature || target.lastGridAppendSignature || "").trim();
      target.missingOnHost = false;
      state.selectedProjectId = target.id;
      syncProjectSelection();
      persist();
      status = `Updated ${target.name} to use ${pickedPath}.`;
      render();
      refreshManagedProjectPresence();
    } catch (error) {
      status = error?.message || "Could not update the project path.";
      render();
    }
  }

  function selectProject(projectId) {
    stopSourceAutomationWatcher();
    stopGridAutomationWatcher();
    state.selectedProjectId = projectId;
    syncProjectSelection();
    lastSelectedSourcePath = "";
    tracklineState.error = "";
    tracklineState.lastLoadedAt = "";
    tracklineState.featureCount = 0;
    svpState = {
      loading: false,
      error: "",
      lastLoadedAt: "",
      profiles: [],
      schedule: [],
      assignments: [],
      selectedProfileNames: [],
      lastSelectedProfileName: "",
    };
    persist();
    const active = getActiveProject(state);
    status = active ? `Selected ${active.name} as the active project.` : "";
    render();
    if (active?.sourceFolder && !(active.sourceItems || []).length) {
      loadSourceFolderFiles();
    } else if (active) {
      refreshSourceStatuses();
    }
    ensureSourceAutomationWatcher();
    ensureGridAutomationWatcher();
    refreshProjectGrids();
  }

  function sourceStageProgress(item) {
    const importPercent = item.importStatus === "imported"
      ? 100
      : item.importStatus === "importing"
        ? 60
        : 0;
    const processPercent = item.processStatus === "processed"
      ? 100
      : item.processStatus === "processing"
        ? 60
        : 0;
    return { importPercent, processPercent };
  }

  function toggleSvpProfileSelection(profileName, event = {}) {
    const name = String(profileName || "").trim();
    if (!name) return;
    const profiles = Array.isArray(svpState.profiles) ? svpState.profiles : [];
    const castRows = deriveSvpCastRows(profiles, svpState.schedule);
    const castByProfile = new Map();
    castRows.forEach((row) => {
      const key = String(row.profileName || "").trim();
      if (!key) return;
      const prior = castByProfile.get(key);
      if (!prior || (Number.isFinite(row.timeMs) && (!Number.isFinite(prior.timeMs) || row.timeMs < prior.timeMs))) {
        castByProfile.set(key, row);
      }
    });
    const names = [...profiles]
      .sort((a, b) => {
        const aName = String(a?.name || "").trim();
        const bName = String(b?.name || "").trim();
        const aTime = castByProfile.get(aName)?.timeMs;
        const bTime = castByProfile.get(bName)?.timeMs;
        const aHas = Number.isFinite(aTime);
        const bHas = Number.isFinite(bTime);
        if (aHas && bHas && aTime !== bTime) return aTime - bTime;
        if (aHas !== bHas) return aHas ? -1 : 1;
        return aName.localeCompare(bName, undefined, { sensitivity: "base" });
      })
      .map((profile) => String(profile?.name || "").trim())
      .filter(Boolean);
    const targetIndex = names.findIndex((entry) => entry === name);
    if (targetIndex < 0) return;
    const current = new Set(Array.isArray(svpState.selectedProfileNames) ? svpState.selectedProfileNames : []);
    const isRange = event.shiftKey === true && svpState.lastSelectedProfileName;
    const isToggle = event.ctrlKey === true || event.metaKey === true;
    if (isRange) {
      const anchorIndex = names.findIndex((entry) => entry === svpState.lastSelectedProfileName);
      const start = Math.max(0, Math.min(anchorIndex, targetIndex));
      const end = Math.max(anchorIndex, targetIndex);
      const next = isToggle ? new Set(current) : new Set();
      for (let i = start; i <= end; i += 1) next.add(names[i]);
      svpState.selectedProfileNames = names.filter((entry) => next.has(entry));
    } else if (isToggle) {
      if (current.has(name)) current.delete(name);
      else current.add(name);
      svpState.selectedProfileNames = names.filter((entry) => current.has(entry));
    } else {
      svpState.selectedProfileNames = [name];
    }
    svpState.lastSelectedProfileName = name;
    render();
  }

  function sourceRowActivity(item) {
    if (item.gridAppendStatus === "appending") {
      return { active: true, color: "#34d399", progress: item.activityProgress || 12 };
    }
    if (item.filterStatus === "filtering") {
      return { active: true, color: "#f472b6", progress: item.activityProgress || 12 };
    }
    if (item.processStatus === "processing") {
      return { active: true, color: "#60a5fa", progress: item.activityProgress || 12 };
    }
    if (item.importStatus === "importing") {
      return { active: true, color: "#facc15", progress: item.activityProgress || 12 };
    }
    return { active: false, color: "#94a3b8", progress: 0 };
  }

  function updateSourceWorkspaceActivityUi(items = getActiveSourceItems()) {
    const hosts = [containerEl, getSourcesBottomPanelEl()].filter(Boolean);
    const itemsByPath = new Map(
      (Array.isArray(items) ? items : [])
        .map((item) => [String(item?.path || "").trim().toLowerCase(), item])
        .filter(([path]) => path),
    );
    hosts.forEach((host) => {
      host.querySelectorAll?.("[data-source-activity-path]").forEach((activityRow) => {
        const item = itemsByPath.get(String(activityRow.getAttribute("data-source-activity-path") || "").trim().toLowerCase());
        const activity = item ? sourceRowActivity(item) : { active: false, color: "#94a3b8", progress: 0 };
        activityRow.style.display = activity.active ? "" : "none";
        const fill = activityRow.querySelector("[data-source-activity-fill]");
        if (fill) {
          fill.style.width = `${activity.progress}%`;
          fill.style.background = activity.color;
        }
      });
    });
  }

  function updateSourceWorkspaceRowsUi(items = getActiveSourceItems()) {
    const hosts = [containerEl, getSourcesBottomPanelEl()].filter(Boolean);
    const itemsByPath = new Map(
      (Array.isArray(items) ? items : [])
        .map((item) => [String(item?.path || "").trim().toLowerCase(), item])
        .filter(([path]) => path),
    );
    hosts.forEach((host) => {
      host.querySelectorAll?.("[data-source-row]").forEach((row) => {
        const item = itemsByPath.get(String(row.getAttribute("data-source-row") || "").trim().toLowerCase());
        if (!item) return;
        const needsSvpReprocess = item.svpNeedsReprocess === true;
        row.style.background = item.selected ? "rgba(96,165,250,.12)" : (needsSvpReprocess ? "rgba(251,146,60,.16)" : "");
        row.style.boxShadow = needsSvpReprocess ? "inset 3px 0 0 rgba(251,146,60,.85)" : "";
        const trackInput = row.querySelector("[data-source-check]");
        if (trackInput) trackInput.checked = item.checked === true;
        const nameEl = row.querySelector("[data-source-name]");
        if (nameEl) {
          nameEl.style.color = item.selected ? "#dbeafe" : (needsSvpReprocess ? "#fed7aa" : "");
          nameEl.style.fontWeight = item.selected ? "600" : "";
        }
        const stateEl = row.querySelector("[data-source-state]");
        if (stateEl) {
          stateEl.textContent = sourceStateLabel(item);
          stateEl.style.color = sourceStateTone(item).replace("color:", "").replace(";", "").trim();
        }
        const sizeEl = row.querySelector("[data-source-size]");
        if (sizeEl) sizeEl.textContent = formatBytes(item.sizeBytes || 0);
        const importedEl = row.querySelector("[data-source-imported]");
        if (importedEl) importedEl.innerHTML = stateGlyph(item.importStatus === "imported" || item.importStatus === "importing");
        const processedEl = row.querySelector("[data-source-processed]");
        if (processedEl) processedEl.innerHTML = stateGlyph((item.processStatus === "processed" || item.processStatus === "processing") && item.svpNeedsReprocess !== true);
        const appendedEl = row.querySelector("[data-source-appended]");
        if (appendedEl) appendedEl.innerHTML = stateGlyph(item.gridAppendStatus === "appended" || item.gridAppendStatus === "appending");
        const filterEl = row.querySelector("[data-source-filter]");
        if (filterEl) filterEl.textContent = item.filterProfile || "None";
      });
    });
  }

  function getActiveSourceItems() {
    return Array.isArray(getActiveProject(state)?.sourceItems) ? getActiveProject(state).sourceItems : [];
  }

  function setActiveProjectSourceItems(items) {
    const activeProject = getActiveProject(state);
    if (!activeProject) return;
    const previousByPath = new Map(
      (Array.isArray(activeProject.sourceItems) ? activeProject.sourceItems : [])
        .map((item) => [String(item?.path || "").trim().toLowerCase(), item])
        .filter(([path]) => path),
    );
    activeProject.sourceItems = Array.isArray(items)
      ? items.map((item) => {
        const normalized = normalizeSourceItem(item);
        if (!normalized) return null;
        const prev = previousByPath.get(String(normalized.path || "").trim().toLowerCase());
        if (
          prev
          && Number(prev.sizeBytes) > 0
          && (!Number.isFinite(Number(normalized.sizeBytes)) || Number(normalized.sizeBytes) <= 0)
        ) {
          normalized.sizeBytes = Number(prev.sizeBytes);
        }
        if (
          prev
          && String(prev.modifiedAt || "").trim()
          && !String(normalized.modifiedAt || "").trim()
        ) {
          normalized.modifiedAt = String(prev.modifiedAt || "").trim();
        }
        return normalized;
      }).filter(Boolean)
      : [];
    state.additionalFiles = activeProject.sourceItems.filter((item) => item.selected).map((item) => item.path).join("\n");
    persist();
  }

  function getActiveGridItems() {
    return Array.isArray(getActiveProject(state)?.gridItems) ? getActiveProject(state).gridItems : [];
  }

  function getActiveGridSourceMembershipSet() {
    const activeProject = getActiveProject(state);
    const selectedGridName = String(activeProject?.selectedGridName || state.selectedGridName || "").trim();
    if (!activeProject || !selectedGridName) return new Set();
    const names = Array.isArray(activeProject.gridSourceMemberships?.[selectedGridName])
      ? activeProject.gridSourceMemberships[selectedGridName]
      : [];
    return new Set(names.map((name) => String(name || "").trim().toLowerCase()).filter(Boolean));
  }

  function setActiveProjectGridItems(items) {
    const activeProject = getActiveProject(state);
    if (!activeProject) return;
    activeProject.gridItems = Array.isArray(items) ? items.map(normalizeGridItem).filter(Boolean) : [];
    const selectedName = String(activeProject.selectedGridName || state.selectedGridName || "").trim();
    const hasSelected = activeProject.gridItems.some((item) => item.name.toLowerCase() === selectedName.toLowerCase());
    if (!hasSelected) {
      activeProject.selectedGridName = activeProject.gridItems[0]?.name || "";
      state.selectedGridName = activeProject.selectedGridName;
    }
    persist();
  }

  function selectGrid(gridName) {
    const activeProject = getActiveProject(state);
    if (!activeProject) return;
    const selectedGridName = String(gridName || "").trim();
    activeProject.selectedGridName = selectedGridName;
    state.selectedGridName = selectedGridName;
    syncAutoExportNames();
    persist();
    persistActiveProjectMetadata({ selectedGridName }).catch(() => {});
    render();
  }

  function syncAutoExportNames(force = false) {
    const nextBase = sanitizeExportStem(getActiveGridName(state) || state.gridName || "grid");
    const previousBase = sanitizeExportStem(state.lastAutoExportBase || "grid");
    let changed = false;

    const applyIfAuto = (currentValue, previousDefault, nextDefault) => {
      const trimmed = String(currentValue || "").trim();
      if (force || !trimmed || trimmed === previousDefault) {
        if (trimmed !== nextDefault) changed = true;
        return nextDefault;
      }
      return currentValue;
    };

    state.geotiffFilename = applyIfAuto(state.geotiffFilename, `${previousBase}.tif`, `${nextBase}.tif`);
    state.floatingGeotiffFilename = applyIfAuto(state.floatingGeotiffFilename, `${previousBase}_float.tif`, `${nextBase}_float.tif`);
    state.noaaCsvFilename = applyIfAuto(state.noaaCsvFilename, `${previousBase}.csv`, `${nextBase}.csv`);

    if (String(state.lastAutoExportBase || "") !== nextBase) {
      state.lastAutoExportBase = nextBase;
      changed = true;
    }
    return changed;
  }

  function buildProcessedGridSignature(items = getActiveSourceItems()) {
    return items
      .filter((item) => item.processStatus === "processed")
      .map((item) => String(item.name || "").trim().toLowerCase())
      .filter(Boolean)
      .sort()
      .join("|");
  }

  function mergeDiscoveredSourceItems(discoveredFiles) {
    const activeProject = getActiveProject(state);
    const nowIso = new Date().toISOString();
    const stabilityMs = Math.max(15000, normalizeSourceScanInterval(activeProject?.sourceScanIntervalSec || state.sourceScanIntervalSec || 30) * 1000);
    const previousByPath = new Map(
      getActiveSourceItems().map((item) => [String(item.path || "").trim().toLowerCase(), item]),
    );
    return (Array.isArray(discoveredFiles) ? discoveredFiles : []).map((file) => {
      const path = String(file?.path || "").trim();
      const previous = previousByPath.get(path.toLowerCase());
      const sizeBytes = Number(file?.sizeBytes || 0);
      const modifiedAt = String(file?.modifiedAt || "").trim();
      const changed = !previous
        || Number(previous.sizeBytes || 0) !== sizeBytes
        || String(previous.modifiedAt || "").trim() !== modifiedAt;
      const seenAt = String(previous?.seenAt || nowIso).trim() || nowIso;
      const lastChangedAt = changed ? nowIso : String(previous?.lastChangedAt || seenAt || nowIso).trim();
      const stableForMs = Math.max(0, Date.now() - new Date(lastChangedAt).getTime());
      return normalizeSourceItem({
        ...previous,
        path,
        name: file?.name,
        sizeBytes,
        modifiedAt,
        seenAt,
        lastObservedAt: nowIso,
        lastChangedAt,
        readyState: stableForMs >= stabilityMs ? "ready" : "building",
        selected: previous ? previous.selected === true : false,
        checked: previous ? previous.checked === true : false,
      });
    }).filter(Boolean);
  }

  function stopSourceActivityAnimation() {
    if (sourceActivityTimer) {
      clearInterval(sourceActivityTimer);
      sourceActivityTimer = null;
    }
  }

  function startSourceActivityAnimation(itemPath, stage) {
    stopSourceActivityAnimation();
    const startedAt = performance.now();
    sourceActivityTimer = setInterval(() => {
      const elapsedMs = performance.now() - startedAt;
      const next = getActiveSourceItems().map((item) => {
        const pathMatches = String(item.path || "").trim().toLowerCase() === String(itemPath || "").trim().toLowerCase();
        const appliesToItem = stage === "grid" ? item.gridAppendStatus === "appending" : pathMatches;
        if (!appliesToItem) {
          return item;
        }
        const isActive = stage === "import"
          ? item.importStatus === "importing"
          : stage === "process"
            ? item.processStatus === "processing"
            : stage === "grid"
              ? item.gridAppendStatus === "appending"
              : item.filterStatus === "filtering";
        if (!isActive) return item;
        const current = Number(item.activityProgress || 0);
        const target = 95;
        const minimumVisible = Math.min(target, 8 + (elapsedMs / 1000) * 18);
        const eased = current + Math.max(0.18, (target - current) * 0.045);
        return normalizeSourceItem({
          ...item,
          activityProgress: Math.min(target, Math.max(minimumVisible, eased)),
        });
      }).filter(Boolean);
      setActiveProjectSourceItems(next);
      updateSourceWorkspaceActivityUi(next);
    }, 60);
  }

  function updateSourceItemsFromStatus(importedNames, processedNames, sourceFilters) {
    const activeProject = getActiveProject(state);
    const sourceFolder = String(activeProject?.sourceFolder || "").trim();
    const imported = new Set((importedNames || []).map((item) => String(item || "").trim().toLowerCase()));
    const processed = new Set((processedNames || []).map((item) => String(item || "").trim().toLowerCase()));
    const gridMembership = getActiveGridSourceMembershipSet();
    const filtersByName = new Map(
      Object.entries(sourceFilters && typeof sourceFilters === "object" ? sourceFilters : {})
        .map(([name, profile]) => [String(name || "").trim().toLowerCase(), String(profile || "").trim()])
        .filter(([name, profile]) => name && profile),
    );
    const existingItems = getActiveSourceItems();
    const itemByName = new Map(
      existingItems
        .map((item) => [String(item?.name || item?.path?.split?.(/[\\/]/).pop() || "").trim().toLowerCase(), item])
        .filter(([name]) => name),
    );
    const knownNames = new Set([
      ...Array.from(itemByName.keys()),
      ...Array.from(imported),
      ...Array.from(processed),
      ...Array.from(filtersByName.keys()),
      ...Array.from(gridMembership),
    ]);
    const appendOnlyNames = Array.from(knownNames).filter((name) => !itemByName.has(name))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    const placeholderItems = appendOnlyNames.map((name) => normalizeSourceItem({
      path: sourceFolder ? joinPathParts(sourceFolder, name) : name,
      name,
      selected: false,
      checked: false,
      readyState: "ready",
      importStatus: "pending",
      processStatus: "pending",
      filterStatus: "pending",
      gridAppendStatus: "pending",
      seenAt: new Date().toISOString(),
      lastObservedAt: new Date().toISOString(),
    })).filter(Boolean);
    const mergedItems = [...existingItems, ...placeholderItems];
    const next = mergedItems.map((item) => {
      const nameKey = String(item.name || item.path.split(/[\\/]/).pop() || "").trim().toLowerCase();
      const isImported = imported.has(nameKey);
      const isProcessed = processed.has(nameKey);
      const isInGrid = gridMembership.has(nameKey);
      const filterProfile = filtersByName.get(nameKey) || "";
      const becameProcessed = isProcessed && item.processStatus !== "processed";
      const svpNeedsReprocess = item.svpNeedsReprocess === true && isImported;
      return normalizeSourceItem({
        ...item,
        importStatus: isImported ? "imported" : (item.importStatus === "importing" ? "pending" : "pending"),
        processStatus: svpNeedsReprocess ? "pending" : (isProcessed ? "processed" : (isImported ? "pending" : "pending")),
        gridAppendStatus: item.gridAppendStatus === "appending" ? "pending" : (isInGrid ? "appended" : "pending"),
        filterProfile,
        filterStatus: item.filterStatus === "filtering" ? "pending" : (filterProfile ? "applied" : "pending"),
        activityProgress: (isImported || isProcessed) ? 0 : item.activityProgress,
        checked: becameProcessed ? true : item.checked === true,
        svpNeedsReprocess,
        svpAssignedProfile: String(item.svpAssignedProfile || "").trim(),
      });
    }).filter(Boolean);
    setActiveProjectSourceItems(next);
  }

  async function hydrateSourceItemsFromProjectXml(activeProject) {
    const payload = await postJson("/api/qimera/project-source-items", {
      projectFolder: activeProject.folder,
    }, 30000);
    const projectSourceItems = Array.isArray(payload?.projectSourceItems)
      ? payload.projectSourceItems
        .map((item) => ({
          name: String(item?.name || "").trim(),
          path: String(item?.path || "").trim(),
          sizeBytes: Number.isFinite(Number(item?.sizeBytes)) ? Number(item.sizeBytes) : 0,
          modifiedAt: String(item?.modifiedAt || "").trim(),
        }))
        .filter((item) => item.name && item.path)
      : [];
    if (String(payload?.sourceFolder || "").trim()) {
      activeProject.sourceFolder = String(payload.sourceFolder).trim();
      state.inputPath = activeProject.sourceFolder;
    }
    if (projectSourceItems.length) {
      setActiveProjectSourceItems(mergeDiscoveredSourceItems(projectSourceItems));
    }
    updateSourceItemsFromStatus(payload?.importedSourceFiles || [], payload?.processedSourceFiles || [], {});
    sourceDiagnostics = {
      lastCheckedAt: new Date().toISOString(),
      mode: "project-xml-fallback",
      sourceStatusCount: sourceDiagnostics.sourceStatusCount || 0,
      projectXmlCount: projectSourceItems.length,
      importedCount: Array.isArray(payload?.importedSourceFiles) ? payload.importedSourceFiles.length : 0,
      processedCount: Array.isArray(payload?.processedSourceFiles) ? payload.processedSourceFiles.length : 0,
      lastError: "",
    };
    return projectSourceItems.length;
  }

  async function refreshSourceStatuses(options = {}) {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) return;
    try {
      const payload = await postJson("/api/qimera/source-status", {
        projectFolder: activeProject.folder,
      }, 30000);
      const projectSourceItems = Array.isArray(payload?.projectSourceItems)
        ? payload.projectSourceItems
          .map((item) => ({
            name: String(item?.name || "").trim(),
            path: String(item?.path || "").trim(),
            sizeBytes: Number.isFinite(Number(item?.sizeBytes)) ? Number(item.sizeBytes) : 0,
            modifiedAt: String(item?.modifiedAt || "").trim(),
          }))
          .filter((item) => item.name && item.path)
        : [];
      if (String(payload?.sourceFolder || "").trim()) {
        activeProject.sourceFolder = String(payload.sourceFolder).trim();
        state.inputPath = activeProject.sourceFolder;
      }
      if (projectSourceItems.length) {
        const merged = mergeDiscoveredSourceItems(projectSourceItems);
        setActiveProjectSourceItems(merged);
      }
      activeProject.gridSourceMemberships = payload?.gridSourceMemberships && typeof payload.gridSourceMemberships === "object"
        ? payload.gridSourceMemberships
        : (activeProject.gridSourceMemberships || {});
      updateSourceItemsFromStatus(payload?.importedSourceFiles || [], payload?.processedSourceFiles || [], payload?.sourceFilters || {});
      sourceDiagnostics = {
        lastCheckedAt: new Date().toISOString(),
        mode: "source-status",
        sourceStatusCount: Array.isArray(payload?.importedSourceFiles) ? payload.importedSourceFiles.length : 0,
        projectXmlCount: projectSourceItems.length,
        importedCount: Array.isArray(payload?.importedSourceFiles) ? payload.importedSourceFiles.length : 0,
        processedCount: Array.isArray(payload?.processedSourceFiles) ? payload.processedSourceFiles.length : 0,
        lastError: String(payload?.statusCommandError || "").trim(),
      };
      if (!getActiveSourceItems().length) {
        await hydrateSourceItemsFromProjectXml(activeProject);
      }
      if (String(payload?.statusCommandError || "").trim()) {
        status = "Loaded source list from project metadata because host source-status command failed.";
      }
      if (getActiveSourceItems().some((item) => item.checked)) {
        loadProjectTracklines({ silent: true }).catch(() => {});
      }
      if (options.renderUi === false) {
        updateSourceWorkspaceRowsUi();
        updateSourceWorkspaceActivityUi();
      } else {
        render();
      }
    } catch (error) {
      try {
        const hydratedCount = await hydrateSourceItemsFromProjectXml(activeProject);
        if (options.renderUi !== false) {
          status = hydratedCount
            ? "Loaded source list from project XML fallback."
            : (error?.message || "Could not refresh source status from the host.");
          render();
        }
      } catch {
        sourceDiagnostics = {
          ...sourceDiagnostics,
          lastCheckedAt: new Date().toISOString(),
          mode: "source-status-error",
          lastError: error?.message || "Could not refresh source status from the host.",
        };
        if (options.renderUi !== false) {
          status = error?.message || "Could not refresh source status from the host.";
          render();
        }
      }
    }
  }

  async function updateAutomationSettingsFromForm() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder || !containerEl) return;
    const monitorSourceFolder = !!containerEl.querySelector("#qimera-monitor-source-folder")?.checked;
    const autoImportNewSources = monitorSourceFolder && !!containerEl.querySelector("#qimera-auto-import-sources")?.checked;
    const autoProcessNewSources = autoImportNewSources && !!containerEl.querySelector("#qimera-auto-process-sources")?.checked;
    const selectedFilterProfile = String(containerEl.querySelector("#qimera-filter-profile")?.value || activeProject.selectedFilterProfile || state.selectedFilterProfile || "").trim();
    const autoFilterSources = monitorSourceFolder && !!selectedFilterProfile && !!containerEl.querySelector("#qimera-auto-filter-sources")?.checked;
    const sourceScanIntervalSec = normalizeSourceScanInterval(containerEl.querySelector("#qimera-source-scan-interval")?.value || activeProject.sourceScanIntervalSec || state.sourceScanIntervalSec || 30);

    state.monitorSourceFolder = monitorSourceFolder;
    state.autoImportNewSources = autoImportNewSources;
    state.autoProcessNewSources = autoProcessNewSources;
    state.autoFilterSources = autoFilterSources;
    state.selectedFilterProfile = selectedFilterProfile;
    state.sourceScanIntervalSec = sourceScanIntervalSec;
    activeProject.monitorSourceFolder = monitorSourceFolder;
    activeProject.autoImportNewSources = autoImportNewSources;
    activeProject.autoProcessNewSources = autoProcessNewSources;
    activeProject.autoFilterSources = autoFilterSources;
    activeProject.selectedFilterProfile = selectedFilterProfile;
    activeProject.sourceScanIntervalSec = sourceScanIntervalSec;

    if (!monitorSourceFolder) {
      activeProject.autoImportNewSources = false;
      activeProject.autoProcessNewSources = false;
      state.autoImportNewSources = false;
      state.autoProcessNewSources = false;
    }
    if (!activeProject.autoImportNewSources) {
      activeProject.autoProcessNewSources = false;
      state.autoProcessNewSources = false;
    }
    if (!selectedFilterProfile) {
      activeProject.autoFilterSources = false;
      state.autoFilterSources = false;
    }
    persist();
    render();
    try {
      await persistActiveProjectMetadata({
        sourceFolder: activeProject.sourceFolder,
        monitorSourceFolder: activeProject.monitorSourceFolder === true,
        autoImportNewSources: activeProject.autoImportNewSources === true,
        autoProcessNewSources: activeProject.autoProcessNewSources === true,
        autoFilterSources: activeProject.autoFilterSources === true,
        selectedFilterProfile: String(activeProject.selectedFilterProfile || "").trim(),
        sourceScanIntervalSec: normalizeSourceScanInterval(activeProject.sourceScanIntervalSec || 30),
      });
    } catch (error) {
      status = error?.message || "Could not save source automation settings.";
    }
    ensureSourceAutomationWatcher();
    render();
  }

  function scheduleNextSourceAutomationScan() {
    stopSourceAutomationWatcher();
    const activeProject = getActiveProject(state);
    const settings = getActiveAutomationSettings();
    if (!activeProject?.folder || !activeProject.sourceFolder || !settings.monitorSourceFolder) return;
    sourceAutomationTimer = setTimeout(() => {
      runSourceAutomationScan();
    }, settings.sourceScanIntervalSec * 1000);
  }

  function ensureSourceAutomationWatcher() {
    const activeProject = getActiveProject(state);
    const settings = getActiveAutomationSettings();
    if (!activeProject?.folder || !activeProject.sourceFolder || !settings.monitorSourceFolder) {
      stopSourceAutomationWatcher();
      if (!settings.monitorSourceFolder) {
        sourceAutomation.message = "";
        sourceAutomation.error = "";
      }
      render();
      return;
    }
    scheduleNextSourceAutomationScan();
    render();
  }

  async function chooseSourceFolder() {
    const activeProject = getActiveProject(state);
    if (!activeProject) {
      status = "Select a project before choosing a source folder.";
      render();
      return;
    }
    try {
      const picked = await postJson("/api/qimera/pick-folder", {
        description: "Select the source folder on the host",
        selectedPath: String(activeProject.sourceFolder || state.inputPath || "").trim(),
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "Source folder selection canceled.";
        render();
        return;
      }
      activeProject.sourceFolder = pickedPath;
      state.inputPath = pickedPath;
      persist();
      try {
        await persistActiveProjectMetadata({ sourceFolder: pickedPath });
      } catch {}
      await loadSourceFolderFiles();
    } catch (error) {
      status = error?.message || "Could not choose the source folder.";
      render();
    }
  }

  async function chooseSbetFile() {
    const activeProject = getActiveProject(state);
    if (!activeProject) {
      status = "Select a project before choosing an SBET file.";
      render();
      return;
    }
    try {
      const picked = await postJson("/api/qimera/pick-file", {
        title: "Select the POSPac SBET file on the host",
        selectedPath: String(activeProject.sbetFile || state.sbetFile || "").trim(),
        filter: "POSPac SBET (*.out;*.sbet)|*.out;*.sbet|All files (*.*)|*.*",
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "SBET file selection canceled.";
        render();
        return;
      }
      activeProject.sbetFile = pickedPath;
      state.sbetFile = pickedPath;
      await inspectSbetImport({ persistMetadata: true });
    } catch (error) {
      status = error?.message || "Could not choose the SBET file.";
      render();
    }
  }

  async function chooseSbetRmsFile() {
    const activeProject = getActiveProject(state);
    if (!activeProject) {
      status = "Select a project before choosing an RMS file.";
      render();
      return;
    }
    try {
      const picked = await postJson("/api/qimera/pick-file", {
        title: "Select the matching POSPac RMS file on the host",
        selectedPath: String(activeProject.sbetRmsFile || state.sbetRmsFile || activeProject.sbetFile || "").trim(),
        filter: "POSPac RMS (*.out;*.smrmsg)|*.out;*.smrmsg|All files (*.*)|*.*",
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "RMS file selection canceled.";
        render();
        return;
      }
      activeProject.sbetRmsFile = pickedPath;
      state.sbetRmsFile = pickedPath;
      await inspectSbetImport({ persistMetadata: true });
    } catch (error) {
      status = error?.message || "Could not choose the RMS file.";
      render();
    }
  }

  async function inspectSbetImport(options = {}) {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before validating SBET files.";
      render();
      return null;
    }
    sbetBusy.validating = true;
    render();
    try {
      const result = await postJson("/api/qimera/inspect-binary-navigation", {
        projectFolder: activeProject.folder,
        sbetFile: String(activeProject.sbetFile || state.sbetFile || "").trim(),
        sbetRmsFile: String(activeProject.sbetRmsFile || state.sbetRmsFile || "").trim(),
      }, 60000);
      const details = result?.details && typeof result.details === "object" ? result.details : {};
      activeProject.sbetFile = String(details.sbetFile || activeProject.sbetFile || "").trim();
      activeProject.sbetRmsFile = String(details.sbetRmsFile || activeProject.sbetRmsFile || "").trim();
      state.sbetFile = activeProject.sbetFile;
      state.sbetRmsFile = activeProject.sbetRmsFile;
      sbetStatus = {
        ok: result?.ok !== false,
        message: String(result?.message || "SBET inspection complete.").trim(),
        details,
        checkedAt: new Date().toISOString(),
      };
      if (options?.persistMetadata !== false) {
        await persistActiveProjectMetadata({
          sbetFile: activeProject.sbetFile,
          sbetRmsFile: activeProject.sbetRmsFile,
          sbetGpsWeek: String(activeProject.sbetGpsWeek || state.sbetGpsWeek || "").trim(),
          sbetCoordinateSystem: String(activeProject.sbetCoordinateSystem || state.sbetCoordinateSystem || "").trim(),
          sbetPriority: activeProject.sbetPriority !== false,
        });
      }
      status = sbetStatus.message;
      return result;
    } catch (error) {
      sbetStatus = {
        ok: false,
        message: error?.message || "SBET inspection failed.",
        details: null,
        checkedAt: new Date().toISOString(),
      };
      status = sbetStatus.message;
      return null;
    } finally {
      sbetBusy.validating = false;
      render();
    }
  }

  async function launchQimeraForSbetImport() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before launching Qimera.";
      render();
      return;
    }
    sbetBusy.launching = true;
    render();
    try {
      await persistActiveProjectMetadata({
        sbetFile: String(activeProject.sbetFile || state.sbetFile || "").trim(),
        sbetRmsFile: String(activeProject.sbetRmsFile || state.sbetRmsFile || "").trim(),
        sbetGpsWeek: String(activeProject.sbetGpsWeek || state.sbetGpsWeek || "").trim(),
        sbetCoordinateSystem: String(activeProject.sbetCoordinateSystem || state.sbetCoordinateSystem || "").trim(),
        sbetPriority: activeProject.sbetPriority !== false,
      });
      const result = await postJson("/api/qimera/launch-qimera", {
        projectFolder: activeProject.folder,
      }, 60000);
      status = String(result?.message || `Opened ${activeProject.name} in Qimera.`).trim();
    } catch (error) {
      status = error?.message || "Could not launch Qimera.";
    } finally {
      sbetBusy.launching = false;
      render();
    }
  }

  async function refreshProjectSvp(options = {}) {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before loading SVP profiles.";
      render();
      return;
    }
    const strategyChanged = options?.strategyChanged === true;
    const previousAssignmentMap = options?.previousAssignmentMap instanceof Map
      ? options.previousAssignmentMap
      : buildSvpAssignmentBySource(svpState.assignments);
    svpState.loading = true;
    svpState.error = "";
    render();
    try {
      const strategyPayload = buildSvpStrategyPayload();
      const result = await postJson("/api/qimera/svp", {
        projectFolder: activeProject.folder,
        svpAssignment: strategyPayload,
      }, 120000);
      const profiles = Array.isArray(result?.profiles) ? result.profiles : [];
      const schedule = Array.isArray(result?.schedule) ? result.schedule : [];
      const assignments = Array.isArray(result?.assignments) ? result.assignments : [];
      const nextAssignmentMap = buildSvpAssignmentBySource(assignments);
      const nextSelectedNames = Array.isArray(svpState.selectedProfileNames)
        ? svpState.selectedProfileNames.filter((name) => profiles.some((profile) => profile?.name === name))
        : [];
      const selectedProfileNames = nextSelectedNames.length
        ? nextSelectedNames
        : (profiles[0]?.name ? [String(profiles[0].name)] : []);
      svpState = {
        loading: false,
        error: "",
        lastLoadedAt: new Date().toISOString(),
        profiles,
        schedule,
        assignments,
        selectedProfileNames,
        lastSelectedProfileName: String(selectedProfileNames[selectedProfileNames.length - 1] || ""),
      };
      const assignmentKeys = new Set(nextAssignmentMap.keys());
      if (strategyChanged) {
        const changedSourceKeys = new Set();
        const combinedKeys = new Set([...previousAssignmentMap.keys(), ...nextAssignmentMap.keys()]);
        combinedKeys.forEach((key) => {
          const previousProfile = String(previousAssignmentMap.get(key) || "").trim();
          const nextProfile = String(nextAssignmentMap.get(key) || "").trim();
          if (previousProfile !== nextProfile) changedSourceKeys.add(key);
        });
        assignmentKeys.forEach((key) => changedSourceKeys.add(key));
        // Strategy changes are treated as globally impactful: mark all imported lines.
        getActiveSourceItems()
          .filter((item) => item.importStatus === "imported")
          .forEach((item) => changedSourceKeys.add(normalizeSourceNameKey(item.name || item.path)));
        const markedCount = markSourcesNeedingSvpReprocess(changedSourceKeys, nextAssignmentMap);
        status = profiles.length
          ? `Loaded ${profiles.length} SVP profile${profiles.length === 1 ? "" : "s"} from ${activeProject.name}. Marked ${markedCount} line${markedCount === 1 ? "" : "s"} for SVP reprocess.`
          : `No resolved SVP profiles were found in ${activeProject.name}.`;
      } else {
        const assignmentSyncItems = getActiveSourceItems().map((item) => {
          const sourceKey = normalizeSourceNameKey(item.name || item.path.split(/[\\/]/).pop() || "");
          const nextAssignedProfile = String(nextAssignmentMap.get(sourceKey) || "").trim();
          if (item.svpAssignedProfile === nextAssignedProfile) return item;
          return normalizeSourceItem({
            ...item,
            svpAssignedProfile: nextAssignedProfile,
          });
        }).filter(Boolean);
        setActiveProjectSourceItems(assignmentSyncItems);
        status = profiles.length
          ? `Loaded ${profiles.length} SVP profile${profiles.length === 1 ? "" : "s"} from ${activeProject.name}.`
          : `No resolved SVP profiles were found in ${activeProject.name}.`;
      }
    } catch (error) {
      svpState.loading = false;
      svpState.error = error?.message || "Could not load project SVP profiles.";
      status = svpState.error;
    } finally {
      render();
    }
  }

  async function applySvpStrategyFromForm() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder || !containerEl) return;
    const strategy = String(containerEl.querySelector("#qimera-svp-strategy")?.value || "nearest-time").trim() || "nearest-time";
    const maxTimeHours = String(containerEl.querySelector("#qimera-svp-max-time-hours")?.value || "").trim();
    const maxDistanceNm = String(containerEl.querySelector("#qimera-svp-max-distance-nm")?.value || "").trim();
    const previousStrategy = String(activeProject.svpAssignmentStrategy || state.svpAssignmentStrategy || "nearest-time").trim() || "nearest-time";
    const previousMaxTimeHours = String(activeProject.svpMaxTimeHours ?? state.svpMaxTimeHours ?? "").trim();
    const previousMaxDistanceNm = String(activeProject.svpMaxDistanceNm ?? state.svpMaxDistanceNm ?? "").trim();
    const strategyChanged = (
      strategy !== previousStrategy
      || maxTimeHours !== previousMaxTimeHours
      || maxDistanceNm !== previousMaxDistanceNm
    );
    state.svpAssignmentStrategy = strategy;
    state.svpMaxTimeHours = maxTimeHours;
    state.svpMaxDistanceNm = maxDistanceNm;
    activeProject.svpAssignmentStrategy = strategy;
    activeProject.svpMaxTimeHours = maxTimeHours;
    activeProject.svpMaxDistanceNm = maxDistanceNm;
    persist();
    if (!strategyChanged) {
      status = "SVP strategy settings are unchanged.";
      render();
      return;
    }
    const previousAssignmentMap = buildSvpAssignmentBySource(svpState.assignments);
    try {
      await persistActiveProjectMetadata({
        svpAssignmentStrategy: strategy,
        svpMaxTimeHours: maxTimeHours,
        svpMaxDistanceNm: maxDistanceNm,
      });
    } catch {
      // Keep local values even if host metadata persistence is unavailable.
    }
    await refreshProjectSvp({
      strategyChanged: true,
      previousAssignmentMap,
    });
  }

  async function refreshProjectMetrics() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before calculating metrics.";
      render();
      return;
    }
    metricsState.loading = true;
    metricsState.error = "";
    render();
    try {
      const sourceItems = getActiveSourceItems();
      const sourceNames = sourceItems.map((item) => String(item.name || "").trim()).filter(Boolean);
      const result = await postJson("/api/qimera/tracklines", {
        projectFolder: activeProject.folder,
        sourceNames,
      }, 120000);
      const featureCollection = result?.featureCollection;
      const features = Array.isArray(featureCollection?.features) ? featureCollection.features : [];
      const byName = new Map();
      features.forEach((feature) => {
        const props = feature?.properties || {};
        const sourceName = String(props.source_name || "").trim();
        const coords = feature?.geometry?.type === "LineString" ? feature.geometry.coordinates : [];
        if (!sourceName || !Array.isArray(coords) || !coords.length) return;
        byName.set(sourceName.toLowerCase(), {
          sourceName,
          points: coords.length,
          trackDistanceNm: lineDistanceNm(coords),
        });
      });
      const rows = sourceItems.map((item) => {
        const name = String(item.name || "").trim();
        const track = byName.get(name.toLowerCase());
        return {
          name,
          state: sourceStateLabel(item),
          imported: item.importStatus === "imported",
          processed: item.processStatus === "processed" && item.svpNeedsReprocess !== true,
          appended: item.gridAppendStatus === "appended",
          filtered: !!String(item.filterProfile || "").trim(),
          distanceNm: Number(track?.trackDistanceNm || 0),
          points: Number(track?.points || 0),
          sizeBytes: Number(item.sizeBytes || 0),
        };
      });
      const summary = {
        totalLines: rows.length,
        importedLines: rows.filter((row) => row.imported).length,
        processedLines: rows.filter((row) => row.processed).length,
        appendedLines: rows.filter((row) => row.appended).length,
        filteredLines: rows.filter((row) => row.filtered).length,
        totalMileageNm: rows.reduce((sum, row) => sum + (Number(row.distanceNm) || 0), 0),
        processedMileageNm: rows.filter((row) => row.processed).reduce((sum, row) => sum + (Number(row.distanceNm) || 0), 0),
      };
      metricsState.rows = rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      metricsState.summary = summary;
      metricsState.lastCalculatedAt = new Date().toISOString();
      status = `Calculated metrics for ${rows.length} line${rows.length === 1 ? "" : "s"}.`;
    } catch (error) {
      metricsState.error = error?.message || "Could not calculate project metrics.";
      status = metricsState.error;
    } finally {
      metricsState.loading = false;
      render();
    }
  }

  async function chooseExportFolder() {
    try {
      const picked = await postJson("/api/qimera/pick-folder", {
        description: "Select the export folder on the host",
        selectedPath: String(state.exportFolder || "").trim(),
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "Export folder selection canceled.";
        render();
        return;
      }
      state.exportFolder = pickedPath;
      persist();
      render();
    } catch (error) {
      status = error?.message || "Could not choose the export folder.";
      render();
    }
  }

  async function chooseBagXmlFile() {
    try {
      const picked = await postJson("/api/qimera/pick-file", {
        title: "Select the BAG metadata XML on the host",
        selectedPath: String(state.bagXmlMetadata || "").trim(),
        filter: "XML files (*.xml)|*.xml|All files (*.*)|*.*",
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "BAG XML selection canceled.";
        render();
        return;
      }
      state.bagXmlMetadata = pickedPath;
      persist();
      render();
    } catch (error) {
      status = error?.message || "Could not choose the BAG XML metadata file.";
      render();
    }
  }

  async function runSelectedExports() {
    pullStateFromForm();
    const error = validateExportPlan(state);
    if (error) {
      exportExecution.error = error;
      exportExecution.message = "";
      render();
      return;
    }
    const activeProject = getActiveProject(state);
    const payload = {
      projectFolder: activeProject?.folder || "",
      gridName: getActiveGridName(state),
      exportFolder: String(state.exportFolder || "").trim(),
      selectedSourceFileNames: getSelectedSourceExportNames(state),
      geotiffDepthLayer: state.geotiffDepthLayer,
      includeGenerateThuTvuLayers: state.includeGenerateThuTvuLayers,
      includeExportGeotiff: state.includeExportGeotiff,
      geotiffFilename: state.geotiffFilename,
      geotiffWorldFile: state.geotiffWorldFile,
      geotiffOrigin: state.geotiffOrigin,
      includeExportFloatingPointGeotiff: state.includeExportFloatingPointGeotiff,
      floatingGeotiffFilename: state.floatingGeotiffFilename,
      floatingGeotiffNoDataValue: state.floatingGeotiffNoDataValue,
      includeExportGridAscii: state.includeExportGridAscii,
      asciiBounds: state.asciiBounds,
      asciiZPrecision: state.asciiZPrecision,
      asciiColumnSeparator: state.asciiColumnSeparator,
      includeExportBag: state.includeExportBag,
      bagVersion: state.bagVersion,
      bagOutputMode: state.bagOutputMode,
      bagScalarPath: state.bagScalarPath,
      bagDate: state.bagDate,
      bagUseXmlDate: state.bagUseXmlDate,
      bagCompression: state.bagCompression,
      bagXmlMetadata: state.bagXmlMetadata,
      includeExportGsf: state.includeExportGsf,
      gsfFilenamePattern: state.gsfFilenamePattern,
      gsfReplaceWhitespace: state.gsfReplaceWhitespace,
      gsfExportSystemsIndividually: state.gsfExportSystemsIndividually,
      gsfSpecificSystem: state.gsfSpecificSystem,
      gsfExcludeUserAdditionalSoundings: state.gsfExcludeUserAdditionalSoundings,
      gsfExcludeRawBackscatterImagery: state.gsfExcludeRawBackscatterImagery,
      gsfDoNotFlagFilteredAsManualEdits: state.gsfDoNotFlagFilteredAsManualEdits,
      gsfFlagAllInterpolatedAsAccepted: state.gsfFlagAllInterpolatedAsAccepted,
      includeExportNoaaCsv: state.includeExportNoaaCsv,
      noaaCsvFilename: state.noaaCsvFilename,
    };
    exportBusy.running = true;
    exportExecution.error = "";
    exportExecution.message = "Running selected exports on the host...";
    render();
    try {
      const result = await postJson("/api/qimera/run-exports", payload, 1000 * 60 * 60);
      exportExecution.lastRunAt = new Date().toISOString();
      exportExecution.results = Array.isArray(result?.results) ? result.results : [];
      exportExecution.error = "";
      autoLoadExportedTiffs(exportExecution.results);
      const failed = exportExecution.results.find((item) => item?.ok === false);
      exportExecution.message = failed
        ? `${failed.label || "An export"} failed. Review the results below.`
        : `Completed ${exportExecution.results.length} export${exportExecution.results.length === 1 ? "" : "s"} on the host.`;
      status = exportExecution.message;
    } catch (err) {
      exportExecution.lastRunAt = new Date().toISOString();
      exportExecution.results = [];
      exportExecution.error = err?.message || "Could not run the selected exports.";
      exportExecution.message = "";
      status = exportExecution.error;
    } finally {
      exportBusy.running = false;
      render();
    }
  }

  async function runAutoGeotiffExportForActiveGrid() {
    pullStateFromForm();
    const activeProject = getActiveProject(state);
    const gridName = getActiveGridName(state);
    if (!activeProject?.folder || !gridName) return { ok: false, skipped: true, message: "No active project/grid." };
    if (!String(state.geotiffFilename || "").trim()) return { ok: false, skipped: true, message: "GeoTIFF filename is required." };
    const resolvedExportFolder = resolveProjectExportFolder(activeProject, state.exportFolder, state.projects);
    if (resolvedExportFolder !== String(state.exportFolder || "").trim()) {
      state.exportFolder = resolvedExportFolder;
      persist();
    }
    exportBusy.running = true;
    exportExecution.error = "";
    exportExecution.message = "Auto-exporting live GeoTIFF on the host...";
    render();
    try {
      const result = await postJson("/api/qimera/run-exports", {
        projectFolder: activeProject.folder,
        gridName,
        exportFolder: resolvedExportFolder,
        geotiffDepthLayer: state.geotiffDepthLayer,
        includeExportGeotiff: true,
        geotiffFilename: state.geotiffFilename,
        geotiffWorldFile: state.geotiffWorldFile,
        geotiffOrigin: state.geotiffOrigin,
        overwriteOutputs: true,
      }, 1000 * 60 * 60);
      const results = Array.isArray(result?.results) ? result.results : [];
      exportExecution.lastRunAt = new Date().toISOString();
      exportExecution.results = results;
      const failed = results.find((item) => item?.ok === false);
      if (failed) {
        exportExecution.error = String(failed.error || failed.stderr || failed.stdout || `${failed.label || "GeoTIFF export"} failed.`).trim();
        exportExecution.message = "Auto-export failed. Review the results below.";
        throw new Error(String(failed.error || failed.stderr || failed.stdout || `${failed.label || "GeoTIFF export"} failed.`).trim());
      }
      exportExecution.error = "";
      exportExecution.message = `Auto-exported ${results.length} host export${results.length === 1 ? "" : "s"}.`;
      return { ok: true, results };
    } finally {
      exportBusy.running = false;
      render();
    }
  }

  function queueAutoLoadExportedTiffs(results) {
    const payload = Array.isArray(results) ? results : [];
    setTimeout(() => {
      try {
        autoLoadExportedTiffs(payload);
      } catch {}
    }, 50);
  }

  function preserveMapViewDuring(callback) {
    const map = app?.map?.instance;
    if (!map || typeof callback !== "function") {
      callback?.();
      return;
    }
    const center = map.getCenter?.();
    const zoom = map.getZoom?.();
    callback();
    const restore = () => {
      try {
        if (center && Number.isFinite(zoom)) {
          map.setView(center, zoom, { animate: false });
        }
      } catch {}
    };
    restore();
    requestAnimationFrame(restore);
    setTimeout(restore, 0);
    setTimeout(restore, 100);
  }

  function autoLoadExportedTiffs(results) {
    if (!app?.services?.layers || state.autoLoadExportedTiffs !== true) return;
    const items = Array.isArray(results) ? results : [];
    const layers = app.services.layers.listLayers?.() || [];
    items
      .filter((item) => item?.ok === true)
      .filter((item) => ["export-geotiff", "export-grid-floating-point-geotiff"].includes(String(item.key || "")))
      .forEach((item) => {
        const outputPath = String(item.outputPath || "").trim();
        if (!outputPath) return;
        const filename = outputPath.split(/[\\/]/).pop() || "overlay.tif";
        const url = buildHostedQimeraFileUrl(outputPath);
        const existing = layers.find((layer) => String(layer?.meta?.qimeraExportPath || "").trim().toLowerCase() === outputPath.toLowerCase());
        const nextLayer = {
          id: existing?.id || `qimera-export-${Math.random().toString(16).slice(2, 8)}`,
          name: `GeoTIFF: ${filename}`,
          kind: "overlay",
          type: "geotiff-url",
          url,
          options: {
            resolution: 256,
            projectionCode: String(item?.projectProjection?.proj4Code || "").trim(),
            projectionDef: String(item?.projectProjection?.proj4Def || "").trim(),
          },
          opacity: Number.isFinite(Number(existing?.opacity)) ? Number(existing.opacity) : 0.85,
          visible: existing?.visible !== false,
          volatile: false,
          meta: {
            ...(existing?.meta || {}),
            sourceType: "qimera-export",
            qimeraExportPath: outputPath,
            qimeraExportJob: String(item.key || "").trim(),
          },
        };
        if (existing?.id) {
          preserveMapViewDuring(() => {
            app.services.layers.removeLayer(existing.id);
            app.services.layers.addLayer("qimera-command", nextLayer);
          });
          return;
        }
        preserveMapViewDuring(() => {
          app.services.layers.addLayer("qimera-command", nextLayer);
        });
      });
  }

  function getTracklineSelectionNames(activeProject) {
    const items = Array.isArray(activeProject?.sourceItems) ? activeProject.sourceItems : [];
    const selected = items
      .filter((item) => item?.checked)
      .map((item) => String(item?.name || "").trim())
      .filter(Boolean);
    return selected;
  }

  function getTracklineStyle(activeProject, feature) {
    const style = activeProject?.tracklineStyle && typeof activeProject.tracklineStyle === "object"
      ? activeProject.tracklineStyle
      : {};
    const colorMode = String(style.colorMode || "solid").trim().toLowerCase();
    const lineType = String(style.lineType || "solid").trim().toLowerCase();
    const dashArray = lineType === "dotted" ? "2 8" : lineType === "dashed" ? "10 8" : null;
    return {
      color: String(style.color || "#2dd4bf").trim() || "#2dd4bf",
      colorMode,
      weight: Math.max(1, Math.min(5, Number(style.weight) || 3)),
      opacity: 0.95,
      dashArray,
    };
  }

  function hashStringToIndex(value, modulo) {
    const text = String(value || "");
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) {
      hash = ((hash << 5) - hash) + text.charCodeAt(i);
      hash |= 0;
    }
    return Math.abs(hash) % Math.max(1, Number(modulo) || 1);
  }

function colorForTracklineFeature(meta = {}, feature = {}, stats = {}) {
  const mode = String(meta.tracklineColorMode || "solid").trim().toLowerCase();
  const solid = String(meta.tracklineColor || "#2dd4bf").trim() || "#2dd4bf";
  const props = feature?.properties || {};
  if (mode === "nadir-depth") {
    const depth = Number(props.avg_depth ?? props.sample_depth);
    const min = Number(stats.minDepth);
    const max = Number(stats.maxDepth);
    if (Number.isFinite(depth) && Number.isFinite(min) && Number.isFinite(max) && max > min) {
      const t = Math.max(0, Math.min(1, (depth - min) / (max - min)));
      const hue = 200 - (t * 170);
      return `hsl(${hue.toFixed(0)} 88% 55%)`;
    }
    return solid;
  }
  if (mode === "surface-sv") {
    const sv = Number(props.surface_sound_speed);
    const min = Number(stats.minSurfaceSv);
    const max = Number(stats.maxSurfaceSv);
    if (Number.isFinite(sv) && Number.isFinite(min) && Number.isFinite(max) && max > min) {
      const t = Math.max(0, Math.min(1, (sv - min) / (max - min)));
      const hue = 260 - (t * 220);
      return `hsl(${hue.toFixed(0)} 85% 58%)`;
    }
    return solid;
  }
  if (mode === "time") {
    const start = Number(props.start_time);
    const min = Number(stats.minStartTime);
    const max = Number(stats.maxStartTime);
    if (Number.isFinite(start) && Number.isFinite(min) && Number.isFinite(max) && max > min) {
      const t = Math.max(0, Math.min(1, (start - min) / (max - min)));
      const hue = 220 - (t * 160);
      return `hsl(${hue.toFixed(0)} 85% 58%)`;
    }
    return solid;
  }
  if (mode === "svp-profile") {
    const profile = String(props.svp_profile || "").trim();
    if (!profile) return "#94a3b8";
    return getSvpProfileColor(profile);
  }
  return solid;
}

function buildTracklineLegendMeta(colorMode, stats = {}) {
  const mode = String(colorMode || "solid").trim().toLowerCase();
  if (mode === "nadir-depth") {
    const min = Number(stats.minDepth);
    const max = Number(stats.maxDepth);
    if (!(Number.isFinite(min) && Number.isFinite(max) && max > min)) return null;
    return {
      title: "Nadir Depth",
      min,
      max,
      format: "depth",
      gradient: "linear-gradient(90deg,hsl(200 88% 55%) 0%,hsl(157.5 88% 55%) 25%,hsl(115 88% 55%) 50%,hsl(72.5 88% 55%) 75%,hsl(30 88% 55%) 100%)",
    };
  }
  if (mode === "surface-sv") {
    const min = Number(stats.minSurfaceSv);
    const max = Number(stats.maxSurfaceSv);
    if (!(Number.isFinite(min) && Number.isFinite(max) && max > min)) return null;
    return {
      title: "Surface SV",
      min,
      max,
      format: "sound-speed",
      gradient: "linear-gradient(90deg,hsl(260 85% 58%) 0%,hsl(205 85% 58%) 25%,hsl(150 85% 58%) 50%,hsl(95 85% 58%) 75%,hsl(40 85% 58%) 100%)",
    };
  }
  if (mode === "time") {
    const min = Number(stats.minStartTime);
    const max = Number(stats.maxStartTime);
    if (!(Number.isFinite(min) && Number.isFinite(max) && max > min)) return null;
    return {
      title: "Trackline Time",
      min,
      max,
      format: "time",
      gradient: "linear-gradient(90deg,hsl(220 85% 58%) 0%,hsl(180 85% 58%) 25%,hsl(140 85% 58%) 50%,hsl(100 85% 58%) 75%,hsl(60 85% 58%) 100%)",
    };
  }
  return null;
}

function formatTracklineLegendTick(value, format) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  if (format === "sound-speed") return formatSoundSpeed(number);
  if (format === "depth") return number.toFixed(number >= 100 ? 0 : number >= 10 ? 1 : 2);
  if (format === "time") return new Date(number * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return String(number);
}

function createTracklineLegendControl(legendMeta) {
  if (!legendMeta) return null;
  const min = Number(legendMeta.min);
  const max = Number(legendMeta.max);
  if (!(Number.isFinite(min) && Number.isFinite(max) && max > min)) return null;
  const mid = min + ((max - min) * 0.5);
  const control = L.control({ position: "bottomright" });
  control.onAdd = () => {
    const el = L.DomUtil.create("div", "wind-legend-control");
    L.DomEvent.disableClickPropagation(el);
    el.innerHTML = `
      <div class="wind-legend-title">${esc(legendMeta.title || "Trackline Scale")}</div>
      <div class="wind-legend-bar" style="background:${legendMeta.gradient};"></div>
      <div class="wind-legend-ticks">
        <span>${esc(formatTracklineLegendTick(min, legendMeta.format))}</span>
        <span>${esc(formatTracklineLegendTick(mid, legendMeta.format))}</span>
        <span>${esc(formatTracklineLegendTick(max, legendMeta.format))}</span>
      </div>
    `;
    return el;
  };
  return control;
}

  function removeProjectTracklineLayer(projectFolder) {
    if (!app?.services?.layers) return;
    const target = String(projectFolder || "").trim().toLowerCase();
    const existing = (app.services.layers.listLayers?.() || []).find((layer) => (
      String(layer?.meta?.sourceType || "").trim() === "qimera-tracklines"
      && String(layer?.meta?.qimeraProjectFolder || "").trim().toLowerCase() === target
    ));
    if (existing?.id) app.services.layers.removeLayer(existing.id);
  }

  async function loadProjectTracklines(options = {}) {
    const activeProject = getActiveProject(state);
    const silent = options.silent === true;
    if (!activeProject?.folder) {
      status = "Select a project before loading tracklines.";
      if (!silent) render();
      return;
    }
    if (!app?.services?.layers || !app?.map?.instance) {
      status = "Layer services are not available to load tracklines.";
      if (!silent) render();
      return;
    }
    tracklineState.loading = true;
    tracklineState.error = "";
    if (!silent) render();
    try {
      const sourceNames = getTracklineSelectionNames(activeProject);
      if (!sourceNames.length) {
        removeProjectTracklineLayer(activeProject.folder);
        tracklineState.featureCount = 0;
        tracklineState.lastLoadedAt = new Date().toISOString();
        status = "Check one or more lines to show their tracklines.";
        if (!silent) render();
        return;
      }
      const result = await postJson("/api/qimera/tracklines", {
        projectFolder: activeProject.folder,
        sourceNames,
      }, 120000);
      const featureCollection = result?.featureCollection;
      const features = Array.isArray(featureCollection?.features) ? featureCollection.features : [];
      const startTimes = features
        .map((feature) => Number(feature?.properties?.start_time))
        .filter(Number.isFinite);
      const depthValues = features
        .map((feature) => Number(feature?.properties?.avg_depth ?? feature?.properties?.sample_depth))
        .filter(Number.isFinite);
      const surfaceSvValues = features
        .map((feature) => Number(feature?.properties?.surface_sound_speed))
        .filter(Number.isFinite);
      const styleStats = {
        minStartTime: startTimes.length ? Math.min(...startTimes) : null,
        maxStartTime: startTimes.length ? Math.max(...startTimes) : null,
        minDepth: depthValues.length ? Math.min(...depthValues) : null,
        maxDepth: depthValues.length ? Math.max(...depthValues) : null,
        minSurfaceSv: surfaceSvValues.length ? Math.min(...surfaceSvValues) : null,
        maxSurfaceSv: surfaceSvValues.length ? Math.max(...surfaceSvValues) : null,
      };
      if (!features.length) {
        removeProjectTracklineLayer(activeProject.folder);
        tracklineState.featureCount = 0;
        tracklineState.lastLoadedAt = new Date().toISOString();
        status = `No tracklines were found for the ${sourceNames.length} checked line${sourceNames.length === 1 ? "" : "s"}.`;
        if (!silent) render();
        return;
      }
      removeProjectTracklineLayer(activeProject.folder);
      const geoJsonLayer = L.geoJSON(featureCollection, {
        style: (feature) => {
          const style = getTracklineStyle(activeProject, feature);
          return {
            color: colorForTracklineFeature({
              tracklineColor: style.color,
              tracklineColorMode: style.colorMode,
            }, feature, styleStats),
            weight: style.weight,
            opacity: style.opacity,
            dashArray: style.dashArray,
          };
        },
        onEachFeature(feature, layer) {
          const props = feature?.properties || {};
          const rows = [
            ["Source", props.source_name || ""],
            ["Points", props.point_count || ""],
            ["Avg depth", Number.isFinite(Number(props.avg_depth)) ? Number(props.avg_depth).toFixed(2) : ""],
            ["Surface SV", Number.isFinite(Number(props.surface_sound_speed)) ? formatSoundSpeed(props.surface_sound_speed) : ""],
            ["Surface SV range", Number.isFinite(Number(props.surface_sound_speed_min)) && Number.isFinite(Number(props.surface_sound_speed_max)) ? `${formatSoundSpeed(props.surface_sound_speed_min)} to ${formatSoundSpeed(props.surface_sound_speed_max)}` : ""],
            ["Surface SV samples", Number.isFinite(Number(props.surface_sound_speed_samples)) && Number(props.surface_sound_speed_samples) > 0 ? String(props.surface_sound_speed_samples) : ""],
            ["SVP", props.svp_profile || ""],
            ["SD file", props.sd_path || ""],
          ].filter((entry) => String(entry[1] || "").trim());
          if (rows.length) {
            layer.bindPopup(rows.map(([label, value]) => `<div><strong>${esc(label)}:</strong> ${esc(value)}</div>`).join(""), {
              maxWidth: 420,
            });
          }
        },
      });
      const bounds = geoJsonLayer.getBounds?.();
      const initialColorMode = String(activeProject?.tracklineStyle?.colorMode || "solid").trim() || "solid";
      const initialLegendMeta = buildTracklineLegendMeta(initialColorMode, styleStats);
      preserveMapViewDuring(() => {
        app.services.layers.addLayer("qimera-command", {
          id: `qimera-tracklines-${activeProject.id}`,
          name: `Tracklines: ${activeProject.name}`,
          kind: "overlay",
          group: "Qimera",
          type: "managed",
          visible: true,
          opacity: 1,
          volatile: true,
          meta: {
            sourceType: "qimera-tracklines",
            qimeraProjectFolder: activeProject.folder,
            featureCount: features.length,
            tracklineColor: String(activeProject?.tracklineStyle?.color || "#2dd4bf").trim() || "#2dd4bf",
            tracklineWeight: Math.max(1, Math.min(5, Number(activeProject?.tracklineStyle?.weight) || 3)),
            tracklineLineType: String(activeProject?.tracklineStyle?.lineType || "solid").trim() || "solid",
            tracklineColorMode: initialColorMode,
            legendAvailable: true,
            legendVisible: initialLegendMeta != null,
            tracklineLegend: initialLegendMeta,
          },
          managed: {
            legendControl: null,
            layerMeta: {
              sourceType: "qimera-tracklines",
              qimeraProjectFolder: activeProject.folder,
              featureCount: features.length,
              tracklineColor: String(activeProject?.tracklineStyle?.color || "#2dd4bf").trim() || "#2dd4bf",
              tracklineWeight: Math.max(1, Math.min(5, Number(activeProject?.tracklineStyle?.weight) || 3)),
              tracklineLineType: String(activeProject?.tracklineStyle?.lineType || "solid").trim() || "solid",
              tracklineColorMode: initialColorMode,
              legendAvailable: true,
              legendVisible: initialLegendMeta != null,
              tracklineLegend: initialLegendMeta,
            },
            syncLegend() {
              try {
                const legendMeta = this.layerMeta?.tracklineLegend || null;
                const wantVisible = !!legendMeta && this.layerMeta?.legendVisible === true && app.map.instance.hasLayer(geoJsonLayer);
                if (this.legendControl?._map) this.legendControl.remove();
                this.legendControl = null;
                if (wantVisible) {
                  this.legendControl = createTracklineLegendControl(legendMeta);
                  if (this.legendControl) this.legendControl.addTo(app.map.instance);
                }
              } catch (error) {
                console.warn("[Qimera] Trackline legend sync failed", error);
                try {
                  if (this.legendControl?._map) this.legendControl.remove();
                } catch {}
                this.legendControl = null;
              }
            },
            setVisible(v) {
              if (v) {
                if (!app.map.instance.hasLayer(geoJsonLayer)) geoJsonLayer.addTo(app.map.instance);
              } else if (app.map.instance.hasLayer(geoJsonLayer)) {
                app.map.instance.removeLayer(geoJsonLayer);
              }
              this.syncLegend();
            },
            setOpacity(v) {
              const alpha = Math.max(0, Math.min(1, Number(v) || 0));
              geoJsonLayer.eachLayer((layer) => {
                layer.setStyle?.({ opacity: alpha, fillOpacity: alpha });
              });
            },
            setMeta(meta = {}) {
              this.layerMeta = { ...this.layerMeta, ...(meta || {}) };
              const color = String(this.layerMeta.tracklineColor || "#2dd4bf").trim() || "#2dd4bf";
              const weight = Math.max(1, Math.min(5, Number(this.layerMeta.tracklineWeight) || 3));
              const lineType = String(this.layerMeta.tracklineLineType || "solid").trim().toLowerCase();
              const colorMode = String(this.layerMeta.tracklineColorMode || "solid").trim().toLowerCase();
              const dashArray = lineType === "dotted" ? "2 8" : lineType === "dashed" ? "10 8" : null;
              const previousLegendMeta = this.layerMeta.tracklineLegend || null;
              const nextLegendMeta = buildTracklineLegendMeta(colorMode, styleStats);
              activeProject.tracklineStyle = { color, weight, lineType, colorMode };
              this.layerMeta.tracklineLegend = nextLegendMeta;
              if (!nextLegendMeta) this.layerMeta.legendVisible = false;
              else if (meta.legendVisible == null && !previousLegendMeta) this.layerMeta.legendVisible = true;
              geoJsonLayer.eachLayer((layer) => {
                layer.setStyle?.({
                  color: colorForTracklineFeature({
                    tracklineColor: color,
                    tracklineColorMode: colorMode,
                  }, layer?.feature || {}, styleStats),
                  weight,
                  dashArray,
                  opacity: 0.95,
                  fillOpacity: 0.95,
                });
              });
              this.syncLegend();
            },
            getBounds() {
              return bounds?.isValid?.() ? bounds : null;
            },
            bringToFront() {
              geoJsonLayer.bringToFront?.();
            },
            remove() {
              if (this.legendControl?._map) this.legendControl.remove();
              try { app.map.instance.removeLayer(geoJsonLayer); } catch {}
            },
          },
        });
      });
      tracklineState.featureCount = features.length;
      tracklineState.lastLoadedAt = new Date().toISOString();
      status = `Loaded ${features.length} trackline${features.length === 1 ? "" : "s"} for the checked source lines.`;
    } catch (error) {
      tracklineState.error = error?.message || "Could not load Qimera tracklines.";
      status = tracklineState.error;
    } finally {
      tracklineState.loading = false;
      if (!silent) render();
    }
  }

  function syncProjectTracklines(options = {}) {
    if (options.clear === true) {
      clearProjectTracklines();
      return;
    }
    loadProjectTracklines({ fitToBounds: false, silent: options.silent === true });
  }

  async function zoomToSourceTrackline(itemPath) {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder || !app?.map?.instance) {
      status = "Select a project before zooming to a source line.";
      render();
      return;
    }
    const sourceItem = getActiveSourceItems().find((item) => String(item.path || "").trim().toLowerCase() === String(itemPath || "").trim().toLowerCase());
    if (!sourceItem?.name) {
      status = "Could not find that source line in the active project.";
      render();
      return;
    }
    try {
      const result = await postJson("/api/qimera/tracklines", {
        projectFolder: activeProject.folder,
        sourceNames: [sourceItem.name],
      }, 120000);
      const featureCollection = result?.featureCollection;
      const features = Array.isArray(featureCollection?.features) ? featureCollection.features : [];
      if (!features.length) {
        status = `No trackline was found for ${sourceItem.name}.`;
        render();
        return;
      }
      const bounds = L.geoJSON(featureCollection).getBounds?.();
      if (!bounds?.isValid?.()) {
        status = `Trackline bounds were not valid for ${sourceItem.name}.`;
        render();
        return;
      }
      app.map.instance.fitBounds(bounds.pad(0.2), { maxZoom: 16 });
      status = `Zoomed to ${sourceItem.name}.`;
      render();
    } catch (error) {
      status = error?.message || `Could not zoom to ${sourceItem?.name || "that source line"}.`;
      render();
    }
  }

  function clearProjectTracklines() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before clearing tracklines.";
      render();
      return;
    }
    removeProjectTracklineLayer(activeProject.folder);
    tracklineState.featureCount = 0;
    tracklineState.error = "";
    tracklineState.lastLoadedAt = new Date().toISOString();
    status = `Cleared tracklines for ${activeProject.name}.`;
    render();
  }

  async function loadSourceFolderFiles() {
    const activeProject = getActiveProject(state);
    const sourceFolder = String(activeProject?.sourceFolder || state.inputPath || "").trim();
    if (!activeProject?.folder) {
      status = "Select a project before loading source files.";
      render();
      return;
    }
    if (!sourceFolder) {
      status = "Choose a source folder first.";
      render();
      return;
    }
    sourceBusy.loading = true;
    render();
    try {
      const payload = await postJson("/api/qimera/source-files", {
        folderPath: sourceFolder,
        format: state.importFormat || "db",
      }, 120000);
      const discovered = Array.isArray(payload?.files) ? payload.files : [];
      const next = mergeDiscoveredSourceItems(discovered);
      activeProject.sourceFolder = sourceFolder;
      state.inputPath = sourceFolder;
      state.importMode = "files";
      setActiveProjectSourceItems(next);
      try {
        await persistActiveProjectMetadata({ sourceFolder });
      } catch {}
      await refreshSourceStatuses();
      ensureSourceAutomationWatcher();
      status = `Loaded ${next.length} source files from ${sourceFolder}.`;
    } catch (error) {
      status = error?.message || "Could not load source files from the host folder.";
    } finally {
      sourceBusy.loading = false;
      render();
    }
  }

  function setSourceSelection(itemPath, selected) {
    const next = getActiveSourceItems().map((item) => (
      String(item.path || "").trim().toLowerCase() === String(itemPath || "").trim().toLowerCase()
        ? normalizeSourceItem({ ...item, selected })
        : item
    )).filter(Boolean);
    setActiveProjectSourceItems(next);
    updateSourceWorkspaceSelectionUi();
  }

  function setSourceSelectionsByPaths(pathSet, selected) {
    const normalized = new Set(Array.from(pathSet || []).map((item) => String(item || "").trim().toLowerCase()).filter(Boolean));
    const next = getActiveSourceItems().map((item) => (
      normalized.has(String(item.path || "").trim().toLowerCase())
        ? normalizeSourceItem({ ...item, selected })
        : item
    )).filter(Boolean);
    setActiveProjectSourceItems(next);
    updateSourceWorkspaceSelectionUi();
  }

  function setAllSourceSelections(selected) {
    const next = getActiveSourceItems().map((item) => normalizeSourceItem({
      ...item,
      selected,
    })).filter(Boolean);
    setActiveProjectSourceItems(next);
    updateSourceWorkspaceSelectionUi();
  }

  function selectSourcesNeedingSvpReprocess() {
    const items = getActiveSourceItems();
    const affectedCount = items.filter((item) => item.svpNeedsReprocess === true && item.importStatus === "imported").length;
    if (!affectedCount) {
      status = "No source lines are currently marked for SVP reprocess.";
      render();
      return;
    }
    const next = items.map((item) => normalizeSourceItem({
      ...item,
      selected: item.svpNeedsReprocess === true && item.importStatus === "imported",
    })).filter(Boolean);
    setActiveProjectSourceItems(next);
    updateSourceWorkspaceSelectionUi();
    status = `Selected ${affectedCount} source line${affectedCount === 1 ? "" : "s"} marked for SVP reprocess.`;
    render();
  }

  function setSourceChecked(itemPath, checked) {
    const next = getActiveSourceItems().map((item) => (
      String(item.path || "").trim().toLowerCase() === String(itemPath || "").trim().toLowerCase()
        ? normalizeSourceItem({ ...item, checked })
        : item
    )).filter(Boolean);
    setActiveProjectSourceItems(next);
    syncProjectTracklines({ silent: true });
  }

  function toggleSourceRowSelection(itemPath, modifiers = {}) {
    const items = getActiveSourceItems();
    const normalizedPath = String(itemPath || "").trim().toLowerCase();
    const currentIndex = items.findIndex((item) => String(item.path || "").trim().toLowerCase() === normalizedPath);
    if (currentIndex < 0) return;
    const shiftKey = modifiers.shiftKey === true;
    const toggleKey = modifiers.ctrlKey === true || modifiers.metaKey === true;
    if (shiftKey && lastSelectedSourcePath) {
      const anchorIndex = items.findIndex((item) => String(item.path || "").trim().toLowerCase() === String(lastSelectedSourcePath || "").trim().toLowerCase());
      if (anchorIndex >= 0) {
        const start = Math.min(anchorIndex, currentIndex);
        const end = Math.max(anchorIndex, currentIndex);
        const next = items.map((item, index) => normalizeSourceItem({
          ...item,
          selected: index >= start && index <= end,
        })).filter(Boolean);
        setActiveProjectSourceItems(next);
        updateSourceWorkspaceSelectionUi();
        return;
      }
    }
    if (toggleKey) {
      const item = items[currentIndex];
      setSourceSelection(item.path, !item.selected);
      lastSelectedSourcePath = item.path;
      return;
    }
    const next = items.map((item, index) => normalizeSourceItem({
      ...item,
      selected: index === currentIndex,
    })).filter(Boolean);
    setActiveProjectSourceItems(next);
    lastSelectedSourcePath = items[currentIndex].path;
    updateSourceWorkspaceSelectionUi();
  }

  async function importSourceItems(selectedItems, options = {}) {
    const activeProject = getActiveProject(state);
    sourceBusy.importing = true;
    const singleFlow = String(options.flowMode || state.sourceFlowMode || "batch") === "single";
    if (!singleFlow) {
      setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
        ...item,
        importStatus: selectedItems.some((selected) => selected.path === item.path) ? "importing" : item.importStatus,
      })).filter(Boolean));
      render();
    }
    try {
      if (singleFlow) {
        for (const selectedItem of selectedItems) {
          setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
            ...item,
            importStatus: item.path === selectedItem.path ? "importing" : item.importStatus,
            activityProgress: item.path === selectedItem.path ? 10 : item.activityProgress,
          })).filter(Boolean));
          startSourceActivityAnimation(selectedItem.path, "import");
          updateSourceWorkspaceRowsUi();
          updateSourceWorkspaceActivityUi();
          await postJson("/api/qimera/import-sources", {
            projectFolder: activeProject.folder,
            sourceFolder: activeProject.sourceFolder,
            format: state.importFormat || "db",
            printProgress: false,
            files: [selectedItem.path],
          }, 1000 * 60 * 20);
          await refreshSourceStatuses({ renderUi: false });
        }
      } else {
        await postJson("/api/qimera/import-sources", {
          projectFolder: activeProject.folder,
          sourceFolder: activeProject.sourceFolder,
          format: state.importFormat || "db",
          printProgress: false,
          files: selectedItems.map((item) => item.path),
        }, 1000 * 60 * 20);
      }
      await refreshSourceStatuses({ renderUi: !singleFlow });
      status = options.successMessage || `Imported ${selectedItems.length} source lines into ${activeProject.name}.`;
    } catch (error) {
      status = error?.message || options.errorMessage || "Source import failed.";
      setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
        ...item,
        importStatus: item.importStatus === "importing" ? "pending" : item.importStatus,
      })).filter(Boolean));
    } finally {
      stopSourceActivityAnimation();
      sourceBusy.importing = false;
      render();
    }
  }

  async function importSelectedSources() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before importing sources.";
      render();
      return;
    }
    const selectedItems = getActiveSourceItems().filter((item) => item.selected && item.importStatus !== "imported" && item.readyState === "ready");
    if (!selectedItems.length) {
      status = "Select at least one ready source line that is not already imported.";
      render();
      return;
    }
    await importSourceItems(selectedItems);
  }

  async function processSourceItems(importedItems, options = {}) {
    const activeProject = getActiveProject(state);
    sourceBusy.processing = true;
    const singleFlow = String(options.flowMode || state.sourceFlowMode || "batch") === "single";
    if (!singleFlow) {
      setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
        ...item,
        processStatus: importedItems.some((selected) => selected.path === item.path) ? "processing" : item.processStatus,
        svpNeedsReprocess: importedItems.some((selected) => selected.path === item.path) ? false : item.svpNeedsReprocess,
      })).filter(Boolean));
      render();
    }
    try {
      if (singleFlow) {
        for (const importedItem of importedItems) {
          setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
            ...item,
            processStatus: item.path === importedItem.path ? "processing" : item.processStatus,
            activityProgress: item.path === importedItem.path ? 10 : item.activityProgress,
            svpNeedsReprocess: item.path === importedItem.path ? false : item.svpNeedsReprocess,
          })).filter(Boolean));
          startSourceActivityAnimation(importedItem.path, "process");
          updateSourceWorkspaceRowsUi();
          updateSourceWorkspaceActivityUi();
          await postJson("/api/qimera/auto-process-sources", {
            projectFolder: activeProject.folder,
            importedSourceFiles: [importedItem.name],
          }, 1000 * 60 * 30);
          await refreshSourceStatuses({ renderUi: false });
        }
      } else {
        await postJson("/api/qimera/auto-process-sources", {
          projectFolder: activeProject.folder,
          importedSourceFiles: importedItems.map((item) => item.name),
        }, 1000 * 60 * 30);
      }
      await refreshSourceStatuses({ renderUi: !singleFlow });
      status = options.successMessage || `Processed imported source lines for ${activeProject.name}.`;
      if (activeProject.autoAppendProcessedToGrid) {
        scheduleNextGridAutomationRun();
      }
    } catch (error) {
      status = error?.message || options.errorMessage || "Auto-process failed.";
      setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
        ...item,
        processStatus: item.processStatus === "processing" ? "pending" : item.processStatus,
      })).filter(Boolean));
    } finally {
      stopSourceActivityAnimation();
      sourceBusy.processing = false;
      render();
    }
  }

  async function processImportedSources() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before processing sources.";
      render();
      return;
    }
    const importedItems = getActiveSourceItems().filter((item) => (
      item.selected
      && item.importStatus === "imported"
      && (item.processStatus !== "processed" || item.svpNeedsReprocess === true)
    ));
    if (!importedItems.length) {
      status = "Select at least one imported line that is pending processing or marked for SVP reprocess.";
      render();
      return;
    }
    await processSourceItems(importedItems);
  }

  async function updateFilterSettingsFromForm() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder || !containerEl) return;
    const selectedFilterProfile = String(containerEl.querySelector("#qimera-filter-profile")?.value || "").trim();
    state.selectedFilterProfile = selectedFilterProfile;
    activeProject.selectedFilterProfile = selectedFilterProfile;
    if (!selectedFilterProfile) {
      state.autoFilterSources = false;
      activeProject.autoFilterSources = false;
    }
    persist();
    render();
    try {
      await persistActiveProjectMetadata({
        selectedFilterProfile,
        autoFilterSources: activeProject.autoFilterSources === true && !!selectedFilterProfile,
      });
    } catch (error) {
      status = error?.message || "Could not save filter settings.";
      render();
    }
  }

  async function runFilterOnSourceItems(items, options = {}) {
    const activeProject = getActiveProject(state);
    const filterName = String(options.filterName || state.selectedFilterProfile || "").trim();
    if (!activeProject?.folder) {
      status = "Select a project before applying filters.";
      render();
      return;
    }
    if (!filterName) {
      status = "Choose a filter profile first.";
      render();
      return;
    }
    if (!items.length) {
      status = "Select at least one imported line to filter.";
      render();
      return;
    }
    sourceBusy.filtering = true;
    const singleFlow = String(options.flowMode || state.sourceFlowMode || "batch") === "single";
    if (!singleFlow) {
      setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
        ...item,
        filterStatus: items.some((selected) => selected.path === item.path) ? "filtering" : item.filterStatus,
      })).filter(Boolean));
      render();
    }
    try {
      if (singleFlow) {
        for (const sourceItem of items) {
          setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
            ...item,
            filterStatus: item.path === sourceItem.path ? "filtering" : item.filterStatus,
            activityProgress: item.path === sourceItem.path ? 10 : item.activityProgress,
          })).filter(Boolean));
          startSourceActivityAnimation(sourceItem.path, "filter");
          updateSourceWorkspaceRowsUi();
          updateSourceWorkspaceActivityUi();
          await postJson("/api/qimera/run-filter-profile", {
            projectFolder: activeProject.folder,
            filterName,
            sourceFileNames: [sourceItem.name],
          }, 1000 * 60 * 30);
          await refreshSourceStatuses({ renderUi: false });
        }
      } else {
        await postJson("/api/qimera/run-filter-profile", {
          projectFolder: activeProject.folder,
          filterName,
          sourceFileNames: items.map((item) => item.name),
        }, 1000 * 60 * 30);
      }
      await refreshSourceStatuses({ renderUi: !singleFlow });
      status = options.successMessage || `Applied ${filterName} to ${items.length} source line${items.length === 1 ? "" : "s"} in ${activeProject.name}.`;
    } catch (error) {
      status = error?.message || options.errorMessage || "Filter run failed.";
      setActiveProjectSourceItems(getActiveSourceItems().map((item) => normalizeSourceItem({
        ...item,
        filterStatus: item.filterStatus === "filtering" ? "pending" : item.filterStatus,
      })).filter(Boolean));
    } finally {
      stopSourceActivityAnimation();
      sourceBusy.filtering = false;
      render();
    }
  }

  async function applyFilterToSelectedSources() {
    const items = getActiveSourceItems().filter((item) => item.selected && item.importStatus === "imported");
    await runFilterOnSourceItems(items);
  }

  async function runSourceAutomationScan(options = {}) {
    const activeProject = getActiveProject(state);
    const settings = getActiveAutomationSettings();
    if (!activeProject?.folder || !activeProject.sourceFolder) {
      sourceAutomation.error = "Choose a source folder before monitoring for new DB files.";
      render();
      return;
    }
    if (!options.manual && !settings.monitorSourceFolder) return;
    if (sourceAutomation.running || sourceBusy.loading || sourceBusy.importing || sourceBusy.processing || sourceBusy.filtering || sourceBusy.removing) {
      scheduleNextSourceAutomationScan();
      return;
    }
    const projectId = activeProject.id;
    const knownPathKeys = new Set(getActiveSourceItems().map((item) => String(item.path || "").trim().toLowerCase()).filter(Boolean));
    sourceAutomation.running = true;
    sourceAutomation.error = "";
    sourceAutomation.message = options.manual ? "Scanning source folder..." : "Monitoring source folder...";
    render();
    try {
      const payload = await postJson("/api/qimera/source-files", {
        folderPath: activeProject.sourceFolder,
        format: state.importFormat || "db",
      }, 120000);
      if (getActiveProject(state)?.id !== projectId) return;
      const discovered = Array.isArray(payload?.files) ? payload.files : [];
      const next = mergeDiscoveredSourceItems(discovered);
      setActiveProjectSourceItems(next);
      await refreshSourceStatuses();
      const currentItems = getActiveSourceItems();
      const discoveredNewItems = currentItems.filter((item) => !knownPathKeys.has(String(item.path || "").trim().toLowerCase()));
      const pendingImportItems = currentItems.filter((item) => item.importStatus !== "imported" && item.readyState === "ready");
      const pendingProcessItems = currentItems.filter((item) => item.importStatus === "imported" && item.processStatus !== "processed");
      const pendingFilterItems = getActiveSourceItems().filter((item) => item.importStatus === "imported" && String(settings.selectedFilterProfile || "").trim() && String(item.filterProfile || "").trim() !== String(settings.selectedFilterProfile || "").trim());
      sourceAutomation.lastScanAt = new Date().toISOString();
      sourceAutomation.newCount = discoveredNewItems.length;
      if (settings.autoImportNewSources && pendingImportItems.length) {
        await importSourceItems(pendingImportItems, {
          successMessage: `Auto-imported ${pendingImportItems.length} source line${pendingImportItems.length === 1 ? "" : "s"} for ${activeProject.name}.`,
          errorMessage: "Auto-import failed.",
        });
        let autoProcessCount = 0;
        if (settings.autoProcessNewSources) {
          const remainingProcessItems = getActiveSourceItems().filter((item) => item.importStatus === "imported" && item.processStatus !== "processed");
          if (remainingProcessItems.length) {
            await processSourceItems(remainingProcessItems, {
              successMessage: `Auto-processed ${remainingProcessItems.length} source line${remainingProcessItems.length === 1 ? "" : "s"} for ${activeProject.name}.`,
              errorMessage: "Auto-process failed.",
            });
            autoProcessCount = remainingProcessItems.length;
          }
        }
        const remainingFilterItems = getActiveSourceItems().filter((item) => item.importStatus === "imported" && String(settings.selectedFilterProfile || "").trim() && String(item.filterProfile || "").trim() !== String(settings.selectedFilterProfile || "").trim());
        if (settings.autoFilterSources && remainingFilterItems.length) {
          await runFilterOnSourceItems(remainingFilterItems, {
            filterName: settings.selectedFilterProfile,
            successMessage: `Auto-applied ${settings.selectedFilterProfile} to ${remainingFilterItems.length} source line${remainingFilterItems.length === 1 ? "" : "s"} for ${activeProject.name}.`,
            errorMessage: "Auto-filter failed.",
          });
          sourceAutomation.message = `${
            discoveredNewItems.length
              ? `Found ${discoveredNewItems.length} new line${discoveredNewItems.length === 1 ? "" : "s"}, `
              : ""
          }auto-imported pending lines${autoProcessCount ? `, auto-processed ${autoProcessCount}` : ""}, and auto-applied ${settings.selectedFilterProfile} to ${remainingFilterItems.length}.`;
        } else {
          sourceAutomation.message = `${
            discoveredNewItems.length
              ? `Found ${discoveredNewItems.length} new line${discoveredNewItems.length === 1 ? "" : "s"} and `
              : ""
          }auto-imported pending lines${autoProcessCount ? ` and auto-processed ${autoProcessCount}` : ""}.`;
        }
      } else if (settings.autoProcessNewSources && pendingProcessItems.length) {
        await processSourceItems(pendingProcessItems, {
          successMessage: `Auto-processed ${pendingProcessItems.length} source line${pendingProcessItems.length === 1 ? "" : "s"} for ${activeProject.name}.`,
          errorMessage: "Auto-process failed.",
        });
        sourceAutomation.message = discoveredNewItems.length
          ? `Found ${discoveredNewItems.length} new line${discoveredNewItems.length === 1 ? "" : "s"} and auto-processed ${pendingProcessItems.length} imported line${pendingProcessItems.length === 1 ? "" : "s"}.`
          : `Auto-processed ${pendingProcessItems.length} imported line${pendingProcessItems.length === 1 ? "" : "s"}.`;
      } else if (settings.autoFilterSources && pendingFilterItems.length) {
        await runFilterOnSourceItems(pendingFilterItems, {
          filterName: settings.selectedFilterProfile,
          successMessage: `Auto-applied ${settings.selectedFilterProfile} to ${pendingFilterItems.length} source line${pendingFilterItems.length === 1 ? "" : "s"} for ${activeProject.name}.`,
          errorMessage: "Auto-filter failed.",
        });
        sourceAutomation.message = discoveredNewItems.length
          ? `Found ${discoveredNewItems.length} new line${discoveredNewItems.length === 1 ? "" : "s"} and auto-applied ${settings.selectedFilterProfile} to ${pendingFilterItems.length} imported line${pendingFilterItems.length === 1 ? "" : "s"}.`
          : `Auto-applied ${settings.selectedFilterProfile} to ${pendingFilterItems.length} imported line${pendingFilterItems.length === 1 ? "" : "s"}.`;
      } else if (discoveredNewItems.length) {
        sourceAutomation.message = `Found ${discoveredNewItems.length} new source line${discoveredNewItems.length === 1 ? "" : "s"} waiting for import.`;
      } else {
        sourceAutomation.newCount = 0;
        sourceAutomation.message = "No new source files found.";
      }
    } catch (error) {
      sourceAutomation.error = error?.message || "Source monitoring scan failed.";
    } finally {
      sourceAutomation.running = false;
      render();
      scheduleNextSourceAutomationScan();
    }
  }

  async function removeImportedSource(itemPath) {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before removing sources.";
      render();
      return;
    }
    const target = getActiveSourceItems().find((item) => String(item.path || "").trim().toLowerCase() === String(itemPath || "").trim().toLowerCase());
    if (!target || target.importStatus !== "imported") {
      status = "Choose an imported source line to remove.";
      render();
      return;
    }
    sourceBusy.removing = target.path;
    render();
    try {
      await postJson("/api/qimera/remove-sources", {
        projectFolder: activeProject.folder,
        files: [target.path],
      }, 1000 * 60 * 15);
      await refreshSourceStatuses();
      status = `Removed ${target.name} from ${activeProject.name}.`;
    } catch (error) {
      status = error?.message || "Could not remove the source line from the project.";
    } finally {
      sourceBusy.removing = "";
      render();
    }
  }

  async function removeSelectedImportedSources() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) {
      status = "Select a project before removing sources.";
      render();
      return;
    }
    const targets = getActiveSourceItems().filter((item) => item.selected && item.importStatus === "imported");
    if (!targets.length) {
      status = "Select at least one imported line to remove.";
      render();
      return;
    }
    sourceBusy.removing = "__bulk__";
    render();
    try {
      await postJson("/api/qimera/remove-sources", {
        projectFolder: activeProject.folder,
        files: targets.map((item) => item.path),
      }, 1000 * 60 * 15);
      await refreshSourceStatuses();
      status = `Removed ${targets.length} imported source line${targets.length === 1 ? "" : "s"} from ${activeProject.name}.`;
    } catch (error) {
      status = error?.message || "Could not remove the selected source lines from the project.";
    } finally {
      sourceBusy.removing = "";
      render();
    }
  }

  async function refreshProjectGrids() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) return;
    gridBusy.loading = true;
    render();
    try {
      const payload = await postJson("/api/qimera/grids", {
        projectFolder: activeProject.folder,
      }, 60000);
      const gridItems = (Array.isArray(payload?.gridNames) ? payload.gridNames : []).map((name) => ({ name }));
      activeProject.selectedGridName = String(payload?.selectedGridName || activeProject.selectedGridName || "").trim();
      activeProject.autoAppendProcessedToGrid = payload?.autoAppendProcessedToGrid === true || activeProject.autoAppendProcessedToGrid === true;
      if (Object.prototype.hasOwnProperty.call(payload || {}, "autoExportGeotiffOnGridAppend")) {
        activeProject.autoExportGeotiffOnGridAppend = payload?.autoExportGeotiffOnGridAppend !== false;
      }
      activeProject.lastGridAppendSignature = String(payload?.lastGridAppendSignature || activeProject.lastGridAppendSignature || "").trim();
      activeProject.gridSourceMemberships = payload?.gridSourceMemberships && typeof payload.gridSourceMemberships === "object"
        ? payload.gridSourceMemberships
        : (activeProject.gridSourceMemberships || {});
      setActiveProjectGridItems(gridItems);
      state.selectedGridName = String(activeProject.selectedGridName || "").trim();
      state.autoAppendProcessedToGrid = activeProject.autoAppendProcessedToGrid === true;
      state.autoExportGeotiffOnGridAppend = activeProject.autoExportGeotiffOnGridAppend !== false;
      updateSourceItemsFromStatus(
        getActiveSourceItems().filter((item) => item.importStatus === "imported").map((item) => item.name),
        getActiveSourceItems().filter((item) => item.processStatus === "processed").map((item) => item.name),
        Object.fromEntries(getActiveSourceItems().filter((item) => item.filterProfile).map((item) => [item.name, item.filterProfile])),
      );
      persist();
    } catch (error) {
      status = error?.message || "Could not load grids from the project.";
    } finally {
      gridBusy.loading = false;
      render();
    }
  }

  async function createDynamicGrid() {
    const activeProject = getActiveProject(state);
    const gridName = String(state.gridName || "").trim();
    const cellSize = String(state.cellSize || "").trim();
    const gridMode = String(state.gridMode || "cube").trim() || "cube";
    const gridProfile = String(state.gridProfile || "Default").trim() || "Default";
    const excludeCoverage = state.excludeGridCoverage === true;
    if (!activeProject?.folder) {
      status = "Select a project before creating a grid.";
      render();
      return;
    }
    if (!gridName) {
      status = "Grid name is required.";
      render();
      return;
    }
    if (gridMode !== "cube") {
      status = "Standard dynamic grid mode is visible for planning, but the non-CUBE CLI path is not yet verified in Qimera Command.";
      render();
      return;
    }
    gridBusy.creating = true;
    render();
    try {
      const result = await postJson("/api/qimera/create-grid", {
        projectFolder: activeProject.folder,
        gridName,
        cellSize,
        cubeConfig: gridProfile,
        excludeCoverage,
      }, 1000 * 60 * 30);
      activeProject.selectedGridName = String(result?.metadata?.selectedGridName || gridName).trim();
      state.selectedGridName = activeProject.selectedGridName;
      activeProject.lastGridAppendSignature = buildProcessedGridSignature();
      await persistActiveProjectMetadata({
        selectedGridName: activeProject.selectedGridName,
        lastGridAppendSignature: activeProject.lastGridAppendSignature,
      });
      await refreshProjectGrids();
      status = `Created grid ${gridName} in ${activeProject.name}.`;
    } catch (error) {
      status = error?.message || "Could not create the dynamic grid.";
    } finally {
      gridBusy.creating = false;
      render();
    }
  }

  async function appendProcessedSourcesToGrids(options = {}) {
    const activeProject = getActiveProject(state);
    const activeGridName = String(activeProject?.selectedGridName || state.selectedGridName || getActiveGridItems()[0]?.name || "").trim();
    if (!activeProject?.folder) {
      status = "Select a project before appending to grids.";
      render();
      return false;
    }
    if (!getActiveGridItems().length) {
      status = "Create or load a grid before appending project lines.";
      render();
      return false;
    }
    gridBusy.appending = true;
    const appendedTargets = getActiveSourceItems().filter((item) => item.importStatus === "imported" && item.gridAppendStatus !== "appended");
    const importedSourceNames = getActiveSourceItems()
      .filter((item) => item.importStatus === "imported")
      .map((item) => String(item.name || "").trim())
      .filter(Boolean);
    setActiveProjectSourceItems(
      getActiveSourceItems().map((item) => normalizeSourceItem({
        ...item,
        gridAppendStatus: appendedTargets.some((target) => target.path === item.path) ? "appending" : item.gridAppendStatus,
      })),
    );
    if (appendedTargets[0]?.path) startSourceActivityAnimation(appendedTargets[0].path, "grid");
    updateSourceWorkspaceRowsUi();
    updateSourceWorkspaceActivityUi();
    try {
      const appendSignature = buildProcessedGridSignature();
      const result = await postJson("/api/qimera/append-grids", {
        projectFolder: activeProject.folder,
        appendSignature,
        gridName: activeGridName,
        appendedSourceFiles: importedSourceNames,
      }, 1000 * 60 * 30);
      const nextMemberships = result?.metadata?.gridSourceMemberships && typeof result.metadata.gridSourceMemberships === "object"
        ? result.metadata.gridSourceMemberships
        : {
            ...(activeProject.gridSourceMemberships || {}),
            ...(activeGridName ? { [activeGridName]: [...new Set(importedSourceNames)] } : {}),
          };
      activeProject.gridSourceMemberships = nextMemberships;
      setActiveProjectSourceItems(
        getActiveSourceItems().map((item) => normalizeSourceItem({
          ...item,
          gridAppendStatus: appendedTargets.some((target) => target.path === item.path) ? "appended" : item.gridAppendStatus,
          activityProgress: 0,
        })),
      );
      activeProject.lastGridAppendSignature = String(result?.metadata?.lastGridAppendSignature || appendSignature).trim();
      status = options.successMessage || `Appended project lines not already in the grids for ${activeProject.name}.`;
      if (activeProject.autoExportGeotiffOnGridAppend !== false) {
        try {
          const exportResult = await runAutoGeotiffExportForActiveGrid();
          queueAutoLoadExportedTiffs(exportResult?.results);
          const geotiffResult = Array.isArray(exportResult?.results)
            ? exportResult.results.find((item) => String(item?.key || "").trim() === "export-geotiff" && item?.ok)
            : null;
          const outputName = String(geotiffResult?.outputPath || "").trim().split(/[\\/]/).pop() || "";
          status = outputName ? `Live GeoTIFF refreshed: ${outputName}.` : "Live GeoTIFF refreshed.";
        } catch (error) {
          status = `GeoTIFF refresh failed: ${error?.message || "unknown error"}.`;
        }
      }
      return true;
    } catch (error) {
      setActiveProjectSourceItems(
        getActiveSourceItems().map((item) => normalizeSourceItem({
          ...item,
          gridAppendStatus: item.gridAppendStatus === "appending" ? "pending" : item.gridAppendStatus,
          activityProgress: 0,
        })),
      );
      status = error?.message || options.errorMessage || "Could not append project lines to the grids.";
      return false;
    } finally {
      stopSourceActivityAnimation();
      gridBusy.appending = false;
      render();
    }
  }

  async function removeSelectedSourcesFromActiveGrid() {
    const activeProject = getActiveProject(state);
    const selectedGridName = String(activeProject?.selectedGridName || state.selectedGridName || "").trim();
    if (!activeProject?.folder) {
      status = "Select a project before removing lines from a grid.";
      render();
      return;
    }
    if (!selectedGridName) {
      status = "Select a grid before removing lines from it.";
      render();
      return;
    }
    const targets = getActiveSourceItems().filter((item) => item.selected && item.importStatus === "imported");
    if (!targets.length) {
      status = "Select at least one imported source line to remove from the active grid.";
      render();
      return;
    }
    gridBusy.removing = true;
    render();
    try {
      await postJson("/api/qimera/remove-grid-sources", {
        projectFolder: activeProject.folder,
        gridName: selectedGridName,
        files: targets.map((item) => item.path),
      }, 1000 * 60 * 20);
      const nextMemberships = { ...(activeProject.gridSourceMemberships || {}) };
      const removedNames = new Set(targets.map((item) => String(item.name || "").trim().toLowerCase()).filter(Boolean));
      if (nextMemberships[selectedGridName]) {
        nextMemberships[selectedGridName] = nextMemberships[selectedGridName].filter((name) => !removedNames.has(String(name || "").trim().toLowerCase()));
        if (!nextMemberships[selectedGridName].length) delete nextMemberships[selectedGridName];
      }
      activeProject.gridSourceMemberships = nextMemberships;
      setActiveProjectSourceItems(
        getActiveSourceItems().map((item) => normalizeSourceItem({
          ...item,
          gridAppendStatus: removedNames.has(String(item.name || "").trim().toLowerCase()) ? "pending" : item.gridAppendStatus,
        })),
      );
      activeProject.lastGridAppendSignature = "";
      await persistActiveProjectMetadata({ lastGridAppendSignature: "", gridSourceMemberships: nextMemberships });
      status = `Removed ${targets.length} selected line${targets.length === 1 ? "" : "s"} from ${selectedGridName}.`;
    } catch (error) {
      status = error?.message || "Could not remove the selected lines from the active grid.";
    } finally {
      gridBusy.removing = false;
      render();
    }
  }

  async function updateGridAutomationSettingsFromForm() {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder || !containerEl) return;
    const autoAppendProcessedToGrid = containerEl.querySelector("#qimera-grid-auto-append")?.checked === true;
    const autoExportGeotiffOnGridAppend = containerEl.querySelector("#qimera-grid-auto-export-geotiff")?.checked !== false;
    activeProject.autoAppendProcessedToGrid = autoAppendProcessedToGrid;
    activeProject.autoExportGeotiffOnGridAppend = autoExportGeotiffOnGridAppend;
    state.autoAppendProcessedToGrid = autoAppendProcessedToGrid;
    state.autoExportGeotiffOnGridAppend = autoExportGeotiffOnGridAppend;
    try {
      await persistActiveProjectMetadata({ autoAppendProcessedToGrid, autoExportGeotiffOnGridAppend });
    } catch (error) {
      status = error?.message || "Could not save grid automation settings.";
    }
    ensureGridAutomationWatcher();
    render();
  }

  function stopGridAutomationWatcher() {
    if (gridAutomationTimer) {
      clearTimeout(gridAutomationTimer);
      gridAutomationTimer = null;
    }
  }

  function scheduleNextGridAutomationRun() {
    stopGridAutomationWatcher();
    const activeProject = getActiveProject(state);
    if (!activeProject?.autoAppendProcessedToGrid) return;
    const delayMs = Math.max(5000, normalizeSourceScanInterval(activeProject?.sourceScanIntervalSec || state.sourceScanIntervalSec || 30) * 1000);
    gridAutomationTimer = setTimeout(() => {
      runGridAutomationScan({ manual: false });
    }, delayMs);
  }

  function ensureGridAutomationWatcher() {
    stopGridAutomationWatcher();
    const activeProject = getActiveProject(state);
    if (!activeProject?.autoAppendProcessedToGrid) return;
    scheduleNextGridAutomationRun();
  }

  async function runGridAutomationScan({ manual = false } = {}) {
    const activeProject = getActiveProject(state);
    if (!activeProject?.folder) return;
    if (!manual && activeProject.autoAppendProcessedToGrid !== true) return;
    if (gridAutomation.running || gridBusy.loading || gridBusy.creating || gridBusy.appending || gridBusy.removing || sourceBusy.loading || sourceBusy.importing || sourceBusy.processing) {
      scheduleNextGridAutomationRun();
      return;
    }
    gridAutomation.running = true;
    gridAutomation.error = "";
    render();
    try {
      await refreshSourceStatuses();
      await refreshProjectGrids();
      const currentSignature = buildProcessedGridSignature();
      const previousSignature = String(activeProject.lastGridAppendSignature || "").trim();
      if (getActiveGridItems().length && currentSignature && currentSignature !== previousSignature) {
        await appendProcessedSourcesToGrids({
          successMessage: manual
            ? `Appended newly processed project lines into the available grid${getActiveGridItems().length === 1 ? "" : "s"}.`
            : `Auto-appended newly processed project lines into the available grid${getActiveGridItems().length === 1 ? "" : "s"}.`,
        });
        gridAutomation.message = manual ? "Grid append completed." : "Auto-appended processed project lines to grids.";
      } else {
        gridAutomation.message = manual ? "No new processed lines were waiting for grid append." : "Grid automation found nothing new to append.";
      }
      gridAutomation.lastRunAt = new Date().toISOString();
    } catch (error) {
      gridAutomation.error = error?.message || "Grid automation failed.";
    } finally {
      gridAutomation.running = false;
      render();
      scheduleNextGridAutomationRun();
    }
  }

  async function browseForProjectFolder(target) {
    if (!projectDialog) return;
    try {
      const selectedPath = target === "parentFolder"
        ? String(projectDialog.parentFolder || "").trim()
        : String(projectDialog.folder || "").trim();
      const picked = await postJson("/api/qimera/pick-folder", {
        description: target === "parentFolder" ? "Select the parent folder for the new Qimera project" : "Select an existing Qimera project folder",
        selectedPath,
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "Folder selection canceled.";
        render();
        return;
      }
      if (target === "parentFolder") {
        updateProjectDialogField("parentFolder", pickedPath);
        status = `Selected parent folder: ${pickedPath}`;
      } else {
        updateProjectDialogField("folder", pickedPath);
        if (!String(projectDialog.name || "").trim()) {
          updateProjectDialogField("name", inferProjectNameFromFolder(pickedPath));
        }
        status = `Selected project folder: ${pickedPath}`;
      }
      render();
    } catch (error) {
      status = `${error?.message || "Folder browsing failed."} If the host picker cannot open, enter the absolute path manually.`;
      render();
    }
  }

  async function browseForProjectCrsFile() {
    if (!projectDialog) return;
    try {
      const picked = await postJson("/api/qimera/pick-file", {
        title: "Select a QINSy DB or WKT file for project CRS",
        selectedPath: String(projectDialog.wktFile || "").trim(),
        filter: "QINSy Database or WKT (*.db;*.wkt)|*.db;*.wkt|QINSy Database (*.db)|*.db|WKT files (*.wkt)|*.wkt|All files (*.*)|*.*",
      }, 300000);
      const pickedPath = String(picked?.path || "").trim();
      if (!pickedPath || picked?.canceled) {
        status = "File selection canceled.";
        render();
        return;
      }
      updateProjectDialogField("wktFile", pickedPath);
      status = `Selected CRS source file: ${pickedPath}`;
      render();
    } catch (error) {
      status = `${error?.message || "File browsing failed."} If the host picker cannot open, enter the absolute path manually.`;
      render();
    }
  }

  async function runEnvironmentCheck() {
    pullStateFromForm();
    const currentNonce = ++environmentCheckNonce;
    environment = {
      ...createEnvironmentStatus(),
      state: "checking",
      summaryText: "Checking host-side Qimera Command capability...",
    };
    render();

    const checks = [];
    let machine = null;
    const machineUrl = String(state.qimeraStatusUrl || defaultState().qimeraStatusUrl).trim();
    if (machineUrl) {
      try {
        const payload = await fetchJsonWithTimeout(machineUrl);
        machine = normalizeMachineStatus(payload, machineUrl);
        if (machine.executablePath && (!String(state.qimeraExecutable || "").trim() || state.qimeraExecutable !== machine.executablePath)) {
          state.qimeraExecutable = machine.executablePath;
          persist();
        }
        checks.push({
          label: "Qimera Command capability",
          tone: machine.installed ? "ok" : "fail",
          detail: machine.installed
            ? `Qimera Command is available on the host machine${machine.version ? ` (${machine.version})` : ""}.`
            : "Qimera Command was not found on the host machine.",
        });
        checks.push({
          label: "Host license",
          tone: machine.licensed ? "ok" : "warn",
          detail: machine.licensed
            ? `A host license appears active${machine.licenseType ? ` (${machine.licenseType})` : ""}.`
            : "No active host license could be confirmed.",
        });
      } catch (error) {
        checks.push({
          label: "Qimera host check",
          tone: "fail",
          detail: `Could not read ${machineUrl}. ${error?.message || "Request failed."}`,
        });
      }
    } else {
      checks.push({
        label: "Qimera host check",
        tone: "fail",
        detail: "Host environment check URL is not configured.",
      });
    }

    let summaryTone = "warn";
    let summaryText = "Host-side Qimera Command capability is partially confirmed.";
    if (machine?.installed && machine?.licensed) {
      summaryTone = "ok";
      summaryText = "The host machine has Qimera Command available and a license appears active.";
    } else if (machine?.installed) {
      summaryTone = "ok";
      summaryText = "The host machine has Qimera Command available, but license activity was not confirmed.";
    } else if (checks.some((check) => check.tone === "fail")) {
      summaryTone = "fail";
      summaryText = "Hydrotools could not confirm host-side Qimera Command capability.";
    }

    if (currentNonce !== environmentCheckNonce) return;
    environment = {
      state: "ready",
      summaryTone,
      summaryText,
      checkedAt: new Date().toISOString(),
      checks,
      machine,
    };
    render();
  }

  function render() {
    if (!containerEl) return;
    sourceWorkspaceScrollSnapshot = getSourceWorkspaceScrollState();
    const sourceWorkspaceScroll = sourceWorkspaceScrollSnapshot;
    captureSectionState();
    syncAutoExportNames();
    const singleCommands = buildSingleCommands(state).join("\n\n");
    const configJson = buildConfigJson(state);
    const launcherCommand = buildLauncherCommand(state);
    const machine = environment.machine;
    if (machine?.executablePath && machine.executablePath !== state.qimeraExecutable) {
      state.qimeraExecutable = machine.executablePath;
      persist();
    }
    const activeProject = getActiveProject(state);
    const sourceItems = getActiveSourceItems();
    const gridItems = getActiveGridItems();
    const svpStrategySettings = getActiveSvpStrategySettings();
    const svpProfiles = Array.isArray(svpState.profiles) ? svpState.profiles : [];
    const svpCastRows = deriveSvpCastRows(svpProfiles, svpState.schedule);
    const svpCastByProfile = new Map();
    svpCastRows.forEach((row) => {
      const key = String(row.profileName || "").trim();
      if (!key) return;
      const prior = svpCastByProfile.get(key);
      if (!prior || (Number.isFinite(row.timeMs) && (!Number.isFinite(prior.timeMs) || row.timeMs < prior.timeMs))) {
        svpCastByProfile.set(key, row);
      }
    });
    const svpProfilesSorted = [...svpProfiles].sort((a, b) => {
      const aName = String(a?.name || "").trim();
      const bName = String(b?.name || "").trim();
      const aTime = svpCastByProfile.get(aName)?.timeMs;
      const bTime = svpCastByProfile.get(bName)?.timeMs;
      const aHas = Number.isFinite(aTime);
      const bHas = Number.isFinite(bTime);
      if (aHas && bHas && aTime !== bTime) return aTime - bTime;
      if (aHas !== bHas) return aHas ? -1 : 1;
      return aName.localeCompare(bName, undefined, { sensitivity: "base" });
    });
    const selectedSvpNames = Array.isArray(svpState.selectedProfileNames) && svpState.selectedProfileNames.length
      ? svpState.selectedProfileNames
      : (svpProfilesSorted[0]?.name ? [String(svpProfilesSorted[0].name)] : []);
    const selectedSvpNameSet = new Set(selectedSvpNames.map((name) => String(name || "").trim()));
    const selectedSvpProfiles = svpProfiles.filter((profile) => selectedSvpNames.includes(String(profile?.name || "")));
    const selectedSvpProfile = selectedSvpProfiles[0] || svpProfilesSorted[0] || null;
    const svpChartSvg = selectedSvpProfiles.length ? buildSvpProfileSvg(selectedSvpProfiles) : "";
    const svpCastMapSvg = buildSvpCastMapSvg(svpCastRows, selectedSvpNames);
    const allSvpAssignments = Array.isArray(svpState.assignments) ? svpState.assignments : [];
    const svpAssignmentsFiltered = selectedSvpNameSet.size
      ? allSvpAssignments.filter((row) => selectedSvpNameSet.has(String(row?.profileName || "").trim()))
      : allSvpAssignments;
    const activeGridName = getActiveGridName(state);
    const selectedSourceCount = getSelectedSourceExportNames(state).length;
    const automationSettings = getActiveAutomationSettings();
    const sourceWorkspaceDock = String(state.sourceWorkspaceDock || "bottom") === "side" ? "side" : "bottom";
    const sourcesWorkspaceHtml = `
      <div data-qimera-bottom-panel="${SOURCES_BOTTOM_PANEL_ID}" style="height:100%; min-height:0;">
        ${renderSourceWorkspaceMarkup({ activeProject, sourceItems, dock: "bottom" })}
      </div>
    `;
    containerEl.innerHTML = `
      <div style="padding:0;">
        <style>
        </style>
        <div class="lm-title">Qimera Command</div>
        <div class="small" style="margin-top:6px;">
          Frame out Qimera 2.8.2 automation workflows as validated commands and JSON configs for <code>qimera-command</code>.
        </div>

        <details class="toolset-group" data-section-key="environment" ${isSectionOpen("environment") ? "open" : ""} style="margin-top:8px;">
          <summary>Environment</summary>
          <div class="toolset-content">
            <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:10px; margin-top:4px; flex-wrap:wrap;">
              <div style="min-width:260px; flex:1;">
                <div class="small" style="font-size:12px; font-weight:600;">${statusBadge(environment.state === "checking" ? "Checking" : environment.summaryTone === "ok" ? "Ready" : environment.summaryTone === "fail" ? "Attention" : "Partial", environment.state === "checking" ? "warn" : environment.summaryTone)}</div>
                <div class="small" style="margin-top:6px;">${esc(environment.summaryText)}</div>
                <div class="small" style="margin-top:6px; opacity:.8;">${esc(environment.checkedAt ? `Last checked ${formatTimestamp(environment.checkedAt)}` : "No environment check run yet.")}</div>
              </div>
              <div class="row lm-add-row" style="margin-top:0;">
                <button id="qimera-check-environment" class="btn primary"${environment.state === "checking" ? " disabled" : ""}>${environment.state === "checking" ? "Checking..." : "Check Environment"}</button>
              </div>
            </div>
            <div style="display:grid; gap:8px; margin-top:10px;">
              ${environment.checks.map((check) => `
                <div style="padding:8px 10px; border-radius:10px; ${statusToneStyle(check.tone)}">
                  <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">
                    ${statusBadge(check.label, check.tone)}
                  </div>
                  <div class="small" style="margin-top:6px;">${esc(check.detail)}</div>
                </div>
              `).join("")}
            </div>
            ${machine ? `
              <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.2); background:rgba(15,23,42,.22);">
                <div class="small" style="font-weight:600;">Host details</div>
                <div class="small" style="margin-top:6px;">Installed: ${machine.installed ? "yes" : "no"} | Licensed: ${machine.licensed ? "yes" : "no"}${machine.version ? ` | Version: ${esc(machine.version)}` : ""}</div>
                ${machine.executablePath ? `<div class="small" style="margin-top:4px;">Executable: <code>${esc(machine.executablePath)}</code></div>` : ""}
                ${machine.licenseType ? `<div class="small" style="margin-top:4px;">License type: ${esc(machine.licenseType)}</div>` : ""}
                ${machine.features.length ? `<div class="small" style="margin-top:4px;">Features: ${esc(machine.features.join(", "))}</div>` : ""}
                <div class="small" style="margin-top:4px; opacity:.85;">License status is inferred from the host's Sentinel runtime and attached local key. Qimera Command itself does not currently report license entitlements directly.</div>
                ${machine.detail ? `<div class="small" style="margin-top:4px;">${esc(machine.detail)}</div>` : ""}
              </div>
            ` : ""}
          </div>
        </details>

        <details class="toolset-group" data-section-key="project" ${isSectionOpen("project") ? "open" : ""} style="margin-top:8px;">
          <summary>Project</summary>
          <div class="toolset-content">
            <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap; margin-top:4px;">
              <div>
                <div class="small" style="font-weight:600;">Project registry</div>
                <div class="small" style="margin-top:4px;">Select one active project. Downstream commands use that project folder and settings.</div>
              </div>
              <div class="row lm-add-row" style="margin-top:0;">
                <button id="qimera-new-project" class="btn">Create New Project</button>
                <button id="qimera-open-project" class="btn">Open Existing Project</button>
              </div>
            </div>
            ${projectDialog ? `
              <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.2); background:rgba(15,23,42,.22);">
                <div class="small" style="font-weight:600;">${projectDialog.mode === "create" ? "Create project entry" : "Open project entry"}</div>
                <div class="small" style="margin-top:4px;">${projectDialog.mode === "create" ? `New projects are created under the host-managed project root${machine?.projectRoot ? `: ${machine.projectRoot}` : ""}.` : `Existing projects are discovered from the host-managed project root${machine?.projectRoot ? `: ${machine.projectRoot}` : ""}.`}</div>
                ${projectDialog.mode === "create" ? `
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Project name</label>
                    <input id="qimera-project-dialog-name" type="text" value="${esc(projectDialog.name)}" placeholder="MANS_20250724_ANCH_24" style="flex:1;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:6px;">
                    <label class="small" style="min-width:92px;">CRS source</label>
                    <select id="qimera-project-dialog-crs-mode" style="width:220px;">
                      <option value="epsg"${projectDialog.crsMode === "epsg" ? " selected" : ""}>Use EPSG code</option>
                      <option value="wkt-file"${projectDialog.crsMode === "wkt-file" ? " selected" : ""}>Derive from DB / WKT file</option>
                    </select>
                  </div>
                  <div class="row lm-add-row" style="margin-top:6px;">
                    <label class="small" style="min-width:92px;">${projectDialog.crsMode === "wkt-file" ? "DB / WKT" : "EPSG"}</label>
                    ${projectDialog.crsMode === "wkt-file"
                      ? `<input id="qimera-project-dialog-wkt-file" type="text" value="${esc(projectDialog.wktFile)}" placeholder="C:\\HydroShare\\MANS_20250724_ANCH_24\\DBs\\0001 - Example.db" style="flex:1;" /><button id="qimera-project-dialog-pick-wkt-file" class="btn">Browse...</button>`
                      : `<input id="qimera-project-dialog-epsg" type="text" value="${esc(projectDialog.epsgCode)}" placeholder="26910" style="width:120px;" />`
                    }
                  </div>
                  <div class="row lm-add-row" style="margin-top:6px;">
                    <label class="small" style="min-width:92px;">Survey type</label>
                    <input id="qimera-project-dialog-survey-type" type="text" value="${esc(projectDialog.surveyType)}" placeholder="multibeam" style="flex:1;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:6px;">
                    <label class="small" style="min-width:92px;">Description</label>
                    <input id="qimera-project-dialog-description" type="text" value="${esc(projectDialog.description)}" placeholder="Project description" style="flex:1;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:6px;">
                    <label class="small" style="min-width:92px;">Final folder</label>
                    <input id="qimera-project-dialog-folder" type="text" value="${esc(joinPathParts(machine?.projectRoot || "", projectDialog.name || ""))}" readonly style="flex:1; opacity:.88;" />
                  </div>
                ` : `
                  <div style="margin-top:8px; max-height:240px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px;">
                    ${projectDialog.loading ? `
                      <div class="small" style="padding:10px 12px;">Loading host projects...</div>
                    ` : projectDialog.availableProjects.length ? `
                      <table style="width:100%; border-collapse:collapse; font-size:12px;">
                        <thead>
                          <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                            <th style="padding:6px 8px;">Project</th>
                            <th style="padding:6px 8px;">Folder</th>
                            <th style="padding:6px 8px;">Open</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${projectDialog.availableProjects.map((project) => `
                            <tr style="border-bottom:1px solid rgba(148,163,184,.12);">
                              <td style="padding:6px 8px;">${esc(project.name)}</td>
                              <td style="padding:6px 8px;"><code>${esc(project.folder)}</code></td>
                              <td style="padding:6px 8px;"><button class="btn" data-managed-project-open="${esc(project.folder)}">Open</button></td>
                            </tr>
                          `).join("")}
                        </tbody>
                      </table>
                    ` : `
                      <div class="small" style="padding:10px 12px;">No projects were found under the host project root.</div>
                    `}
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <button id="qimera-project-dialog-refresh" class="btn">Refresh List</button>
                  </div>
                `}
                <div class="small" style="margin-top:6px; opacity:.85;">Project storage is managed on the host now, so remote clients do not choose filesystem paths directly.</div>
                ${projectDialog.error ? `<div style="margin-top:8px; padding:8px 10px; border-radius:8px; ${statusToneStyle("fail")}">${esc(projectDialog.error)}</div>` : ""}
                <div class="row lm-add-row" style="margin-top:8px;">
                  ${projectDialog.mode === "create" ? `<button id="qimera-project-dialog-save" class="btn primary"${projectDialog.submitting ? " disabled" : ""}>${projectDialog.submitting ? "Creating..." : "Create Project"}</button>` : ""}
                  <button id="qimera-project-dialog-cancel" class="btn">Cancel</button>
                </div>
              </div>
            ` : ""}
            <div style="margin-top:10px; overflow:auto;">
              <table style="width:100%; border-collapse:collapse; font-size:12px;">
                <thead>
                  <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                    <th style="padding:6px 8px;">Active</th>
                    <th style="padding:6px 8px;">Project</th>
                    <th style="padding:6px 8px;">Host</th>
                    <th style="padding:6px 8px;">Folder</th>
                    <th style="padding:6px 8px;">Mode</th>
                    <th style="padding:6px 8px;">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  ${state.projects.length ? state.projects.map((project) => `
                    <tr style="border-bottom:1px solid rgba(148,163,184,.12); ${project.missingOnHost ? "background:rgba(248,113,113,.10);" : project.id === activeProject?.id ? "background:rgba(34,197,94,.06);" : ""}">
                      <td style="padding:6px 8px;"><input type="radio" name="qimera-active-project" data-project-select="${esc(project.id)}" ${project.id === activeProject?.id ? "checked" : ""} /></td>
                      <td style="padding:6px 8px;">${esc(project.name)}${project.missingOnHost ? `<div class="small" style="margin-top:4px; color:#fca5a5;">Missing on host</div>` : ""}</td>
                      <td style="padding:6px 8px;">${esc(project.host)}</td>
                      <td style="padding:6px 8px;"><code>${esc(project.folder)}</code></td>
                      <td style="padding:6px 8px;">${esc(project.mode === "new" ? "new" : "existing")}${project.missingOnHost ? `<div style="margin-top:4px;">${statusBadge("Missing", "fail")}</div>` : ""}</td>
                      <td style="padding:6px 8px; display:flex; gap:6px; flex-wrap:wrap;">
                        ${project.missingOnHost ? `<button class="btn" data-project-update-path="${esc(project.id)}">Update Path</button>` : ""}
                        <button class="btn" data-project-remove="${esc(project.id)}">${project.missingOnHost ? "Remove" : "Remove"}</button>
                      </td>
                    </tr>
                  `).join("") : `
                    <tr>
                      <td colspan="6" style="padding:10px 8px;" class="small">No projects added yet. Start with Create New Project or Open Existing Project.</td>
                    </tr>
                  `}
                </tbody>
              </table>
            </div>
            <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
              <div class="small" style="font-weight:600;">Active project settings</div>
              <div class="small" style="margin-top:4px;">${esc(activeProject ? `Displaying settings for ${activeProject.name}` : "Select a project to view its settings.")}</div>
              <div class="row lm-add-row" style="margin-top:8px;">
                <label class="small" style="min-width:92px;">Host</label>
                <input type="text" value="${esc(activeProject?.host || "")}" readonly style="flex:1; opacity:.88;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px;">
                <label class="small" style="min-width:92px;">Folder</label>
                <input type="text" value="${esc(activeProject?.folder || "")}" readonly style="flex:1; opacity:.88;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px;">
                <label class="small" style="min-width:92px;">CRS</label>
                <input type="text" value="${esc(activeProject?.crsMode === "wkt-file" ? "Derived from DB / WKT file" : (activeProject?.epsgCode ? `EPSG ${activeProject.epsgCode}` : ""))}" style="flex:1; opacity:.88;" readonly />
              </div>
              ${activeProject?.crsMode === "wkt-file" && activeProject?.wktFile ? `
              <div class="row lm-add-row" style="margin-top:6px;">
                <label class="small" style="min-width:92px;">DB / WKT</label>
                <input type="text" value="${esc(activeProject.wktFile)}" style="flex:1; opacity:.88;" readonly />
              </div>
              ` : ""}
              <div class="row lm-add-row" style="margin-top:6px;">
                <label class="small" style="min-width:92px;">Survey type</label>
                <input type="text" value="${esc(activeProject?.surveyType || "")}" style="flex:1; opacity:.88;" readonly />
              </div>
              <div class="row lm-add-row" style="margin-top:6px;">
                <label class="small" style="min-width:92px;">Description</label>
                <input type="text" value="${esc(activeProject?.description || "")}" style="flex:1; opacity:.88;" readonly />
              </div>
              <div class="row lm-add-row" style="margin-top:8px; align-items:center;">
                <label class="small" style="min-width:92px;">Tracklines</label>
                <div style="display:flex; gap:8px; flex-wrap:wrap; align-items:center; flex:1;">
                  <button id="qimera-load-tracklines" class="btn"${tracklineState.loading || !activeProject ? " disabled" : ""}>${tracklineState.loading ? "Loading..." : "Load Tracklines"}</button>
                  <button id="qimera-clear-tracklines" class="btn"${!activeProject ? " disabled" : ""}>Clear Tracklines</button>
                  <div class="small" style="opacity:.82;">
                    ${
                      tracklineState.error
                        ? esc(tracklineState.error)
                        : tracklineState.lastLoadedAt
                          ? esc(`${tracklineState.featureCount} trackline${tracklineState.featureCount === 1 ? "" : "s"} loaded. Last run ${formatTimestamp(tracklineState.lastLoadedAt)}.`)
                          : "Load tracklines from the Qimera project metadata .sd files."
                    }
                  </div>
                </div>
              </div>
              <div class="small" style="margin-top:6px; opacity:.8;">Survey type and description are Qimera create-project fields. To change them, create a new project with the desired values.</div>
            </div>
          </div>
        </details>

        <details class="toolset-group" data-section-key="import" ${isSectionOpen("import") ? "open" : ""} style="margin-top:8px;">
          <summary>Sources</summary>
          <div class="toolset-content">
            <div class="small" style="margin-top:4px;">Add raw sonar source data to the active Qimera project. This is the next host-side step after project creation.</div>
            <div class="row lm-add-row" style="margin-top:4px;">
              <label class="small" style="min-width:92px;">Format</label>
              <select id="qimera-import-format" style="width:180px;">
                <option value="kmall"${state.importFormat === "kmall" ? " selected" : ""}>KMALL</option>
                <option value="all"${state.importFormat === "all" ? " selected" : ""}>Kongsberg .all</option>
                <option value="s7k"${state.importFormat === "s7k" ? " selected" : ""}>S7K</option>
                <option value="db"${state.importFormat === "db" ? " selected" : ""}>QINSy DB</option>
              </select>
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:92px;">Folder path</label>
              <input id="qimera-input-path" type="text" value="${esc(activeProject?.sourceFolder || state.inputPath)}" placeholder="C:\\HydroShare\\SurveyFolder" style="flex:1;" readonly />
              <button id="qimera-source-folder-pick" class="btn">Browse...</button>
              <button id="qimera-source-folder-load" class="btn"${sourceBusy.loading ? " disabled" : ""}>${sourceBusy.loading ? "Loading..." : "Load Lines"}</button>
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:92px;">Vessel</label>
              <input id="qimera-vessel-name" type="text" value="${esc(state.vesselName)}" placeholder="Optional vessel name override" style="flex:1;" />
            </div>
            <details class="toolset-group" data-section-key="sourceAutomation" ${isSectionOpen("sourceAutomation") ? "open" : ""} style="margin-top:10px;">
              <summary>Automation</summary>
              <div class="toolset-content" style="padding-top:8px;">
                <div class="row lm-add-row" style="margin-top:0; flex-wrap:wrap;">
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-monitor-source-folder" type="checkbox"${automationSettings.monitorSourceFolder ? " checked" : ""} />
                    Monitor source folder
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-auto-import-sources" type="checkbox"${automationSettings.autoImportNewSources ? " checked" : ""}${automationSettings.monitorSourceFolder ? "" : " disabled"} />
                    Auto-import new lines
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-auto-process-sources" type="checkbox"${automationSettings.autoProcessNewSources ? " checked" : ""}${automationSettings.autoImportNewSources ? "" : " disabled"} />
                    Auto-process imported lines
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-auto-filter-sources" type="checkbox"${automationSettings.autoFilterSources ? " checked" : ""}${automationSettings.monitorSourceFolder && automationSettings.selectedFilterProfile ? "" : " disabled"} />
                    Auto-filter imported lines
                  </label>
                </div>
                <div class="row lm-add-row" style="margin-top:8px; flex-wrap:wrap;">
                  <label class="small" style="min-width:92px;">Scan every</label>
                  <select id="qimera-source-scan-interval" style="width:110px;"${automationSettings.monitorSourceFolder ? "" : " disabled"}>
                    <option value="15"${automationSettings.sourceScanIntervalSec === 15 ? " selected" : ""}>15s</option>
                    <option value="30"${automationSettings.sourceScanIntervalSec === 30 ? " selected" : ""}>30s</option>
                    <option value="60"${automationSettings.sourceScanIntervalSec === 60 ? " selected" : ""}>60s</option>
                    <option value="300"${automationSettings.sourceScanIntervalSec === 300 ? " selected" : ""}>5m</option>
                  </select>
                  <button id="qimera-run-source-scan" class="btn"${sourceAutomation.running || !activeProject?.sourceFolder ? " disabled" : ""}>${sourceAutomation.running ? "Scanning..." : "Scan Now"}</button>
                </div>
                <div class="small" style="margin-top:8px; opacity:.88;">
                  ${esc(
                    sourceAutomation.error
                      ? sourceAutomation.error
                      : automationSettings.monitorSourceFolder
                        ? `${sourceAutomation.running ? "Watching source folder." : "Source monitoring is active."}${automationSettings.selectedFilterProfile ? ` Filter profile: ${automationSettings.selectedFilterProfile}.` : ""}${sourceAutomation.lastScanAt ? ` Last scan ${formatTimestamp(sourceAutomation.lastScanAt)}.` : ""}${sourceAutomation.message ? ` ${sourceAutomation.message}` : ""}`
                        : "Automation is off. Enable monitoring to watch the source folder for new DB files."
                  )}
                </div>
              </div>
            </details>
            <details class="toolset-group" data-section-key="sourceFilters" ${isSectionOpen("sourceFilters") ? "open" : ""} style="margin-top:8px;">
              <summary>Filters</summary>
              <div class="toolset-content" style="padding-top:8px;">
                <div class="small" style="margin-top:0;">Apply a predefined Qimera filter profile to selected imported source lines.</div>
                <div class="row lm-add-row" style="margin-top:8px; flex-wrap:wrap;">
                  <label class="small" style="min-width:92px;">Profile</label>
                  <select id="qimera-filter-profile" style="min-width:240px; flex:1;">
                    <option value="">Choose a filter profile</option>
                    ${FILTER_PROFILE_OPTIONS.map((profile) => `<option value="${esc(profile)}"${state.selectedFilterProfile === profile ? " selected" : ""}>${esc(profile)}</option>`).join("")}
                  </select>
                  <button id="qimera-apply-filter-selected" class="btn"${sourceBusy.filtering || !activeProject || !String(state.selectedFilterProfile || "").trim() ? " disabled" : ""}>${sourceBusy.filtering ? "Applying..." : "Apply To Selected"}</button>
                </div>
                <div class="small" style="margin-top:8px; opacity:.85;">The selected profile is also used by source automation when Auto-filter imported lines is enabled.</div>
              </div>
            </details>
            ${sourceWorkspaceDock === "side" ? `
              ${renderSourceWorkspaceMarkup({ activeProject, sourceItems, dock: "side" })}
            ` : `
              <div style="margin-top:10px; padding:12px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
                <div style="display:flex; align-items:center; justify-content:space-between; gap:10px; flex-wrap:wrap;">
                  <div>
                    <div class="small" style="font-weight:600;">Source workspace docked to bottom panel</div>
                    <div class="small" style="margin-top:4px; color:#cbd5e1;">
                      ${esc(activeProject ? `${sourceItems.length} loaded line${sourceItems.length === 1 ? "" : "s"} for ${activeProject.name}. Open the bottom panel workspace for selection, import, process, append, and per-line status.` : "Load a source folder, then use the bottom panel workspace to work the line list.")}
                    </div>
                  </div>
                  <div class="row lm-add-row" style="margin-top:0;">
                    <label class="small">Dock</label>
                    <select id="qimera-source-workspace-dock" style="width:120px;">
                      <option value="bottom" selected>Bottom</option>
                      <option value="side">Sidebar</option>
                    </select>
                    <button id="qimera-open-source-workspace" class="btn">Open Source Workspace</button>
                  </div>
                </div>
              </div>
            `}
          </div>
        </details>

        <details class="toolset-group" data-section-key="sbet" ${isSectionOpen("sbet") ? "open" : ""} style="margin-top:8px;">
          <summary>SBET</summary>
          <div class="toolset-content">
            <div class="small" style="margin-top:4px;">
              Prepare POSPac SBET import for the active project. Hydrotools validates the SBET/RMS pair and opens the project in Qimera for the binary navigation import step.
            </div>
            <div class="row lm-add-row" style="margin-top:10px;">
              <label class="small" style="min-width:110px;">SBET file</label>
              <input id="qimera-sbet-file" type="text" value="${esc(activeProject?.sbetFile || state.sbetFile || "")}" placeholder="C:\\POSPac\\Project\\line_sbet.out" style="flex:1;" readonly />
              <button id="qimera-sbet-pick-file" class="btn"${!activeProject ? " disabled" : ""}>Browse...</button>
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:110px;">RMS file</label>
              <input id="qimera-sbet-rms-file" type="text" value="${esc(activeProject?.sbetRmsFile || state.sbetRmsFile || "")}" placeholder="C:\\POSPac\\Project\\line_smrmsg.out" style="flex:1;" readonly />
              <button id="qimera-sbet-pick-rms-file" class="btn"${!activeProject ? " disabled" : ""}>Browse...</button>
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:110px;">GPS week</label>
              <input id="qimera-sbet-gps-week" type="date" value="${esc(activeProject?.sbetGpsWeek || state.sbetGpsWeek || "")}" style="width:180px;" />
              <div class="small" style="opacity:.85;">Choose the Sunday for the week the SBET was logged.</div>
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:110px;">Acq. CRS</label>
              <input id="qimera-sbet-coordinate-system" type="text" value="${esc(activeProject?.sbetCoordinateSystem || state.sbetCoordinateSystem || activeProject?.epsgCode ? `EPSG ${activeProject?.epsgCode || ""}` : "")}" placeholder="EPSG 26910 or NAD83 / UTM zone 10N" style="flex:1;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <label style="display:flex; align-items:center; gap:8px;">
                <input id="qimera-sbet-priority" type="checkbox" ${activeProject?.sbetPriority !== false ? "checked" : ""} />
                Prioritize imported navigation in Qimera
              </label>
            </div>
            <div class="row lm-add-row" style="margin-top:10px;">
              <button id="qimera-sbet-validate" class="btn"${sbetBusy.validating || !activeProject ? " disabled" : ""}>${sbetBusy.validating ? "Validating..." : "Validate Pair"}</button>
              <button id="qimera-sbet-launch" class="btn primary"${sbetBusy.launching || !activeProject ? " disabled" : ""}>${sbetBusy.launching ? "Launching..." : "Open In Qimera"}</button>
            </div>
            <div class="small" style="margin-top:8px; ${sbetStatus.ok ? "color:#86efac;" : sbetStatus.message ? "color:#fbbf24;" : "opacity:.85;"}">
              ${esc(sbetStatus.message || "Qimera imports SBET through Add Binary Navigation Files. Hydrotools validates filenames and opens the active project for that step.")}
              ${sbetStatus.checkedAt ? ` Last checked ${esc(formatTimestamp(sbetStatus.checkedAt))}.` : ""}
            </div>
            ${sbetStatus.details ? `
              <div class="small" style="margin-top:8px; padding:8px 10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.22);">
                <div>SBET base name valid: ${sbetStatus.details.sbetBaseNameValid ? "yes" : "no"}</div>
                <div>Matching RMS found: ${sbetStatus.details.autoDetectedRms ? "yes" : "no"}</div>
                ${sbetStatus.details.warning ? `<div style="margin-top:4px; color:#fbbf24;">${esc(sbetStatus.details.warning)}</div>` : ""}
                ${sbetStatus.details.nextStep ? `<div style="margin-top:4px;">${esc(sbetStatus.details.nextStep)}</div>` : ""}
              </div>
            ` : ""}
          </div>
        </details>

        <details class="toolset-group" data-section-key="svp" ${isSectionOpen("svp") ? "open" : ""} style="margin-top:8px;">
          <summary>SVP</summary>
          <div class="toolset-content">
            <div class="small" style="margin-top:4px;">
              Inspect the resolved Qimera sound velocity profiles from the project <code>.bsvp</code> files and the project SVP schedule.
            </div>
            <div class="row lm-add-row" style="margin-top:10px;">
              <button id="qimera-refresh-svp" class="btn primary"${svpState.loading || !activeProject ? " disabled" : ""}>${svpState.loading ? "Loading..." : "Load SVP"}</button>
            </div>
            <div class="row lm-add-row" style="margin-top:8px; gap:10px; flex-wrap:wrap; align-items:flex-end;">
              <label class="small" style="display:flex; flex-direction:column; gap:4px;">
                Strategy
                <select id="qimera-svp-strategy" style="min-width:220px;">
                  ${SVP_ASSIGNMENT_STRATEGY_OPTIONS.map((option) => `
                    <option value="${esc(option.value)}"${svpStrategySettings.strategy === option.value ? " selected" : ""}>${esc(option.label)}</option>
                  `).join("")}
                </select>
              </label>
              <label class="small" style="display:flex; flex-direction:column; gap:4px;">
                Max time (hours)
                <input id="qimera-svp-max-time-hours" type="number" min="0" step="0.1" placeholder="none" value="${esc(svpStrategySettings.maxTimeHours)}" style="width:130px;" />
              </label>
              <label class="small" style="display:flex; flex-direction:column; gap:4px;">
                Max distance (nm)
                <input id="qimera-svp-max-distance-nm" type="number" min="0" step="0.1" placeholder="none" value="${esc(svpStrategySettings.maxDistanceNm)}" style="width:130px;" />
              </label>
              <button id="qimera-svp-apply-strategy" class="btn primary" style="align-self:flex-end;"${svpState.loading || !activeProject ? " disabled" : ""}>Apply SVP Strategy</button>
              <button id="qimera-svp-select-affected" class="btn" style="align-self:flex-end;">Select Affected Lines</button>
            </div>
            <div class="small" style="margin-top:6px; opacity:.78;">Adjust settings, then click Apply SVP Strategy to reload assignments and mark affected imported lines for reprocess.</div>
            <div class="small" style="margin-top:8px; ${svpState.error ? "color:#fca5a5;" : "opacity:.85;"}">
              ${esc(svpState.error || (svpState.lastLoadedAt ? `Loaded ${svpProfiles.length} profile${svpProfiles.length === 1 ? "" : "s"} from ${activeProject?.name || "the active project"}. Last run ${formatTimestamp(svpState.lastLoadedAt)}.` : "No SVP profiles loaded yet."))}
            </div>
            ${svpProfiles.length ? `
              <details class="toolset-group" data-section-key="svpProfileView" ${isSectionOpen("svpProfileView") ? "open" : ""} style="margin-top:10px;">
                <summary>Profile View</summary>
                <div class="toolset-content">
                  <div class="small" style="opacity:.82;">
                    ${esc(`Profiles ${svpProfiles.length} | Schedule entries ${svpState.schedule.length || 0} | Assigned lines ${svpState.assignments.filter((row) => String(row.profileName || "").trim()).length || 0} | Cast locations ${svpCastRows.filter((row) => Number.isFinite(row.lat) && Number.isFinite(row.lon)).length}`)}
                  </div>
                  <div class="small" style="margin-top:8px; opacity:.82;">${esc(selectedSvpProfiles.length ? `${selectedSvpProfiles.length} selected profile${selectedSvpProfiles.length === 1 ? "" : "s"}.` : "No profiles selected.")}${selectedSvpProfile ? ` ${esc(`${selectedSvpProfile.pointCount || 0} points | ${Number(selectedSvpProfile.minDepth || 0).toFixed(0)}-${Number(selectedSvpProfile.maxDepth || 0).toFixed(0)} depth | ${formatSoundSpeed(selectedSvpProfile.minSoundSpeed)}-${formatSoundSpeed(selectedSvpProfile.maxSoundSpeed)} speed`)}` : ""}</div>
                  ${svpChartSvg ? `<div style="margin-top:10px;">${svpChartSvg}</div>` : ""}
                  ${svpCastMapSvg ? `<div style="margin-top:10px;">${svpCastMapSvg}</div>` : `<div class="small" style="margin-top:10px; opacity:.75;">No cast locations found in current SVP payload.</div>`}
                </div>
              </details>
              <details class="toolset-group" data-section-key="svpProfileList" ${isSectionOpen("svpProfileList") ? "open" : ""} style="margin-top:10px;">
                <summary>Profile List</summary>
                <div class="toolset-content">
                  <div class="small" style="opacity:.82;">Click a profile to plot it. Use Ctrl/Cmd-click to add or remove profiles, and Shift-click for a range.</div>
                  <div style="max-height:220px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px; margin-top:8px;">
                    <table style="width:100%; border-collapse:collapse; font-size:12px;">
                      <thead>
                        <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                          <th style="padding:6px 8px;">Profile</th>
                          <th style="padding:6px 8px;">Date/Time</th>
                          <th style="padding:6px 8px;">Location</th>
                          <th style="padding:6px 8px;">Points</th>
                          <th style="padding:6px 8px;">Depth</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${svpProfilesSorted.map((profile) => {
                          const cast = svpCastByProfile.get(String(profile?.name || "").trim()) || null;
                          const castTimeText = Number.isFinite(cast?.timeMs) ? formatTimestamp(new Date(cast.timeMs).toISOString()) : "";
                          const castLocationText = Number.isFinite(cast?.lat) && Number.isFinite(cast?.lon) ? `${Number(cast.lat).toFixed(5)}, ${Number(cast.lon).toFixed(5)}` : "";
                          return `
                          <tr data-svp-profile-row="${esc(profile.name)}" style="cursor:pointer; border-bottom:1px solid rgba(148,163,184,.12); ${selectedSvpNames.includes(profile.name) ? "background:rgba(56,189,248,.12);" : ""}">
                            <td style="padding:6px 8px;">${esc(profile.name)}</td>
                            <td style="padding:6px 8px;">${esc(castTimeText)}</td>
                            <td style="padding:6px 8px;">${esc(castLocationText)}</td>
                            <td style="padding:6px 8px;">${esc(String(profile.pointCount || 0))}</td>
                            <td style="padding:6px 8px;">${esc(`${Number(profile.minDepth || 0).toFixed(0)} - ${Number(profile.maxDepth || 0).toFixed(0)}`)}</td>
                          </tr>
                        `;
                        }).join("")}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
              <details class="toolset-group" data-section-key="svpLineAssignments" ${isSectionOpen("svpLineAssignments") ? "open" : ""} style="margin-top:10px;">
                <summary>Line Assignments</summary>
                <div class="toolset-content">
                  <div class="small" style="opacity:.82; margin-bottom:8px;">
                    ${esc(`Showing ${svpAssignmentsFiltered.length} of ${allSvpAssignments.length} assignment${allSvpAssignments.length === 1 ? "" : "s"} for selected profile${selectedSvpNames.length === 1 ? "" : "s"}.`)}
                  </div>
                  <div style="max-height:220px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px;">
                    <table style="width:100%; border-collapse:collapse; font-size:12px;">
                      <thead>
                        <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                          <th style="padding:6px 8px;">Line</th>
                          <th style="padding:6px 8px;">Profile</th>
                          <th style="padding:6px 8px;">Overlap</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${svpAssignmentsFiltered.length ? svpAssignmentsFiltered.map((row) => `
                          <tr style="border-bottom:1px solid rgba(148,163,184,.12);">
                            <td style="padding:6px 8px;">${esc(row.sourceName || "")}</td>
                            <td style="padding:6px 8px;">${esc(row.profileName || "Unassigned")}</td>
                            <td style="padding:6px 8px;">${esc(row.overlapSeconds ? `${Math.round(Number(row.overlapSeconds || 0))} s` : "")}</td>
                          </tr>
                        `).join("") : `
                          <tr><td colspan="3" style="padding:8px;" class="small">No assignments found for the selected profile(s).</td></tr>
                        `}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            ` : ""}
          </div>
        </details>
        <details class="toolset-group" data-section-key="metrics" ${isSectionOpen("metrics") ? "open" : ""} style="margin-top:8px;">
          <summary>Metrics</summary>
          <div class="toolset-content">
            <div class="small" style="margin-top:4px;">
              Project reporting and line metrics derived from the active source list and decoded Qimera tracklines.
            </div>
            <div class="row lm-add-row" style="margin-top:10px;">
              <button id="qimera-refresh-metrics" class="btn primary"${metricsState.loading || !activeProject ? " disabled" : ""}>${metricsState.loading ? "Calculating..." : "Calculate Metrics"}</button>
            </div>
            ${metricsState.summary ? `
              <div style="display:grid; grid-template-columns:repeat(auto-fit, minmax(150px, 1fr)); gap:8px; margin-top:10px;">
                <div style="padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.22);">
                  <div class="small" style="opacity:.8;">Lines</div>
                  <div style="font-size:18px; font-weight:700; margin-top:4px;">${esc(String(metricsState.summary.totalLines || 0))}</div>
                </div>
                <div style="padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.22);">
                  <div class="small" style="opacity:.8;">Imported</div>
                  <div style="font-size:18px; font-weight:700; margin-top:4px;">${esc(String(metricsState.summary.importedLines || 0))}</div>
                </div>
                <div style="padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.22);">
                  <div class="small" style="opacity:.8;">Processed</div>
                  <div style="font-size:18px; font-weight:700; margin-top:4px;">${esc(String(metricsState.summary.processedLines || 0))}</div>
                </div>
                <div style="padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.22);">
                  <div class="small" style="opacity:.8;">Appended</div>
                  <div style="font-size:18px; font-weight:700; margin-top:4px;">${esc(String(metricsState.summary.appendedLines || 0))}</div>
                </div>
                <div style="padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.22);">
                  <div class="small" style="opacity:.8;">Total mileage</div>
                  <div style="font-size:18px; font-weight:700; margin-top:4px;">${esc(formatDistanceNm(metricsState.summary.totalMileageNm) || "0 nm")}</div>
                </div>
                <div style="padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.22);">
                  <div class="small" style="opacity:.8;">Processed mileage</div>
                  <div style="font-size:18px; font-weight:700; margin-top:4px;">${esc(formatDistanceNm(metricsState.summary.processedMileageNm) || "0 nm")}</div>
                </div>
              </div>
            ` : ""}
            <div class="small" style="margin-top:8px; ${metricsState.error ? "color:#fca5a5;" : "opacity:.85;"}">
              ${esc(metricsState.error || (metricsState.lastCalculatedAt ? `Last calculated ${formatTimestamp(metricsState.lastCalculatedAt)}.` : "No metrics calculated yet."))}
            </div>
            ${metricsState.rows.length ? `
              <div style="margin-top:10px; max-height:260px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px;">
                <table style="width:100%; border-collapse:collapse; font-size:12px;">
                  <thead>
                    <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                      <th style="padding:6px 8px;">Line</th>
                      <th style="padding:6px 8px;">State</th>
                      <th style="padding:6px 8px;">Mileage</th>
                      <th style="padding:6px 8px;">Points</th>
                      <th style="padding:6px 8px;">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${metricsState.rows.map((row) => `
                      <tr style="border-bottom:1px solid rgba(148,163,184,.12);">
                        <td style="padding:6px 8px;">${esc(row.name)}</td>
                        <td style="padding:6px 8px;">${esc(row.state)}</td>
                        <td style="padding:6px 8px;">${esc(formatDistanceNm(row.distanceNm) || "")}</td>
                        <td style="padding:6px 8px;">${esc(String(row.points || 0))}</td>
                        <td style="padding:6px 8px;">${esc(formatBytes(row.sizeBytes) || "")}</td>
                      </tr>
                    `).join("")}
                  </tbody>
                </table>
              </div>
            ` : ""}
          </div>
        </details>

        <details class="toolset-group" data-section-key="grid" ${isSectionOpen("grid") ? "open" : ""} style="margin-top:8px;">
          <summary>Grid</summary>
          <div class="toolset-content">
            <div class="row lm-add-row" style="margin-top:4px;">
              <label class="small" style="min-width:92px;">Grid name</label>
              <input id="qimera-grid-name" type="text" value="${esc(state.gridName)}" style="flex:1;" />
              <label class="small" style="min-width:92px; margin-left:8px;">Cell size</label>
              <input id="qimera-cell-size" type="text" value="${esc(state.cellSize)}" style="width:100px;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px; align-items:center; gap:10px; flex-wrap:wrap;">
              <label class="small" style="min-width:92px;">Grid mode</label>
              <select id="qimera-grid-mode" style="min-width:180px;">
                <option value="cube"${String(state.gridMode || "cube") === "cube" ? " selected" : ""}>Dynamic grid with CUBE</option>
                <option value="standard"${String(state.gridMode || "cube") === "standard" ? " selected" : ""}>Standard dynamic grid</option>
              </select>
              <label class="small" style="display:flex; align-items:center; gap:6px; margin-left:8px;">
                <input id="qimera-grid-exclude-coverage" type="checkbox" ${state.excludeGridCoverage ? "checked" : ""} />
                Exclude coverage
              </label>
            </div>
            ${String(state.gridMode || "cube") === "cube" ? `
            <div class="row lm-add-row" style="margin-top:6px; align-items:center; gap:10px; flex-wrap:wrap;">
              <label class="small" style="min-width:92px;">CUBE profile</label>
              <select id="qimera-grid-profile" style="min-width:180px;">
                ${GRID_PROFILE_OPTIONS.map((option) => `<option value="${esc(option)}"${String(state.gridProfile || "Default") === option ? " selected" : ""}>${esc(option)}</option>`).join("")}
              </select>
            </div>
            <div class="small" style="margin-top:6px; color:#cbd5e1;">
              NOAA and other CUBE profiles map to Qimera Command's <code>--cube-config</code> option.
            </div>
            ` : `
            <div class="small" style="margin-top:6px; color:#fbbf24;">
              Standard dynamic grid mode is shown for workflow planning, but the non-CUBE CLI creation path is not yet verified in <code>qimera-command</code>.
            </div>
            `}
            <details class="toolset-group" data-section-key="gridAutomation" ${isSectionOpen("gridAutomation") ? "open" : ""} style="margin-top:10px;">
              <summary>Automation</summary>
              <div class="toolset-content">
                <div class="row lm-add-row" style="margin-top:4px; align-items:center; gap:14px; flex-wrap:wrap;">
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-grid-auto-append" type="checkbox" ${activeProject?.autoAppendProcessedToGrid ? "checked" : ""} />
                    Auto-append when processed source lines change
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-grid-auto-export-geotiff" type="checkbox" ${activeProject?.autoExportGeotiffOnGridAppend !== false ? "checked" : ""} />
                    Auto-export live GeoTIFF after append
                  </label>
                  <button id="qimera-grid-run-automation" class="btn"${gridAutomation.running || !activeProject ? " disabled" : ""}>${gridAutomation.running ? "Running..." : "Run Append Now"}</button>
                </div>
                <div class="small" style="margin-top:6px; color:#cbd5e1;">
                  Grid append is project-wide in Qimera Command, so automation appends any project lines not already in the existing dynamic grids after the processed source set changes. Live GeoTIFF export overwrites the same TIFF path and refreshes the loaded layer.
                </div>
                <div class="small" style="margin-top:6px; min-height:16px;">
                  ${esc(gridAutomation.error || gridAutomation.message || (activeProject?.autoAppendProcessedToGrid ? `Watching processed source state for ${activeProject.name}.` : "Grid automation is off."))}
                  ${gridAutomation.lastRunAt ? ` Last run ${esc(formatTimestamp(gridAutomation.lastRunAt))}.` : ""}
                </div>
              </div>
            </details>
            <div class="row lm-add-row" style="margin-top:10px;">
              <button id="qimera-create-grid" class="btn primary"${gridBusy.creating || !activeProject ? " disabled" : ""}>${gridBusy.creating ? "Creating..." : "Create Grid"}</button>
              <button id="qimera-refresh-grids" class="btn"${gridBusy.loading || !activeProject ? " disabled" : ""}>${gridBusy.loading ? "Refreshing..." : "Refresh Grids"}</button>
              <button id="qimera-append-grids" class="btn"${gridBusy.appending || !activeProject || !gridItems.length ? " disabled" : ""}>${gridBusy.appending ? "Appending..." : "Append Missing Lines"}</button>
              <button id="qimera-remove-grid-selected" class="btn"${gridBusy.removing || !activeProject || !String(activeProject?.selectedGridName || "").trim() ? " disabled" : ""}>${gridBusy.removing ? "Removing..." : "Remove Selected From Grid"}</button>
            </div>
            <div style="margin-top:10px; overflow:auto;">
              <table style="width:100%; border-collapse:collapse; font-size:12px;">
                <thead>
                  <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                    <th style="padding:6px 8px;">Active</th>
                    <th style="padding:6px 8px;">Grid</th>
                    <th style="padding:6px 8px;">Scope</th>
                  </tr>
                </thead>
                <tbody>
                  ${gridItems.length ? gridItems.map((item) => `
                    <tr style="border-bottom:1px solid rgba(148,163,184,.1); ${String(activeProject?.selectedGridName || "").trim().toLowerCase() === String(item.name || "").trim().toLowerCase() ? "background:rgba(34,197,94,.06);" : ""}">
                      <td style="padding:6px 8px;"><input type="radio" name="qimera-active-grid" data-grid-select="${esc(item.name)}" ${String(activeProject?.selectedGridName || "").trim().toLowerCase() === String(item.name || "").trim().toLowerCase() ? "checked" : ""} /></td>
                      <td style="padding:6px 8px;">${esc(item.name)}</td>
                      <td style="padding:6px 8px;" class="small">Dynamic grid</td>
                    </tr>
                  `).join("") : `
                    <tr>
                      <td colspan="3" style="padding:10px 8px;" class="small">No grids found yet. Create a dynamic grid from the active project to get started.</td>
                    </tr>
                  `}
                </tbody>
              </table>
            </div>
            <div class="small" style="margin-top:8px; color:#cbd5e1;">
              <code>Append Missing Lines</code> uses Qimera Command's project-wide append behavior. <code>Remove Selected From Grid</code> uses the current line selection from Sources against the active grid.
            </div>
          </div>
        </details>

        <details class="toolset-group" data-section-key="export" ${isSectionOpen("export") ? "open" : ""} style="margin-top:8px;">
          <summary>Export</summary>
          <div class="toolset-content">
            <div class="row lm-add-row" style="margin-top:6px;">
              <label class="small" style="min-width:92px;">Export root</label>
              <input id="qimera-export-folder" type="text" value="${esc(state.exportFolder)}" style="flex:1;" />
              <button id="qimera-export-folder-pick" class="btn">Browse...</button>
            </div>
            <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:14px; flex-wrap:wrap;">
              <label class="small" style="display:flex; align-items:center; gap:6px;">
                <input id="qimera-auto-load-tiffs" type="checkbox" ${state.autoLoadExportedTiffs ? "checked" : ""} />
                Auto-load exported TIFFs into Hydrotools
              </label>
            </div>
            <div class="small" style="margin-top:6px; color:#cbd5e1;">
              Grid exports use the active grid: <code>${esc(activeGridName || "None selected")}</code>. Source exports use the current Sources selection: <code>${selectedSourceCount}</code> selected line${selectedSourceCount === 1 ? "" : "s"}.
            </div>

            <details class="toolset-group" data-section-key="exportGrid" ${isSectionOpen("exportGrid") ? "open" : ""} style="margin-top:10px;">
              <summary>Grid Exports</summary>
              <div class="toolset-content">
                <div class="row lm-add-row" style="margin-top:4px; align-items:center; gap:14px; flex-wrap:wrap;">
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-step-geotiff" type="checkbox" ${state.includeExportGeotiff ? "checked" : ""} />
                    GeoTIFF
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-step-float-geotiff" type="checkbox" ${state.includeExportFloatingPointGeotiff ? "checked" : ""} />
                    Floating GeoTIFF
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-step-grid-ascii" type="checkbox" ${state.includeExportGridAscii ? "checked" : ""} />
                    ASCII Grid
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-step-bag" type="checkbox" ${state.includeExportBag ? "checked" : ""} />
                    BAG
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-step-thu-tvu" type="checkbox" ${state.includeGenerateThuTvuLayers ? "checked" : ""} />
                    Generate THU/TVU
                  </label>
                </div>

                <div class="row lm-add-row" style="margin-top:10px;">
                  <label class="small" style="min-width:92px;">Depth layer</label>
                  <select id="qimera-depth-layer" style="min-width:160px;">
                    ${DEPTH_LAYER_OPTIONS.map((option) => `<option value="${esc(option)}"${String(state.geotiffDepthLayer || "Average") === option ? " selected" : ""}>${esc(option)}</option>`).join("")}
                  </select>
                </div>

                <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
                  <div class="small" style="font-weight:600;">GeoTIFF</div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Filename</label>
                    <input id="qimera-geotiff-filename" type="text" value="${esc(state.geotiffFilename)}" placeholder="grid.tif" style="flex:1;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:14px; flex-wrap:wrap;">
                    <label class="small" style="display:flex; align-items:center; gap:6px;">
                      <input id="qimera-geotiff-world-file" type="checkbox" ${state.geotiffWorldFile ? "checked" : ""} />
                      Export world file
                    </label>
                    <label class="small" style="min-width:92px;">Origin</label>
                    <select id="qimera-geotiff-origin" style="min-width:160px;">
                      <option value="lower-left"${state.geotiffOrigin === "lower-left" ? " selected" : ""}>lower-left</option>
                      <option value="upper-left"${state.geotiffOrigin === "upper-left" ? " selected" : ""}>upper-left</option>
                    </select>
                  </div>
                </div>

                <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
                  <div class="small" style="font-weight:600;">Floating-Point GeoTIFF</div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Filename</label>
                    <input id="qimera-float-geotiff-filename" type="text" value="${esc(state.floatingGeotiffFilename)}" placeholder="grid-float.tif" style="flex:1;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">No data</label>
                    <input id="qimera-float-geotiff-nodata" type="text" value="${esc(state.floatingGeotiffNoDataValue)}" placeholder="NaN or -9999" style="flex:1;" />
                  </div>
                </div>

                <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
                  <div class="small" style="font-weight:600;">ASCII Grid</div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Bounds</label>
                    <textarea id="qimera-ascii-bounds" placeholder="Optional SD object names, one per line" style="flex:1; min-height:62px; resize:vertical;">${esc(state.asciiBounds)}</textarea>
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Z precision</label>
                    <input id="qimera-ascii-z-precision" type="text" value="${esc(state.asciiZPrecision)}" placeholder="Optional decimal precision" style="width:180px;" />
                    <label class="small" style="min-width:92px; margin-left:8px;">Separator</label>
                    <input id="qimera-ascii-separator" type="text" value="${esc(state.asciiColumnSeparator)}" style="width:80px;" />
                  </div>
                </div>

                <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
                  <div class="small" style="font-weight:600;">BAG</div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Version</label>
                    <input id="qimera-bag-version" type="text" value="${esc(state.bagVersion)}" style="width:80px;" />
                    <label class="small" style="min-width:92px; margin-left:8px;">Output</label>
                    <select id="qimera-bag-output" style="min-width:240px;">
                      ${BAG_OUTPUT_OPTIONS.map((option) => `<option value="${esc(option)}"${String(state.bagOutputMode || "compute-tvu") === option ? " selected" : ""}>${esc(option)}</option>`).join("")}
                    </select>
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Scalar path</label>
                    <input id="qimera-bag-scalar-path" type="text" value="${esc(state.bagScalarPath)}" placeholder="Required for loaded-scalar output" style="flex:1;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Date</label>
                    <input id="qimera-bag-date" type="text" value="${esc(state.bagDate)}" placeholder="yyyy-MM-dd" style="width:180px;" />
                    <label class="small" style="display:flex; align-items:center; gap:6px; margin-left:8px;">
                      <input id="qimera-bag-xml-date" type="checkbox" ${state.bagUseXmlDate ? "checked" : ""} />
                      Use XML date
                    </label>
                    <label class="small" style="min-width:92px; margin-left:8px;">Compression</label>
                    <input id="qimera-bag-compression" type="text" value="${esc(state.bagCompression)}" style="width:80px;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">BAG XML</label>
                    <input id="qimera-bag-xml" type="text" value="${esc(state.bagXmlMetadata)}" placeholder="C:\\QPS-Data\\Projects\\Project\\Export\\Bag_Config.xml" style="flex:1;" />
                    <button id="qimera-bag-xml-pick" class="btn">Browse...</button>
                  </div>
                </div>
              </div>
            </details>

            <details class="toolset-group" data-section-key="exportSource" ${isSectionOpen("exportSource") ? "open" : ""} style="margin-top:10px;">
              <summary>Source Exports</summary>
              <div class="toolset-content">
                <div class="row lm-add-row" style="margin-top:4px; align-items:center; gap:14px; flex-wrap:wrap;">
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-step-gsf" type="checkbox" ${state.includeExportGsf ? "checked" : ""} />
                    GSF
                  </label>
                  <label class="small" style="display:flex; align-items:center; gap:6px;">
                    <input id="qimera-step-noaa" type="checkbox" ${state.includeExportNoaaCsv ? "checked" : ""} />
                    NOAA CSV
                  </label>
                </div>

                <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
                  <div class="small" style="font-weight:600;">GSF</div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Pattern</label>
                    <input id="qimera-gsf-pattern" type="text" value="${esc(state.gsfFilenamePattern)}" placeholder="%v\\%f" style="flex:1;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:14px; flex-wrap:wrap;">
                    <label class="small" style="display:flex; align-items:center; gap:6px;">
                      <input id="qimera-gsf-replace-whitespace" type="checkbox" ${state.gsfReplaceWhitespace ? "checked" : ""} />
                      Replace whitespace
                    </label>
                    <label class="small" style="display:flex; align-items:center; gap:6px;">
                      <input id="qimera-gsf-export-systems" type="checkbox" ${state.gsfExportSystemsIndividually ? "checked" : ""} />
                      Export systems individually
                    </label>
                    <label class="small" style="min-width:92px;">Specific system</label>
                    <input id="qimera-gsf-specific-system" type="text" value="${esc(state.gsfSpecificSystem)}" style="width:100px;" />
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:14px; flex-wrap:wrap;">
                    <label class="small" style="display:flex; align-items:center; gap:6px;">
                      <input id="qimera-gsf-exclude-user-additional" type="checkbox" ${state.gsfExcludeUserAdditionalSoundings ? "checked" : ""} />
                      Exclude user additional soundings
                    </label>
                    <label class="small" style="display:flex; align-items:center; gap:6px;">
                      <input id="qimera-gsf-exclude-backscatter" type="checkbox" ${state.gsfExcludeRawBackscatterImagery ? "checked" : ""} />
                      Exclude raw backscatter imagery
                    </label>
                  </div>
                  <div class="row lm-add-row" style="margin-top:8px; align-items:center; gap:14px; flex-wrap:wrap;">
                    <label class="small" style="display:flex; align-items:center; gap:6px;">
                      <input id="qimera-gsf-do-not-flag-filtered" type="checkbox" ${state.gsfDoNotFlagFilteredAsManualEdits ? "checked" : ""} />
                      Do not flag filtered as manual edits
                    </label>
                    <label class="small" style="display:flex; align-items:center; gap:6px;">
                      <input id="qimera-gsf-flag-interpolated" type="checkbox" ${state.gsfFlagAllInterpolatedAsAccepted ? "checked" : ""} />
                      Flag interpolated as accepted
                    </label>
                  </div>
                </div>

                <div style="margin-top:10px; padding:10px; border-radius:10px; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.18);">
                  <div class="small" style="font-weight:600;">NOAA CSV</div>
                  <div class="row lm-add-row" style="margin-top:8px;">
                    <label class="small" style="min-width:92px;">Filename</label>
                    <input id="qimera-noaa-filename" type="text" value="${esc(state.noaaCsvFilename)}" placeholder="noaa-export.csv" style="flex:1;" />
                  </div>
                </div>
              </div>
            </details>

            <div class="row lm-add-row" style="margin-top:10px;">
              <button id="qimera-run-exports" class="btn primary"${exportBusy.running || !activeProject ? " disabled" : ""}>${exportBusy.running ? "Running Exports..." : "Run Selected Exports"}</button>
            </div>
            <div class="small" style="margin-top:8px; min-height:16px; color:${exportExecution.error ? "#fca5a5" : "#cbd5e1"};">
              ${esc(exportExecution.error || exportExecution.message || "Checked exports are staged for host execution. Run them here or use Generated Output below.")}
              ${exportExecution.lastRunAt ? ` Last run ${esc(formatTimestamp(exportExecution.lastRunAt))}.` : ""}
            </div>
            ${exportExecution.results.length ? `
              <div style="margin-top:10px; overflow:auto;">
                <table style="width:100%; border-collapse:collapse; font-size:12px;">
                  <thead>
                    <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,.18);">
                      <th style="padding:6px 8px;">Export</th>
                      <th style="padding:6px 8px;">Result</th>
                      <th style="padding:6px 8px;">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${exportExecution.results.map((item) => `
                      <tr style="border-bottom:1px solid rgba(148,163,184,.1);">
                        <td style="padding:6px 8px;">${esc(item.label || item.key || "Export")}</td>
                        <td style="padding:6px 8px;">${statusBadge(item.ok ? "Done" : "Failed", item.ok ? "ok" : "fail")}</td>
                        <td style="padding:6px 8px;" class="small">${esc(item.error || item.stderr || item.stdout || "Completed without additional output.")}</td>
                      </tr>
                    `).join("")}
                  </tbody>
                </table>
              </div>
            ` : ""}
          </div>
        </details>

        <details class="toolset-group" data-section-key="output" ${isSectionOpen("output") ? "open" : ""} style="margin-top:8px;">
          <summary>Generated Output</summary>
          <div class="toolset-content">
            <div class="small" style="margin-top:4px;">Single-command sequence</div>
            <textarea id="qimera-single-commands" readonly style="width:100%; min-height:140px; resize:vertical;">${esc(singleCommands)}</textarea>
            <div class="small" style="margin-top:8px;">Config JSON</div>
            <textarea id="qimera-config-json" readonly style="width:100%; min-height:180px; resize:vertical;">${esc(configJson)}</textarea>
            <div class="small" style="margin-top:8px;">Launcher command</div>
            <textarea id="qimera-launcher-command" readonly style="width:100%; min-height:48px; resize:vertical;">${esc(launcherCommand)}</textarea>
            <div class="row lm-add-row" style="margin-top:8px;">
              <button id="qimera-refresh" class="btn">Refresh</button>
              <button id="qimera-copy-commands" class="btn primary">Copy Commands</button>
              <button id="qimera-copy-config" class="btn">Copy Config</button>
              <button id="qimera-save-ps1" class="btn">Save .ps1</button>
              <button id="qimera-save-config" class="btn">Save JSON</button>
            </div>
            <div class="small" style="margin-top:8px; min-height:16px;">
              ${esc((projectDialog?.submitting || projectDialog?.error) ? "Workflow validation is paused while creating a project." : (status || "Scaffolded for Qimera 2.8.2. This first version generates commands/configs; it does not run Qimera directly."))}
            </div>
          </div>
        </details>
      </div>
    `;
    if (sourceWorkspaceDock === "bottom") {
      app.ui.mountBottomPanel(sourcesWorkspaceHtml, {
        ownerId: SOURCES_BOTTOM_PANEL_ID,
        title: "Qimera Sources",
        onShow: () => {
          bindSourcesBottomPanel();
          stabilizeSourceWorkspaceScroll(sourceWorkspaceScroll);
        },
      });
      stabilizeSourceWorkspaceScroll(sourceWorkspaceScroll);
    } else {
      unbindSourcesBottomPanel();
      app.ui.unmountBottomPanel(SOURCES_BOTTOM_PANEL_ID);
    }
    if (sourceWorkspaceDock !== "bottom") {
      stabilizeSourceWorkspaceScroll(sourceWorkspaceScroll);
    }
    bindSectionToggleHandlers();
  }

  function refreshOutputs() {
    pullStateFromForm();
    if (!projectDialog) {
      status = validateForm(state) || "";
    }
    render();
  }

  async function copyText(text, okMessage) {
    const err = validateForm(state);
    if (err) {
      status = err;
      render();
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      status = okMessage;
    } catch {
      status = "Clipboard copy failed; generated output is shown above.";
    }
    render();
  }

  function savePs1() {
    const err = validateForm(state);
    if (err) {
      status = err;
      render();
      return;
    }
    const launcher = [
      "$ErrorActionPreference = 'Stop'",
      "",
      "$configPath = Join-Path $PSScriptRoot 'qimera-command-config.json'",
      "@'",
      buildConfigJson(state),
      "'@ | Set-Content -LiteralPath $configPath -Encoding UTF8",
      "",
      `${quoteArg(state.qimeraExecutable)} --config=$configPath`,
      "",
    ].join("\n");
    downloadText("run-qimera-command.ps1", launcher);
    status = "Saved PowerShell launcher.";
    render();
  }

  function saveConfig() {
    const err = validateForm(state);
    if (err) {
      status = err;
      render();
      return;
    }
    downloadText("qimera-command-config.json", buildConfigJson(state));
    status = "Saved config JSON.";
    render();
  }

  function onInput(e) {
    if (!containerEl) return;
    const target = e?.target;
    const id = target?.id || "";
    if (["qimera-monitor-source-folder", "qimera-auto-import-sources", "qimera-auto-process-sources", "qimera-auto-filter-sources", "qimera-source-scan-interval", "qimera-filter-profile", "qimera-grid-auto-append", "qimera-grid-exclude-coverage", "qimera-svp-strategy", "qimera-svp-max-time-hours", "qimera-svp-max-distance-nm"].includes(id)) {
      return;
    }
    const selectedProjectId = target?.dataset?.projectSelect;
    if (selectedProjectId) {
      selectProject(selectedProjectId);
      return;
    }
    const sourceCheckPath = target?.dataset?.sourceCheck;
    if (sourceCheckPath) {
      setSourceChecked(sourceCheckPath, !!target.checked);
      return;
    }
    const selectedGridName = target?.dataset?.gridSelect;
    if (selectedGridName) {
      selectGrid(selectedGridName);
      return;
    }
    if (id === "qimera-project-dialog-name") {
      updateProjectDialogField("name", target.value);
      const previewEl = containerEl.querySelector("#qimera-project-dialog-folder");
      if (previewEl) {
        previewEl.value = joinPathParts(environment.machine?.projectRoot || "", target.value || "");
      }
      return;
    }
    if (id === "qimera-project-dialog-crs-mode" || id === "qimera-project-dialog-epsg" || id === "qimera-project-dialog-wkt-file" || id === "qimera-project-dialog-survey-type" || id === "qimera-project-dialog-description") {
      if (id === "qimera-project-dialog-crs-mode") {
        updateProjectDialogField("crsMode", target.value);
        render();
        return;
      }
      if (id === "qimera-project-dialog-epsg") updateProjectDialogField("epsgCode", target.value);
      if (id === "qimera-project-dialog-wkt-file") updateProjectDialogField("wktFile", target.value);
      if (id === "qimera-project-dialog-survey-type") updateProjectDialogField("surveyType", target.value);
      if (id === "qimera-project-dialog-description") updateProjectDialogField("description", target.value);
      return;
    }
    if (id === "qimera-grid-name") {
      state.gridName = String(target.value || "");
      return;
    }
    if (id === "qimera-cell-size") {
      state.cellSize = String(target.value || "");
      return;
    }
    if (id === "qimera-grid-mode") {
      state.gridMode = String(target.value || "cube");
      render();
      return;
    }
    if (id === "qimera-grid-profile") {
      state.gridProfile = String(target.value || "Default");
      return;
    }
    if (id === "qimera-source-flow-mode") {
      state.sourceFlowMode = String(target.value || "batch");
      persist();
      return;
    }
    if (id === "qimera-source-workspace-dock") {
      setSourceWorkspaceDock(target.value);
      return;
    }
    if (id === "qimera-sbet-gps-week") {
      state.sbetGpsWeek = String(target.value || "");
      const activeProject = getActiveProject(state);
      if (activeProject) activeProject.sbetGpsWeek = state.sbetGpsWeek;
      return;
    }
    if (id === "qimera-sbet-coordinate-system") {
      state.sbetCoordinateSystem = String(target.value || "");
      const activeProject = getActiveProject(state);
      if (activeProject) activeProject.sbetCoordinateSystem = state.sbetCoordinateSystem;
      return;
    }
    if (id === "qimera-export-folder") {
      state.exportFolder = String(target.value || "");
      return;
    }
    if (id === "qimera-geotiff-filename") {
      state.geotiffFilename = String(target.value || "");
      return;
    }
    if (id === "qimera-float-geotiff-filename") {
      state.floatingGeotiffFilename = String(target.value || "");
      return;
    }
    if (id === "qimera-float-geotiff-nodata") {
      state.floatingGeotiffNoDataValue = String(target.value || "");
      return;
    }
    if (id === "qimera-ascii-bounds") {
      state.asciiBounds = String(target.value || "");
      return;
    }
    if (id === "qimera-ascii-z-precision") {
      state.asciiZPrecision = String(target.value || "");
      return;
    }
    if (id === "qimera-ascii-separator") {
      state.asciiColumnSeparator = String(target.value || "");
      return;
    }
    if (id === "qimera-bag-date") {
      state.bagDate = String(target.value || "");
      return;
    }
    if (id === "qimera-bag-compression") {
      state.bagCompression = String(target.value || "");
      return;
    }
    if (id === "qimera-bag-scalar-path") {
      state.bagScalarPath = String(target.value || "");
      return;
    }
    if (id === "qimera-bag-xml") {
      state.bagXmlMetadata = String(target.value || "");
      return;
    }
    if (id === "qimera-bag-version") {
      state.bagVersion = String(target.value || "");
      return;
    }
    if (id === "qimera-gsf-pattern") {
      state.gsfFilenamePattern = String(target.value || "");
      return;
    }
    if (id === "qimera-gsf-specific-system") {
      state.gsfSpecificSystem = String(target.value || "");
      return;
    }
    if (id === "qimera-noaa-filename") {
      state.noaaCsvFilename = String(target.value || "");
      return;
    }
    refreshOutputs();
  }

  function onChange(e) {
    if (String(e.target?.id || "").startsWith("qimera-project-dialog-")) {
      return;
    }
    if (e.target?.dataset?.sourceCheck) {
      return;
    }
    if (["qimera-monitor-source-folder", "qimera-auto-import-sources", "qimera-auto-process-sources", "qimera-auto-filter-sources", "qimera-source-scan-interval"].includes(String(e.target?.id || ""))) {
      updateAutomationSettingsFromForm();
      return;
    }
    if (["qimera-step-geotiff", "qimera-step-float-geotiff", "qimera-step-noaa"].includes(String(e.target?.id || ""))) {
      pullStateFromForm();
      if (e.target?.checked) {
        syncAutoExportNames(true);
        persist();
      }
      render();
      return;
    }
    if (["qimera-grid-auto-append", "qimera-grid-auto-export-geotiff"].includes(String(e.target?.id || ""))) {
      updateGridAutomationSettingsFromForm();
      return;
    }
    if (String(e.target?.id || "") === "qimera-source-workspace-dock") {
      setSourceWorkspaceDock(e.target?.value);
      return;
    }
    if (String(e.target?.id || "") === "qimera-filter-profile") {
      updateFilterSettingsFromForm();
      return;
    }
    if (["qimera-sbet-gps-week", "qimera-sbet-coordinate-system", "qimera-sbet-priority"].includes(String(e.target?.id || ""))) {
      pullStateFromForm();
      persistActiveProjectMetadata({
        sbetFile: String(state.sbetFile || "").trim(),
        sbetRmsFile: String(state.sbetRmsFile || "").trim(),
        sbetGpsWeek: String(state.sbetGpsWeek || "").trim(),
        sbetCoordinateSystem: String(state.sbetCoordinateSystem || "").trim(),
        sbetPriority: state.sbetPriority !== false,
      }).catch(() => {});
      return;
    }
    const selectedProjectId = e.target?.dataset?.projectSelect;
    if (selectedProjectId) {
      selectProject(selectedProjectId);
      return;
    }
    if (e.target?.id === "qimera-workflow-preset") {
      pullStateFromForm();
      persist();
      render();
      return;
    }
    refreshOutputs();
  }

  async function onClick(e) {
    if (e.target?.closest?.("#qimera-new-project")) {
      pullStateFromForm();
      openProjectDialog("create");
      return;
    }
    if (e.target?.closest?.("#qimera-open-project")) {
      pullStateFromForm();
      openProjectDialog("open");
      return;
    }
    if (e.target?.closest?.("#qimera-project-dialog-save")) {
      await commitProjectDialog();
      return;
    }
    if (e.target?.closest?.("#qimera-project-dialog-refresh")) {
      loadManagedProjects();
      return;
    }
    if (e.target?.closest?.("#qimera-project-dialog-pick-wkt-file")) {
      browseForProjectCrsFile();
      return;
    }
    const managedProjectFolder = e.target?.closest?.("[data-managed-project-open]")?.dataset?.managedProjectOpen;
    if (managedProjectFolder) {
      const managedProject = projectDialog?.availableProjects?.find((project) => String(project?.folder || "").trim().toLowerCase() === String(managedProjectFolder || "").trim().toLowerCase());
      addManagedProject(managedProject || {
        folder: managedProjectFolder,
        name: inferProjectNameFromFolder(managedProjectFolder),
      });
      return;
    }
    if (e.target?.closest?.("#qimera-project-dialog-cancel")) {
      closeProjectDialog();
      return;
    }
    if (e.target?.closest?.("#qimera-source-folder-pick")) {
      chooseSourceFolder();
      return;
    }
    if (e.target?.closest?.("#qimera-sbet-pick-file")) {
      chooseSbetFile();
      return;
    }
    if (e.target?.closest?.("#qimera-sbet-pick-rms-file")) {
      chooseSbetRmsFile();
      return;
    }
    if (e.target?.closest?.("#qimera-sbet-validate")) {
      inspectSbetImport({ persistMetadata: true });
      return;
    }
    if (e.target?.closest?.("#qimera-sbet-launch")) {
      launchQimeraForSbetImport();
      return;
    }
    const svpProfileName = e.target?.closest?.("[data-svp-profile-row]")?.dataset?.svpProfileRow;
    if (svpProfileName) {
      toggleSvpProfileSelection(svpProfileName, {
        shiftKey: e.shiftKey === true,
        ctrlKey: e.ctrlKey === true,
        metaKey: e.metaKey === true,
      });
      return;
    }
    if (e.target?.closest?.("#qimera-svp-select-affected")) {
      selectSourcesNeedingSvpReprocess();
      return;
    }
    if (e.target?.closest?.("#qimera-svp-apply-strategy")) {
      applySvpStrategyFromForm();
      return;
    }
    if (e.target?.closest?.("#qimera-refresh-svp")) {
      refreshProjectSvp();
      return;
    }
    if (e.target?.closest?.("#qimera-refresh-metrics")) {
      refreshProjectMetrics();
      return;
    }
    if (e.target?.closest?.("#qimera-export-folder-pick")) {
      chooseExportFolder();
      return;
    }
    if (e.target?.closest?.("#qimera-bag-xml-pick")) {
      chooseBagXmlFile();
      return;
    }
    if (e.target?.closest?.("#qimera-run-exports")) {
      runSelectedExports();
      return;
    }
    if (e.target?.closest?.("#qimera-source-folder-load")) {
      loadSourceFolderFiles();
      return;
    }
    if (e.target?.closest?.("#qimera-open-source-workspace")) {
      app.ui.showBottomPanel(SOURCES_BOTTOM_PANEL_ID);
      return;
    }
    if (e.target?.closest?.("#qimera-run-source-scan")) {
      runSourceAutomationScan({ manual: true });
      return;
    }
    if (e.target?.closest?.("#qimera-grid-run-automation")) {
      runGridAutomationScan({ manual: true });
      return;
    }
    if (e.target?.closest?.("#qimera-source-select-all")) {
      setAllSourceSelections(true);
      return;
    }
    if (e.target?.closest?.("#qimera-source-select-none")) {
      setAllSourceSelections(false);
      return;
    }
    const sourceRowPath = e.target?.closest?.("[data-source-row]")?.dataset?.sourceRow;
    if (sourceRowPath && !e.target?.closest?.("button") && !e.target?.closest?.("input")) {
      toggleSourceRowSelection(sourceRowPath, {
        shiftKey: e.shiftKey === true,
        ctrlKey: e.ctrlKey === true,
        metaKey: e.metaKey === true,
      });
      return;
    }
    if (e.target?.closest?.("#qimera-source-remove-selected")) {
      removeSelectedImportedSources();
      return;
    }
    if (e.target?.closest?.("#qimera-import-selected")) {
      importSelectedSources();
      return;
    }
    const sourceRemovePath = e.target?.closest?.("[data-source-remove]")?.dataset?.sourceRemove;
    if (sourceRemovePath) {
      removeImportedSource(sourceRemovePath);
      return;
    }
    const sourceZoomPath = e.target?.closest?.("[data-source-zoom]")?.dataset?.sourceZoom;
    if (sourceZoomPath) {
      zoomToSourceTrackline(sourceZoomPath);
      return;
    }
    if (e.target?.closest?.("#qimera-process-imported")) {
      processImportedSources();
      return;
    }
    if (e.target?.closest?.("#qimera-create-grid")) {
      createDynamicGrid();
      return;
    }
    if (e.target?.closest?.("#qimera-refresh-grids")) {
      refreshProjectGrids();
      return;
    }
    if (e.target?.closest?.("#qimera-append-grids")) {
      appendProcessedSourcesToGrids();
      return;
    }
    if (e.target?.closest?.("#qimera-remove-grid-selected")) {
      removeSelectedSourcesFromActiveGrid();
      return;
    }
    if (e.target?.closest?.("#qimera-apply-filter-selected")) {
      applyFilterToSelectedSources();
      return;
    }
    if (e.target?.closest?.("#qimera-refresh-sources")) {
      refreshSourceStatuses();
      return;
    }
    const removeProjectId = e.target?.closest?.("[data-project-remove]")?.dataset?.projectRemove;
    if (removeProjectId) {
      removeProject(removeProjectId);
      return;
    }
    const updateProjectPathId = e.target?.closest?.("[data-project-update-path]")?.dataset?.projectUpdatePath;
    if (updateProjectPathId) {
      updateProjectPath(updateProjectPathId);
      return;
    }
    if (e.target?.closest?.("#qimera-apply-preset")) {
      pullStateFromForm();
      state = applyPreset(state, state.workflowPreset);
      persist();
      status = `Applied ${WORKFLOW_PRESETS[state.workflowPreset]?.label || "workflow"} preset.`;
      render();
      return;
    }
    if (e.target?.closest?.("#qimera-refresh")) {
      refreshOutputs();
      return;
    }
    if (e.target?.closest?.("#qimera-check-environment")) {
      runEnvironmentCheck();
      refreshManagedProjectPresence();
      return;
    }
    if (e.target?.closest?.("#qimera-load-tracklines")) {
      loadProjectTracklines();
      return;
    }
    if (e.target?.closest?.("#qimera-clear-tracklines")) {
      clearProjectTracklines();
      return;
    }
    if (e.target?.closest?.("#qimera-copy-commands")) {
      copyText(buildSingleCommands(state).join("\n\n"), "Qimera commands copied to clipboard.");
      return;
    }
    if (e.target?.closest?.("#qimera-copy-config")) {
      copyText(buildConfigJson(state), "Qimera config JSON copied to clipboard.");
      return;
    }
    if (e.target?.closest?.("#qimera-save-ps1")) {
      savePs1();
      return;
    }
    if (e.target?.closest?.("#qimera-save-config")) {
      saveConfig();
    }
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onChange);
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("scroll", onSourceWorkspaceScroll, true);
    }
    containerEl = el;
    render();
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onChange);
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("scroll", onSourceWorkspaceScroll, true);
    if (environment.state === "idle") {
      runEnvironmentCheck();
    }
    refreshManagedProjectPresence();
    const activeProject = getActiveProject(state);
    if (activeProject?.sourceFolder && !(activeProject.sourceItems || []).length) {
      loadSourceFolderFiles();
    } else if (activeProject) {
      refreshSourceStatuses();
    }
    if (activeProject && (activeProject.sourceItems || []).some((item) => item?.checked)) {
      setTimeout(() => {
        loadProjectTracklines({ silent: true });
      }, 200);
    }
    ensureSourceAutomationWatcher();
    if (activeProject) {
      refreshProjectGrids();
    }
    ensureGridAutomationWatcher();
  }

  function destroy() {
    stopSourceAutomationWatcher();
    stopGridAutomationWatcher();
    stopSourceActivityAnimation();
    unbindSourcesBottomPanel();
    app.ui.unmountBottomPanel(SOURCES_BOTTOM_PANEL_ID);
    if (!containerEl) return;
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("change", onChange);
    containerEl.removeEventListener("click", onClick);
    containerEl = null;
  }

  function snapshot() {
    return { version: 1, state: { ...state } };
  }

  function restore(data) {
    const s = data?.state;
    if (!s || typeof s !== "object") return;
    state = { ...state, ...s };
    syncProjectSelection();
    persist();
    render();
    ensureGridAutomationWatcher();
  }

  return { mount, destroy, snapshot, restore };
}


